import { DocAcksBanner } from "@/components/DocAcksBanner";
import { useState, useEffect, useMemo, useCallback, Fragment } from "react";
import { DocCompanyCell } from "@/components/DocCompanyCell";
import { CompanyMissingTools, companyMissing } from "@/components/CompanyMissingTools";
import { createPortal } from "react-dom";

// Iter 246 — shared history search: multi-token AND match across every
// searchable field a document history entry may carry (invoice #, client
// name/code, tracking, company, file name, generator).
const docSearchMatch = (entry, q) => {
  const needle = (q || "").trim().toLowerCase();
  if (!needle) return true;
  const cs = Array.isArray(entry.customers_summary) ? entry.customers_summary : [];
  const hay = [
    entry.invoice_no, entry.matched_invoice_no, entry.filename,
    entry.source_file, entry.sender_company, entry.company_name,
    entry.generated_by_name, entry.user_name,
    Array.isArray(entry.tracking_numbers) ? entry.tracking_numbers.join(" ") : "",
    Array.isArray(entry.emi_generated_trackings) ? entry.emi_generated_trackings.join(" ") : "",
    cs.map(c => `${c.name || ""} ${c.tracking || ""} ${c.client_code || ""}`).join(" "),
  ].filter(Boolean).join(" ").toLowerCase();
  return needle.split(/\s+/).every(t => hay.includes(t));
};
import axios from "axios";
import { API, getAuthHeader, formatDateDDMMYYYY } from "@/App";
import CompletedInvoicesBar from "@/components/CompletedInvoicesBar";
import { MissingGenericsPanel } from "@/components/MissingGenericsPanel";
import { NameLinksReviewButton } from "@/components/NameLinksReview";
import { useCancelFlags, CancelFlagBadges, SUPERSEDED_ROW_CLS, CorrectedTag, useSupersededToggle, SupersededToggle } from "@/components/DocCancelFlags";
import { SelectLabelsButton, SelectedLabelsDialog } from "@/components/SelectedLabelsPrint";
import { CompanyInvoiceFields, invoiceFieldsOf } from "@/components/CompanyInvoiceFields";
import { useLatestOnly } from "@/components/DocLatestOnly";
import { useDirectCreated, isDirectEntry, originMatch, OriginFilter, DirectBadge, DIRECT_ROW_CLS } from "@/components/DirectCreate";
import UseInvoiceDialog from "@/components/UseInvoiceDialog";
import { BulkLabelReadyTable } from "@/components/BulkLabelReadyTable";
import { QtyMismatchDialog } from "@/components/QtyMismatchDialog";
import { ScanWeightsDialog } from "@/components/ScanWeightsDialog";
import { TrimmedProductsBadge } from "@/components/TrimmedProductsBadge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import BannedProductWarning, { useBannedCheck, parseBannedBlockError, BannedBlockBanner } from "@/components/BannedProductWarning";
import BulkDataMergePanel from "@/components/BulkDataMergePanel";
import PendingParcelsStrip from "@/components/PendingParcelsStrip";
import {
  FileSpreadsheet, FileText, Upload, Download, Trash2, RotateCcw,
  Search, Filter, X, RefreshCw, Eye, Edit, MoreVertical, CheckCircle2,
  AlertCircle, AlertTriangle, Clock, FolderOpen, Archive, ChevronDown, Pencil, Library, Tag, BookOpen, Square, Package, PackageMinus, Building2, Plus, Scissors, Printer, FileInput, Type, FileImage, Bell, Layers, XCircle, Wrench, Scale, ScanLine
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import PDFEditor from "./PDFEditor";
import CanvasPDFEditor from "./CanvasPDFEditor";
import DocumentPreview from "./DocumentPreview";
import { WeightChip, WeightSortToggle, useWeightMode, useWeightSorted, useWeightStatus } from "./WeightSort";
import { DocPrintBar, SelectBox, useDocSelection } from "./DocPrintBar";
import { CentralEditLog } from "./DocCellEdit";

/* ---------------- Banned-product wired preview components ---------------- */

const PackingListPreviewWithBannedCheck = ({ previewData }) => {
  const productNames = useMemo(() => {
    const list = [];
    (previewData.customers || []).forEach(c => (c.products || []).forEach(p => list.push(p.product_name)));
    return list;
  }, [previewData]);
  const { matches, isBanned } = useBannedCheck(productNames);
  return (
    <div className="mt-4">
      <BannedProductWarning matches={matches} />
      <div className="border rounded-xl overflow-hidden">
        <div className="bg-slate-50 px-4 py-3 border-b">
          <h4 className="font-semibold text-sm">Preview: {previewData.customers_found} Customers, {previewData.products_in_slip} Products in Slip</h4>
        </div>
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Customer</th>
                <th className="px-3 py-2 text-left">Products</th>
                <th className="px-3 py-2 text-left">Tracking</th>
                <th className="px-3 py-2 text-left">Country</th>
              </tr>
            </thead>
            <tbody>
              {previewData.customers?.map((c, idx) => {
                const rowHasBanned = (c.products || []).some(p => isBanned(p.product_name));
                return (
                  <tr key={idx} className={`border-b ${rowHasBanned ? "bg-red-50 hover:bg-red-100 text-red-800" : "hover:bg-slate-50"}`}
                      data-testid={rowHasBanned ? `packing-row-banned-${idx}` : `packing-row-${idx}`}>
                    <td className="px-3 py-2">{idx + 1}</td>
                    <td className="px-3 py-2 font-medium">{c.name}</td>
                    <td className="px-3 py-2">
                      {c.products?.map((p, pi) => (
                        <div key={pi} className={isBanned(p.product_name) ? "text-red-700 font-semibold" : "text-slate-600"}>
                          {isBanned(p.product_name) && <span title="Banned Drug" className="mr-1">🚫</span>}
                          {p.product_name} <span className="text-slate-400">x{p.qty}</span>
                        </div>
                      ))}
                    </td>
                    <td className="px-3 py-2 font-mono text-slate-500">{c.tracking}</td>
                    <td className="px-3 py-2">{c.country}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* Iter 229 — compact horizontal upload card shared by all document tools.
 * Replaces the tall centered dropzones so the panels keep more vertical
 * space for results. Same behaviour, same testids — just denser. */
const UPLOAD_ACCENTS = {
  indigo: "bg-indigo-100 text-indigo-600",
  green: "bg-green-100 text-green-600",
  orange: "bg-orange-100 text-orange-600",
  teal: "bg-teal-100 text-teal-600",
  violet: "bg-violet-100 text-violet-600",
  blue: "bg-blue-100 text-blue-600",
};
const CompactUploadBox = ({ file, label, hint, icon, required, accept,
                            inputTestId, removeTestId, onPick, onRemove,
                            accent = "indigo" }) => (
  <div className={`flex items-center gap-2.5 border-2 border-dashed rounded-lg px-3 py-2 transition-all ${
    file ? "border-green-300 bg-green-50"
         : "border-slate-200 hover:border-indigo-300 bg-slate-50"
  }`}>
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
      file ? "bg-green-100 text-green-600" : (UPLOAD_ACCENTS[accent] || UPLOAD_ACCENTS.indigo)
    }`}>{icon}</div>
    <div className="flex-1 min-w-0 text-left">
      <p className="text-xs font-semibold text-slate-800 leading-tight">
        {label} {required && <span className="text-red-500">*</span>}
      </p>
      <p className="text-[10px] text-slate-500 leading-tight truncate"
         title={file ? file.name : hint}>
        {file
          ? <span className="text-green-700 font-medium">{file.name}</span>
          : hint}
      </p>
    </div>
    {file ? (
      <Button variant="ghost" size="sm"
              className="h-6 px-1.5 text-[11px] shrink-0"
              onClick={onRemove} data-testid={removeTestId}>
        <X className="w-3 h-3 mr-0.5" /> Remove
      </Button>
    ) : (
      <label className="cursor-pointer shrink-0">
        <input type="file" accept={accept} className="hidden"
               data-testid={inputTestId}
               onChange={(e) => { if (e.target.files[0]) onPick(e.target.files[0]); }} />
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-slate-300 rounded-lg text-[11px] font-medium text-slate-700 hover:bg-slate-50">
          <Upload className="w-3 h-3" /> Upload
        </span>
      </label>
    )}
  </div>
);


/* Simple banner for Pack Team / Bulk Data results — highlights product name list */
const BannedBannerForNames = ({ productNames }) => {
  const { matches } = useBannedCheck(productNames || []);
  return <BannedProductWarning matches={matches} />;
};

const PackTeamPreviewWithBannedCheck = ({ ptPreviewData }) => {
  const productNames = useMemo(() => {
    const list = [];
    (ptPreviewData.customers || []).forEach(c => (c.products || []).forEach(p => list.push(p.name)));
    return list;
  }, [ptPreviewData]);
  const { matches, isBanned } = useBannedCheck(productNames);
  return (
    <div className="mt-4">
      <BannedProductWarning matches={matches} />
      <div className="border rounded-xl overflow-hidden" data-testid="pt-preview-section">
        <div className="bg-slate-50 px-4 py-3 border-b">
          <h4 className="font-semibold text-sm">
            Preview: {ptPreviewData.customers_count} Customers — {ptPreviewData.pages_count} A4 Page(s)
          </h4>
          <p className="text-xs text-slate-500 mt-0.5">5 slips per page, horizontal layout</p>
        </div>
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-xs" data-testid="pt-preview-table">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Code</th>
                <th className="px-3 py-2 text-left">Method</th>
                <th className="px-3 py-2 text-left">Customer</th>
                <th className="px-3 py-2 text-left">Address</th>
                <th className="px-3 py-2 text-left">Products</th>
                <th className="px-3 py-2 text-left">Batch</th>
                <th className="px-3 py-2 text-left">Tracking</th>
                <th className="px-3 py-2 text-left">Notes</th>
              </tr>
            </thead>
            <tbody>
              {ptPreviewData.customers?.map((c, idx) => {
                const rowHasBanned = (c.products || []).some(p => isBanned(p.name));
                return (
                  <tr key={idx}
                      className={`border-b ${rowHasBanned ? "bg-red-50 hover:bg-red-100" : "hover:bg-slate-50"}`}
                      data-testid={rowHasBanned ? `pt-preview-row-banned-${idx}` : `pt-preview-row-${idx}`}>
                    <td className="px-3 py-2 text-slate-400">{idx + 1}</td>
                    <td className="px-3 py-2 font-medium text-slate-700">{c.client_code}</td>
                    <td className="px-3 py-2">
                      <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-semibold rounded">
                        {c.method || 'ITPS'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{c.name}</div>
                      <div className="text-slate-400 text-[10px]">{c.address_lines?.slice(0, 2).join(', ')}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-600 max-w-[150px] text-[10px]">
                      {c.address_lines?.join(', ') || '-'}
                      {c.postal_status === 'CORRECTED' && (
                        <span className="ml-1 px-1 py-0.5 bg-amber-100 text-amber-700 text-[9px] font-semibold rounded" title={`ZIP corrected to ${c.postal_code}`}>CORRECTED</span>
                      )}
                      {c.postal_status === 'INVALID' && (
                        <span className="ml-1 px-1 py-0.5 bg-red-100 text-red-700 text-[9px] font-semibold rounded">INVALID ZIP</span>
                      )}
                      {c.postal_status === 'MISSING' && (
                        <span className="ml-1 px-1 py-0.5 bg-gray-100 text-gray-500 text-[9px] font-semibold rounded">NO ZIP</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {c.products?.length > 0 ? c.products.map((p, pi) => (
                        <div key={pi} className={isBanned(p.name) ? "text-red-700 font-semibold" : "text-slate-600"}>
                          {isBanned(p.name) && <span title="Banned Drug" className="mr-1">🚫</span>}
                          {p.name || 'N/A'} <span className="text-slate-400">x{p.qty || 0}</span>
                        </div>
                      )) : <span className="text-slate-400">N/A</span>}
                    </td>
                    <td className="px-3 py-2">
                      {c.products?.length > 0 ? c.products.map((p, pi) => (
                        <div key={pi} className="text-slate-500 font-mono text-[10px]">
                          {p.batch || '-'}
                        </div>
                      )) : <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-slate-500 text-[10px]">{c.tracking}</td>
                    <td className="px-3 py-2 text-slate-500 text-[10px] max-w-[120px]">{c.notes || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Lightweight in-memory cache for thumbnails (object URLs)
const __thumbCache = new Map();
const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'tif', 'tiff'];
const isImageSourced = (doc) => {
  if (!doc || !doc.file_name) return false;
  const ext = doc.file_name.split('.').pop().toLowerCase();
  return IMAGE_EXTS.includes(ext);
};

const DocThumb = ({ docId, alt }) => {
  const [src, setSrc] = useState(__thumbCache.get(docId) || null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (__thumbCache.has(docId)) {
      setSrc(__thumbCache.get(docId));
      return;
    }
    axios
      .get(`${API}/documents/${docId}/thumbnail`, {
        headers: { ...getAuthHeader() },
        responseType: 'blob'
      })
      .then((res) => {
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        __thumbCache.set(docId, url);
        setSrc(url);
      })
      .catch(() => !cancelled && setErr(true));
    return () => { cancelled = true; };
  }, [docId]);

  if (err || !src) {
    return (
      <div className="w-10 h-[52px] flex items-center justify-center bg-slate-100 rounded border border-slate-200 shrink-0">
        <FileImage className="w-4 h-4 text-slate-400" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt || 'thumbnail'}
      className="w-10 h-[52px] object-cover rounded border border-slate-200 shrink-0 bg-white"
      loading="lazy"
      data-testid={`doc-thumb-${docId}`}
    />
  );
};

const invKeyOf = (e) => String(e.matched_invoice_no || e.invoice_no || "").toUpperCase();
const wkey = (e) => invKeyOf(e);

const DocumentsPanel = ({ user, subPanel = "excel_transpose" }) => {
  // Access control check - admin always has access
  const hasSubPanelAccess = user?.role === 'admin' || (user?.document_sub_panels?.[subPanel] !== false);
  
  const [documents, setDocuments] = useState([]);
  const [recycleBin, setRecycleBin] = useState([]);
  const [libraryDocs, setLibraryDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("documents");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [pdfOperationDialogOpen, setPdfOperationDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pdfEditorOpen, setPdfEditorOpen] = useState(false);
  const [canvasEditorOpen, setCanvasEditorOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);

  // Reactive map of saved editor preferences per document so the badge updates instantly
  // when the user swaps editors. Initial value re-hydrates from localStorage on mount.
  const [editorPrefs, setEditorPrefs] = useState({});
  
  // Library
  const [librarySearch, setLibrarySearch] = useState("");
  const [saveToLibraryDialog, setSaveToLibraryDialog] = useState(false);
  const [libraryDocToSave, setLibraryDocToSave] = useState(null);
  const [libraryName, setLibraryName] = useState("");
  const [libraryTags, setLibraryTags] = useState("");

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    file_type: "all",
    conversion_type: "all",
    status: "all",
    uploaded_by: "all"
  });

  // Upload form
  const [uploadFile, setUploadFile] = useState(null);
  const [conversionType, setConversionType] = useState("");
  const [dragActive, setDragActive] = useState(false);

  // PDF Operation form
  const [pdfOperation, setPdfOperation] = useState("");
  const [pdfPages, setPdfPages] = useState("");
  const [pdfRotation, setPdfRotation] = useState("90");
  const [watermarkText, setWatermarkText] = useState("");
  const [mergeFiles, setMergeFiles] = useState([]);

  const [users, setUsers] = useState([]);

  // Format date/time with full details for library
  const formatDateTimeDetailed = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} at ${hours}:${minutes}`;
  };

  // Unified preview — opens in new tab
  const openPreviewTab = (moduleName, entryId) => {
    window.open(`/preview?module=${moduleName}&id=${entryId}`, '_blank');
  };

  // Download original source file
  const downloadSourceFile = async (moduleName, entryId, fileIndex, fileName) => {
    try {
      const res = await axios.get(
        `${API}/doc-preview/${moduleName}/${entryId}/source-file/${fileIndex}`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || `source_file_${fileIndex}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded: ${fileName}`);
    } catch { toast.error('Source file not available for this entry'); }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docsRes, binRes, usersRes, libraryRes] = await Promise.all([
        axios.get(`${API}/documents`, { headers: getAuthHeader() }),
        axios.get(`${API}/documents/recycle-bin`, { headers: getAuthHeader() }),
        axios.get(`${API}/users`, { headers: getAuthHeader() }),
        axios.get(`${API}/documents/library`, { headers: getAuthHeader() })
      ]);
      setDocuments(docsRes.data);
      // Re-hydrate editor preferences from localStorage for the loaded docs
      const prefs = {};
      try {
        for (const d of (docsRes.data || [])) {
          const p = window.localStorage.getItem(`taskflow-doc-editor-pref:${d.id}`);
          if (p) prefs[d.id] = p;
        }
      } catch (_) {}
      setEditorPrefs(prefs);
      setRecycleBin(binRes.data);
      setUsers(usersRes.data);
      setLibraryDocs(libraryRes.data);
    } catch (error) {
      toast.error("Failed to fetch documents");
    } finally {
      setLoading(false);
    }
  };

  // Filtered documents based on sub-panel and user filters
  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      // Sub-panel filtering
      if (subPanel === 'excel_transpose') {
        if (doc.file_type !== 'excel') return false;
      } else if (subPanel === 'pdf_edits') {
        if (doc.file_type !== 'pdf') return false;
      }
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!doc.file_name.toLowerCase().includes(query) &&
            !doc.uploaded_by_name.toLowerCase().includes(query)) {
          return false;
        }
      }
      if (filters.file_type !== "all" && doc.file_type !== filters.file_type) return false;
      if (filters.conversion_type !== "all" && doc.conversion_type !== filters.conversion_type) return false;
      if (filters.status !== "all" && doc.status !== filters.status) return false;
      if (filters.uploaded_by !== "all" && doc.uploaded_by !== filters.uploaded_by) return false;
      return true;
    });
  }, [documents, searchQuery, filters, subPanel]);

  const hasActiveFilters = searchQuery || 
    filters.file_type !== "all" || 
    filters.conversion_type !== "all" || 
    filters.status !== "all" ||
    filters.uploaded_by !== "all";

  const resetFilters = () => {
    setSearchQuery("");
    setFilters({
      file_type: "all",
      conversion_type: "all",
      status: "all",
      uploaded_by: "all"
    });
  };

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setUploadFile(e.dataTransfer.files[0]);
      autoDetectConversionType(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0]);
      autoDetectConversionType(e.target.files[0]);
    }
  };

  const autoDetectConversionType = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    const imageExts = ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'tif', 'tiff'];
    if (subPanel === 'excel_transpose' && (ext === 'xlsx' || ext === 'xls')) {
      setConversionType('transpose');
    } else if (subPanel === 'pdf_edits' && (ext === 'pdf' || imageExts.includes(ext))) {
      setConversionType('pdf_edit');
    } else if (ext === 'xlsx' || ext === 'xls') {
      setConversionType('transpose');
    } else if (ext === 'pdf') {
      setConversionType('pdf_to_excel');
    } else if (imageExts.includes(ext)) {
      setConversionType('pdf_edit');
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !conversionType) {
      toast.error("Please select a file and conversion type");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('conversion_type', conversionType);

    try {
      await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      toast.success("Document uploaded and processed!");
      setUploadDialogOpen(false);
      setUploadFile(null);
      setConversionType("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (docId, type) => {
    try {
      const response = await axios.get(
        `${API}/documents/${docId}/download/${type}`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Get filename from content-disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = type === 'original' ? 'document' : 'converted_file';
      
      if (contentDisposition) {
        // Try to match filename with or without quotes
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '');
        }
      }
      
      // Ensure filename has an extension based on content type
      const contentType = response.headers['content-type'];
      if (!filename.includes('.')) {
        if (contentType?.includes('spreadsheetml')) {
          filename += '.xlsx';
        } else if (contentType?.includes('pdf')) {
          filename += '.pdf';
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded: ${filename}`);
    } catch (error) {
      toast.error("Download failed");
    }
  };

  const handleSoftDelete = async (docId) => {
    if (!window.confirm("Move this document to recycle bin?")) return;
    try {
      await axios.delete(`${API}/documents/${docId}`, { headers: getAuthHeader() });
      toast.success("Document moved to recycle bin");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const handleRestore = async (docId) => {
    try {
      await axios.post(`${API}/documents/${docId}/restore`, {}, { headers: getAuthHeader() });
      toast.success("Document restored");
      fetchData();
    } catch (error) {
      toast.error("Failed to restore document");
    }
  };

  const handlePermanentDelete = async (docId) => {
    if (!window.confirm("Permanently delete this document? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/documents/${docId}/permanent`, { headers: getAuthHeader() });
      toast.success("Document permanently deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const handlePdfOperation = async () => {
    if (!selectedDocument || !pdfOperation) return;

    const formData = new FormData();
    formData.append('doc_id', selectedDocument.id);
    formData.append('operation', pdfOperation);

    if (pdfOperation === 'split' || pdfOperation === 'rotate') {
      formData.append('pages', pdfPages);
    }
    if (pdfOperation === 'rotate') {
      formData.append('rotation', pdfRotation);
    }
    if (pdfOperation === 'add_watermark') {
      formData.append('watermark_text', watermarkText);
    }
    if (pdfOperation === 'merge' && mergeFiles.length > 0) {
      mergeFiles.forEach(file => formData.append('merge_files', file));
    }

    try {
      await axios.post(`${API}/documents/pdf-operations`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`PDF ${pdfOperation} completed!`);
      setPdfOperationDialogOpen(false);
      resetPdfForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const resetPdfForm = () => {
    setPdfOperation("");
    setPdfPages("");
    setPdfRotation("90");
    setWatermarkText("");
    setMergeFiles([]);
    setSelectedDocument(null);
  };

  const openPdfOperations = (doc) => {
    setSelectedDocument(doc);
    setPdfOperationDialogOpen(true);
  };

  const PREF_KEY = (docId) => `taskflow-doc-editor-pref:${docId}`;
  const getEditorPref = (docId) => {
    try { return window.localStorage.getItem(PREF_KEY(docId)); } catch (_) { return null; }
  };
  const setEditorPref = (docId, mode) => {
    try { window.localStorage.setItem(PREF_KEY(docId), mode); } catch (_) {}
    setEditorPrefs(prev => ({ ...prev, [docId]: mode }));
  };
  const clearEditorPref = (docId) => {
    try { window.localStorage.removeItem(PREF_KEY(docId)); } catch (_) {}
    setEditorPrefs(prev => {
      const next = { ...prev };
      delete next[docId];
      return next;
    });
    toast.success('Editor preference cleared — auto-detect will be used next time');
  };

  const openSmartEditor = async (doc) => {
    // Route to the right editor:
    // 1. Saved per-doc preference wins (user manually swapped before)
    // 2. Otherwise auto-detect: scan pages → Canvas Editor, vector text → Text Editor
    setEditingDocument(doc);
    const pref = getEditorPref(doc.id);
    if (pref === 'text') {
      toast.info('Opening Text Editor (your saved preference for this document)', { duration: 2200 });
      setPdfEditorOpen(true);
      return;
    }
    if (pref === 'image') {
      toast.info('Opening Image Editor (your saved preference for this document)', { duration: 2200 });
      setCanvasEditorOpen(true);
      return;
    }
    try {
      const res = await axios.get(`${API}/documents/${doc.id}/page-types`, { headers: getAuthHeader() });
      const mode = res.data?.recommended_mode || 'image';
      const totalPages = res.data?.total_pages || 1;
      const pages = res.data?.pages || [];
      const summary = pages.length
        ? pages.slice(0, 5).map(p => `p${p.page}:${p.type}`).join(' · ') + (pages.length > 5 ? ` …+${pages.length - 5}` : '')
        : '';
      if (mode === 'image') {
        toast.info(
          `Detected scanned/image PDF (${totalPages} page${totalPages > 1 ? 's' : ''}) — opening Image Editor. Tip: press Q for Quick Edit, drag a box around any text to replace it.`,
          { duration: 6000 }
        );
        setCanvasEditorOpen(true);
      } else {
        toast.info(`Detected vector text PDF (${totalPages} page${totalPages > 1 ? 's' : ''}) — opening Text Editor.${summary ? ' ' + summary : ''}`, { duration: 5000 });
        setPdfEditorOpen(true);
      }
    } catch (e) {
      toast.warning('Could not detect PDF type — opening Image Editor by default.');
      setCanvasEditorOpen(true);
    }
  };

  const openPdfEditor = (doc) => {
    setEditingDocument(doc);
    setPdfEditorOpen(true);
  };

  const openCanvasEditor = (doc) => {
    setEditingDocument(doc);
    setCanvasEditorOpen(true);
  };

  const handlePdfEditorClose = () => {
    setPdfEditorOpen(false);
    setEditingDocument(null);
  };

  const handleCanvasEditorClose = () => {
    setCanvasEditorOpen(false);
    setEditingDocument(null);
  };

  const handlePdfEditorSave = () => {
    fetchData();
    setPdfEditorOpen(false);
    setEditingDocument(null);
  };

  const handleCanvasEditorSave = () => {
    fetchData();
    setCanvasEditorOpen(false);
    setEditingDocument(null);
  };

  const openPreview = (doc) => {
    setPreviewDocument(doc);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewDocument(null);
  };

  const handleRemoveFromLibrary = async (docId) => {
    if (!window.confirm("Remove this document from the library?")) return;
    try {
      await axios.delete(`${API}/documents/${docId}/library`, { headers: getAuthHeader() });
      toast.success("Document removed from library");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove from library");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ready':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Ready</Badge>;
      case 'processing':
        return <Badge className="bg-blue-100 text-blue-700"><Clock className="w-3 h-3 mr-1" />Processing</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    if (type === 'excel') {
      return <Badge className="bg-emerald-100 text-emerald-700"><FileSpreadsheet className="w-3 h-3 mr-1" />Excel</Badge>;
    }
    return <Badge className="bg-orange-100 text-orange-700"><FileText className="w-3 h-3 mr-1" />PDF</Badge>;
  };

  const getConversionLabel = (type) => {
    switch (type) {
      case 'transpose': return 'CARE EXIM';
      case 'pdf_to_excel': return 'PDF → Excel';
      case 'pdf_edit': return 'PDF Edit';
      default: return type;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Sub-panel specific config
  const subPanelConfig = {
    excel_transpose: {
      title: "Excel Transpose",
      description: "Upload and convert Excel files to CARE EXIM format",
      icon: <FileSpreadsheet className="w-5 h-5 text-green-600" />,
      uploadAccept: ".xlsx,.xls",
      uploadHint: "Supported: Excel (.xlsx, .xls)",
    },
    pdf_edits: {
      title: "PDF Edits",
      description: "Edit, annotate, and manage PDF documents",
      icon: <Pencil className="w-5 h-5 text-orange-600" />,
      uploadAccept: ".pdf,.jpg,.jpeg,.png,.webp,.bmp,.tif,.tiff,image/*",
      uploadHint: "Supported: PDF (.pdf) and Images (.jpg, .png, .webp, .bmp, .tiff) — images are auto-converted to a single-page PDF",
    },
    packing_list: {
      title: "Packing List Create",
      description: "Create and manage packing lists",
      icon: <Package className="w-5 h-5 text-blue-600" />,
    },
    bulk_data: {
      title: "Bulk Data Generation",
      description: "Generate Bulk Data Sheet for postal export",
      icon: <FolderOpen className="w-5 h-5 text-indigo-600" />,
    },
    manage_companies: {
      title: "Manage Companies",
      description: "Add, edit, and delete sender companies used in document generation",
      icon: <Building2 className="w-5 h-5 text-indigo-600" />,
    },
    pack_team_print: {
      title: "Pack Team Print",
      description: "Print multiple packing slips on A4 in 2x2 grid for easy cutting",
      icon: <Scissors className="w-5 h-5 text-teal-600" />,
    },
    pdf_to_word: {
      title: "PDF to Word",
      description: "Smart AI-powered PDF to Word conversion with layout preservation",
      icon: <FileText className="w-5 h-5 text-rose-600" />,
      uploadAccept: ".pdf",
      uploadHint: "Supported: PDF (.pdf)",
    },
    bulk_label: {
      title: "Bulk Label Generator",
      description: "Generate CN22 Customs Declaration labels with Code128 barcodes",
      icon: <Tag className="w-5 h-5 text-violet-600" />,
    }
  };

  const config = subPanelConfig[subPanel] || subPanelConfig.excel_transpose;

  // Packing List states
  const [orderSheetFile, setOrderSheetFile] = useState(null);
  const [packingSlipFile, setPackingSlipFile] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [packingHistory, setPackingHistory] = useState([]);
  const [packingHistQ, setPackingHistQ] = useState("");
  const [plNoCo, setPlNoCo] = useState(false);
  const [bdNoCo, setBdNoCo] = useState(false);
  const directCreated = useDirectCreated();
  const [histOrigin, setHistOrigin] = useState({});
  const originOf = (t) => histOrigin[t] || "all";
  const setOriginOf = (t) => (v) => setHistOrigin(o => ({ ...o, [t]: v }));
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editedProducts, setEditedProducts] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [orderSheetB64, setOrderSheetB64] = useState('');
  const [slipB64, setSlipB64] = useState('');
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [plCompanies, setPlCompanies] = useState([]);
  const [plSelectedCompany, setPlSelectedCompany] = useState("");
  const [plInvoiceOverride, setPlInvoiceOverride] = useState("");
  const [bulkInvoiceOverride, setBulkInvoiceOverride] = useState("");
  const [ptInvoiceOverride, setPtInvoiceOverride] = useState("");
  const [blInvoiceOverride, setBlInvoiceOverride] = useState("");
  const [packingBannedBlock, setPackingBannedBlock] = useState(null);

  // Invoice Replacement Popup — opened when /packing-list/generate
  // returns `requires_replacement:true` because the invoice number
  // already exists in Database A within the same fiscal year.
  const [replacementPopup, setReplacementPopup] = useState({
    open: false, entryId: null, invoiceNo: '',
    existingCount: 0, newCount: 0,
    financialYear: null,
    comparison: { rows: [], summary: {} },
    busy: false,
  });

  const closeReplacementPopup = useCallback(() => {
    setReplacementPopup(p => ({ ...p, open: false }));
  }, []);

  const handleReplaceConfirm = useCallback(async () => {
    const eid = replacementPopup.entryId;
    if (!eid) return;
    setReplacementPopup(p => ({ ...p, busy: true }));
    try {
      const r = await axios.post(
        `${API}/packing-list/history/${eid}/replace-invoice`,
        {}, { headers: getAuthHeader(), timeout: 45000 },
      );
      // Download the just-generated Packing List Excel so the operator
      // still gets the corrected file.
      try {
        const dl = await axios.get(
          `${API}/packing-list/history/${eid}/download`,
          { headers: getAuthHeader(), responseType: 'blob', timeout: 30000 },
        );
        const url = window.URL.createObjectURL(new Blob([dl.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Invoice-${r.data.invoice_no}-Replaced.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch { /* download best-effort */ }

      toast.success(
        `Invoice ${r.data.invoice_no}: removed ${r.data.removed_row_count} old row(s), inserted ${r.data.inserted_row_count} new row(s)`,
        { duration: 6000 },
      );
      setReplacementPopup({ open: false, entryId: null, invoiceNo: '',
        existingCount: 0, newCount: 0, financialYear: null,
        comparison: { rows: [], summary: {} }, busy: false });
      // Ping the packing-list history & Database A panels to refresh.
      // We avoid closing over `fetchPackingHistory` directly because it
      // is declared later in the file (TDZ) — a window event decouples
      // us cleanly.
      window.dispatchEvent(new Event('packing-list-history-refresh'));
      window.dispatchEvent(new Event('database-a-refresh'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Replace failed');
      setReplacementPopup(p => ({ ...p, busy: false }));
    }
  }, [replacementPopup.entryId]);

  const handleReplaceCancel = useCallback(async () => {
    const eid = replacementPopup.entryId;
    if (!eid) { closeReplacementPopup(); return; }
    setReplacementPopup(p => ({ ...p, busy: true }));
    try {
      await axios.delete(
        `${API}/packing-list/history/${eid}/decline-replacement`,
        { headers: getAuthHeader(), timeout: 20000 },
      );
      toast.info('Replacement cancelled — new packing list was discarded');
      setReplacementPopup({ open: false, entryId: null, invoiceNo: '',
        existingCount: 0, newCount: 0, financialYear: null,
        comparison: { rows: [], summary: {} }, busy: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed');
      setReplacementPopup(p => ({ ...p, busy: false }));
    }
  }, [replacementPopup.entryId, closeReplacementPopup]);

  const handleReplaceDownloadExcel = useCallback(async (mode) => {
    const eid = replacementPopup.entryId;
    if (!eid) return;
    try {
      const dl = await axios.get(
        `${API}/packing-list/history/${eid}/replacement-comparison-excel`,
        { params: { mode }, headers: getAuthHeader(), responseType: 'blob', timeout: 30000 },
      );
      const url = window.URL.createObjectURL(new Blob([dl.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice-${replacementPopup.invoiceNo}-${mode === 'changes' ? 'Changes' : 'Full'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    }
  }, [replacementPopup.entryId, replacementPopup.invoiceNo]);

  const fetchPackingHistory = useCallback(async () => {
    if (subPanel !== 'packing_list') return;
    setHistoryLoading(true);
    try {
      const response = await axios.get(`${API}/packing-list/history`, { headers: getAuthHeader() });
      setPackingHistory(response.data);
    } catch { /* silent */ } finally { setHistoryLoading(false); }
  }, [subPanel]);

  useEffect(() => { fetchPackingHistory(); }, [fetchPackingHistory]);

  // Listen for cross-panel refresh events (e.g. after Invoice Replacement
  // popup confirms). Decoupled via window event so the handler that
  // fires it doesn't have to close over `fetchPackingHistory` (which is
  // declared after the handler — TDZ).
  useEffect(() => {
    const onRefresh = () => fetchPackingHistory();
    window.addEventListener('packing-list-history-refresh', onRefresh);
    return () => window.removeEventListener('packing-list-history-refresh', onRefresh);
  }, [fetchPackingHistory]);

  // Fetch companies for packing list sender dropdown
  useEffect(() => {
    if (subPanel !== 'packing_list') return;
    const fetchPlCompanies = async () => {
      try {
        const res = await axios.get(`${API}/companies`, { headers: getAuthHeader() });
        const data = res.data || [];
        setPlCompanies(data);
        if (data.length === 1 && !plSelectedCompany) {
          setPlSelectedCompany(data[0].id || data[0].name);
        }
      } catch { /* silent */ }
    };
    fetchPlCompanies();
  }, [subPanel]);

  // Bulk Data states
  const [bulkOrderFile, setBulkOrderFile] = useState(null);
  const [bulkPackingSheetFile, setBulkPackingSheetFile] = useState(null);
  const [bulkPackingSlipFile, setBulkPackingSlipFile] = useState(null);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkHistory, setBulkHistory] = useState([]);
  const [plCancelFlags, reloadPlFlags] = useCancelFlags("packing_list", packingHistory.length);
  const [bdCancelFlags, reloadBdFlags] = useCancelFlags("bulk_data", bulkHistory.length);
  const [showSupPl, toggleSupPl] = useSupersededToggle("packing_list");
  const [showSupBd, toggleSupBd] = useSupersededToggle("bulk_data");
  const [showSupBl, toggleSupBl] = useSupersededToggle("bulk_label");
  const [bulkHistQ, setBulkHistQ] = useState("");
  const [bulkWarnings, setBulkWarnings] = useState([]);
  const [bulkMissingCodes, setBulkMissingCodes] = useState([]);
  const [missingGen, setMissingGen] = useState(null);   // {entryId, items}   // USA-only missing product-code report
  // Iter 247 — quantity discrepancies (packing list / bulk data)
  const [qtyDiscrepancies, setQtyDiscrepancies] = useState(null);
  const [qtyMismatch, setQtyMismatch] = useState(null);
  const [scanEntry, setScanEntry] = useState(null);
  const [missingQuickAddOpen, setMissingQuickAddOpen] = useState(false);
  const [missingQuickAddRows, setMissingQuickAddRows] = useState([]);   // [{brand, generic, product_code, hs_code}]
  const [missingQuickAddSaving, setMissingQuickAddSaving] = useState(false);

  const downloadMissingCsv = (missing) => {
    if (!missing || missing.length === 0) return;
    const _q = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = "Parcel #,Customer,Brand,Generic,Product Code,HS Code,HTSUS Code";
    const rows = missing.map(m => [
      m.parcel_serial, m.customer_name, m.brand, m.generic || '', '', '300490', ''
    ].map(_q).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url; a.download = `missing_usa_product_codes_${stamp}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success(`Downloaded ${missing.length} missing rows`);
  };

  const openMissingQuickAdd = (missing) => {
    // Dedupe by brand (case-insensitive) so each unique brand appears once
    const seen = new Set();
    const drafts = [];
    for (const m of missing || []) {
      const key = String(m.brand || '').trim().toUpperCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      drafts.push({
        brand: key,
        generic: String(m.generic || '').trim().toUpperCase(),
        product_code: "",
        hs_code: "300490",           // USA v2.1: 6-digit master default
        htsus_code: "",              // USA v2.1: optional 7–10 digits
      });
    }
    setMissingQuickAddRows(drafts);
    setMissingQuickAddOpen(true);
  };

  const _postCode = async (r, opts = {}) => {
    const payload = {
      hs_description: r.brand,
      brand_name: r.brand,
      generic_name: r.generic,
      product_code: (r.product_code || '').trim(),
      hs_code: (r.hs_code || '').trim(),
      htsus_code: (r.htsus_code || '').trim(),
    };
    if (opts.blankCode) payload.product_code = '';
    await axios.post(`${API}/usa-product-codes`, payload, { headers: getAuthHeader() });
  };

  const saveMissingQuickAdd = async () => {
    // "Save" — only rows with a Product Code entered; existing behaviour.
    const filled = missingQuickAddRows.filter(r => r.product_code && r.product_code.trim());
    if (filled.length === 0) { toast.error("Enter at least one Product Code, or use ‘Generate Without Saving’ to seed brands with blank codes"); return; }
    setMissingQuickAddSaving(true);
    let ok = 0; let fail = 0;
    for (const r of filled) {
      try { await _postCode(r); ok += 1; } catch { fail += 1; }
    }
    setMissingQuickAddSaving(false);
    if (ok > 0) toast.success(`Saved ${ok} product code${ok === 1 ? '' : 's'}${fail ? `, ${fail} failed` : ''}`);
    else toast.error(`All ${fail} saves failed`);
    // Remove saved rows from the report so the list shrinks
    const savedBrands = new Set(filled.map(r => r.brand));
    setBulkMissingCodes(prev => prev.filter(m => !savedBrands.has(String(m.brand).trim().toUpperCase())));
    if (fail === 0) setMissingQuickAddOpen(false);
    setMissingQuickAddRows(prev => prev.filter(r => !savedBrands.has(r.brand)));
  };

  // USA v2.1 "Generate Without Saving": seed EVERY listed brand to the USA
  // Product Code DB, keeping Product Code blank so the Codes Team can fill
  // it in later. Bulk Data is already generated — this just unblocks ops
  // for the next run.
  const generateMissingWithoutSaving = async () => {
    if (missingQuickAddRows.length === 0) return;
    setMissingQuickAddSaving(true);
    let ok = 0; let fail = 0;
    for (const r of missingQuickAddRows) {
      try { await _postCode(r, { blankCode: !(r.product_code || '').trim() }); ok += 1; }
      catch { fail += 1; }
    }
    setMissingQuickAddSaving(false);
    if (ok > 0) toast.success(`Seeded ${ok} brand${ok === 1 ? '' : 's'} to USA Product Codes${fail ? ` (${fail} failed)` : ''}. Codes Team can fill Product Code later.`);
    else toast.error(`All ${fail} seed attempts failed`);
    // Clear both the popup rows and the missing report — ops can move on.
    setBulkMissingCodes([]);
    setMissingQuickAddRows([]);
    setMissingQuickAddOpen(false);
  };
  const [bulkSelectedCompany, setBulkSelectedCompany] = useState("");
  const [bulkCompanies, setBulkCompanies] = useState([]);
  // Iter 228 — shared "Use invoice" dialog state: asks sender company +
  // packing-slip choice before any of the four tools generate.
  const [useDlg, setUseDlg] = useState(null);  // {inv, tool} | null
  const openUseDialog = (inv, tool) => setUseDlg({ inv, tool });
  const [bulkBannedBlock, setBulkBannedBlock] = useState(null);
  // Format pre-detection (Emirates vs standard ITPS) shown after order-sheet upload
  const [bulkFormat, setBulkFormat] = useState(null);       // {format,format_label,customer_count,methods_detected}
  const [bulkFormatLoading, setBulkFormatLoading] = useState(false);

  // Manage Companies state
  const [mcCompanies, setMcCompanies] = useState([]);
  const [mcLoading, setMcLoading] = useState(false);
  const [mcDialogOpen, setMcDialogOpen] = useState(false);
  const [mcEditingCompany, setMcEditingCompany] = useState(null);
  const [mcForm, setMcForm] = useState({ name: '', gstin: '', iec: '', address_line_1: '', address_line_2: '', address_line_3: '', pincode: '' });

  // Pack Team Print state
  const [ptOrderFile, setPtOrderFile] = useState(null);
  const [ptSlipFile, setPtSlipFile] = useState(null);
  const [ptGenerating, setPtGenerating] = useState(false);
  const [ptPreviewData, setPtPreviewData] = useState(null);
  // Pack Team duplicate-detection popup (Replace / Keep Both / Cancel)
  const [ptDupePopup, setPtDupePopup] = useState({
    open: false, token: null, invoiceNo: '',
    existingEntries: 0, existingParcels: 0, newParcels: 0,
    comparison: { rows: [], summary: {} },
    busy: false,
  });
  const [ptHistory, setPtHistory] = useState([]);
  const [ptHistQ, setPtHistQ] = useState("");
  const [ptCompany, setPtCompany] = useState("");
  const [ptHistoryLoading, setPtHistoryLoading] = useState(false);

  // PDF to Word states
  const [p2wFile, setP2wFile] = useState(null);
  const [p2wMode, setP2wMode] = useState("fast");
  const [p2wConverting, setP2wConverting] = useState(false);
  const [p2wStage, setP2wStage] = useState("");
  const [p2wProgress, setP2wProgress] = useState(0);
  const [p2wResult, setP2wResult] = useState(null);
  const [p2wClassification, setP2wClassification] = useState(null);
  const [p2wHistory, setP2wHistory] = useState([]);
  const [p2wDragActive, setP2wDragActive] = useState(false);

  // Bulk Label states
  const [blFile, setBlFile] = useState(null);
  const [blPackingList, setBlPackingList] = useState(null);   // USA-only: product names source
  const [blUsdInrRate, setBlUsdInrRate] = useState(95);       // USD→INR (settings-backed)
  const [blRateEditing, setBlRateEditing] = useState(false);  // admin-only rate editor
  const [blRateDraft, setBlRateDraft] = useState("");
  const [blRateReason, setBlRateReason] = useState("");
  const [blRateSaving, setBlRateSaving] = useState(false);
  const [blRateHistoryOpen, setBlRateHistoryOpen] = useState(false);
  const [blRateHistory, setBlRateHistory] = useState([]);
  const [blRateHistoryLoading, setBlRateHistoryLoading] = useState(false);
  const [blCompanies, setBlCompanies] = useState([]);
  const [blSelectedCompany, setBlSelectedCompany] = useState("");
  const [blGenerating, setBlGenerating] = useState(false);
  const [blPreviewing, setBlPreviewing] = useState(false);
  const [blHistory, setBlHistory] = useState([]);
  const [blSelectId, setBlSelectId] = useState(null);
  const [blHistQ, setBlHistQ] = useState("");
  useEffect(() => {
    const clear = { packing_list: setPackingHistQ, bulk_data: setBulkHistQ, pack_team: setPtHistQ, bulk_label: setBlHistQ };
    const h = (e) => {
      const t = e.detail?.tool;
      clear[t]?.("");
      setHistOrigin(o => ({ ...o, [t]: "all" }));
    };
    window.addEventListener("history-jump", h);
    return () => window.removeEventListener("history-jump", h);
  }, []);

  // Iter 247 — Global Documents search: one box, all four histories.
  const [globalQ, setGlobalQ] = useState("");
  const [globalOpen, setGlobalOpen] = useState(false);
  const DOC_PANEL_KEYS = ["packing_list", "bulk_data", "pack_team_print", "bulk_label"];
  const globalResults = useMemo(() => {
    const q = globalQ.trim();
    if (!q) return [];
    const groups = [
      { key: "packing_list", label: "Packing List", kind: "packing_list", entries: packingHistory },
      { key: "bulk_data",    label: "Bulk Data",    kind: "bulk_data",    entries: bulkHistory },
      { key: "pack_team",    label: "Pack Team",    kind: "pack_team",    entries: ptHistory },
      { key: "bulk_label",   label: "Bulk Label",   kind: "bulk_label",   entries: blHistory },
    ];
    return groups
      .map(g => ({ ...g, hits: (g.entries || []).filter(e => docSearchMatch(e, q)).slice(0, 10) }))
      .filter(g => g.hits.length > 0);
  }, [globalQ, packingHistory, bulkHistory, ptHistory, blHistory]);

  // Weight sort — per-table mode + Database A weight status per invoice.
  const wtKeys = useMemo(() => (subPanel === "packing_list" ? packingHistory.map(invKeyOf)
    : subPanel === "bulk_data" ? bulkHistory.map(invKeyOf) : subPanel === "bulk_label" ? blHistory.map(invKeyOf) : []),
    [subPanel, packingHistory, bulkHistory, blHistory]);
  const wtSt = useWeightStatus(wtKeys);
  const [wtModePl, setWtModePl] = useWeightMode("packing_list");
  const [wtModeBd, setWtModeBd] = useWeightMode("bulk_data");
  const [wtModeBl, setWtModeBl] = useWeightMode("bulk_label");
  const plRows = useWeightSorted(packingHistory, wtSt, wtModePl, invKeyOf);
  const bdRows = useWeightSorted(bulkHistory, wtSt, wtModeBd, invKeyOf);
  const plLatest = useLatestOnly();
  const bdLatest = useLatestOnly();
  const ptLatest = useLatestOnly();
  const blLatest = useLatestOnly();
  const blRows = useWeightSorted(blHistory, wtSt, wtModeBl, invKeyOf);
  const isGreen = (e) => { const w = wtSt[invKeyOf(e)]; return !!(w && w.total && !w.missing); };
  const greenKeys = (rows) => [...new Set(rows.filter(isGreen).map(invKeyOf))];
  const plSel = useDocSelection(), bdSel = useDocSelection(), blSel = useDocSelection();

  // Iter 247 — floating global search bar (portal) shown only in the four
  // document generation panels. Clicking a result opens its preview tab
  // without needing to switch sub-panels.
  const GlobalDocSearch = () => (
    DOC_PANEL_KEYS.includes(subPanel) ? createPortal(
      <div className="fixed top-2 right-6 z-50 w-80" data-testid="doc-global-search">
        <div className="relative">
          <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-slate-400" />
          <Input value={globalQ}
                 onChange={(e) => { setGlobalQ(e.target.value); setGlobalOpen(true); }}
                 onFocus={() => globalQ && setGlobalOpen(true)}
                 placeholder="Search all documents — invoice, client, tracking…"
                 className="h-8 pl-7 pr-7 text-xs bg-white shadow-md border-slate-300"
                 data-testid="doc-global-search-input" />
          {globalQ && (
            <button onClick={() => { setGlobalQ(""); setGlobalOpen(false); }}
                    className="absolute right-1 top-1 p-1 text-slate-400 hover:text-slate-700"
                    data-testid="doc-global-search-clear">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {globalOpen && globalQ && (
          <div className="mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-96 overflow-y-auto"
               data-testid="doc-global-search-results">
            {globalResults.length === 0 ? (
              <div className="p-3 text-xs text-slate-400 text-center">No matches</div>
            ) : globalResults.map(g => (
              <div key={g.key} className="border-b border-slate-100 last:border-0">
                <div className="px-2 py-1 bg-slate-50 text-[10px] font-semibold text-slate-500 uppercase sticky top-0">
                  {g.label} · {g.hits.length}
                </div>
                {g.hits.map(h => (
                  <button key={h.id}
                          onClick={() => { openPreviewTab(g.kind, h.id); setGlobalOpen(false); }}
                          className="w-full text-left px-2 py-1.5 hover:bg-indigo-50 text-xs border-t border-slate-50"
                          data-testid={`doc-global-hit-${g.key}-${h.id}`}>
                    <div className="font-semibold text-slate-800 truncate">
                      {h.invoice_no || h.matched_invoice_no || h.filename || h.id}
                    </div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {h.filename}
                      {Array.isArray(h.customers_summary) && h.customers_summary.length > 0 && (
                        <> · {h.customers_summary.slice(0, 2).map(c => c.name).filter(Boolean).join(", ")}
                          {h.customers_summary.length > 2 ? ` +${h.customers_summary.length - 2}` : ""}</>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>, document.body) : null
  );
  const [blHistoryLoading, setBlHistoryLoading] = useState(false);
  const [blMailingDate, setBlMailingDate] = useState("");
  const [blLayout, setBlLayout] = useState("horizontal");
  const [blLabelsPerPage, setBlLabelsPerPage] = useState("4");
  // Iter 170 — Opt-in USD→INR conversion for USA labels. Default OFF because
  // packing lists already export INR-denominated declared values.
  const [blConvertUsdToInr, setBlConvertUsdToInr] = useState(false);
  const [blPersonalNote, setBlPersonalNote] = useState(true);
  const [blNoteCount, setBlNoteCount] = useState(null);
  // Iter 171 — Inline rate spot-check calculator (only visible when
  // Conversion is ON). Lets operators type any USD amount and instantly
  // see the multiplier applied before generating a whole batch.
  const [blRatePreviewUsd, setBlRatePreviewUsd] = useState("100");
  const [blRegenId, setBlRegenId] = useState(null);
  const [blRegenLayout, setBlRegenLayout] = useState("vertical");
  const [blRegenLpp, setBlRegenLpp] = useState("4");
  const [blRegenLoading, setBlRegenLoading] = useState(false);

  // Iter 160 — Live preview auto-refresh
  const [blLivePreviewOn, setBlLivePreviewOn] = useState(false);
  const [blLivePreviewUrl, setBlLivePreviewUrl] = useState(null);
  const [blLivePreviewLoading, setBlLivePreviewLoading] = useState(false);

  // Iter 160 — Debounced auto-refresh of the inline PDF preview when
  // the operator toggles layout / labels-per-page / mailing date /
  // packing list. Only fires when Live Preview is ON and both the
  // label file + sender company are picked.
  useEffect(() => {
    if (!blLivePreviewOn) return;
    if (!blFile || !blSelectedCompany) return;
    const handle = setTimeout(async () => {
      setBlLivePreviewLoading(true);
      try {
        const formData = new FormData();
        formData.append('label_file', blFile);
        formData.append('sender_company_id', blSelectedCompany);
        if (blMailingDate) formData.append('mailing_date', blMailingDate);
        formData.append('layout', blLayout);
        formData.append('labels_per_page', blLabelsPerPage);
        if (blPackingList) formData.append('packing_list', blPackingList);
        formData.append('save_to_history', 'false');
        formData.append('convert_usd_to_inr', blConvertUsdToInr ? 'true' : 'false');
        formData.append('personal_note', blPersonalNote ? 'true' : 'false');
        const res = await axios.post(`${API}/bulk-label/generate`, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
          responseType: 'blob', timeout: 60000,
        });
        setBlNoteCount(parseInt(res.headers?.['x-personal-note-count'] || '0', 10));
        const zw = parseInt(res.headers?.['x-zero-weight-count'] || '0', 10);
        if (zw > 0) {
          toast.warning(`${zw} parcel(s) have ZERO weight — labels will show 0 g.`,
            { id: 'bl-zero-wt', duration: 8000 });
        }
        const url = window.URL.createObjectURL(
          new Blob([res.data], { type: 'application/pdf' }));
        setBlLivePreviewUrl(prev => {
          if (prev) window.URL.revokeObjectURL(prev);
          return url;
        });
      } catch (error) {
        let msg = "Live preview failed";
        try {
          const t = await error.response?.data?.text?.();
          if (t) { try { msg = JSON.parse(t).detail || msg; } catch { msg = t; } }
        } catch { /* ignore */ }
        toast.error(msg, { id: 'bl-live-preview-err' });
      } finally { setBlLivePreviewLoading(false); }
    }, 700);
    return () => clearTimeout(handle);
  }, [blLivePreviewOn, blFile, blSelectedCompany, blMailingDate,
      blLayout, blLabelsPerPage, blPackingList, blConvertUsdToInr, blPersonalNote]);

  // Free the blob URL on unmount / when live preview turns off.
  useEffect(() => {
    if (!blLivePreviewOn && blLivePreviewUrl) {
      window.URL.revokeObjectURL(blLivePreviewUrl);
      setBlLivePreviewUrl(null);
    }
  }, [blLivePreviewOn, blLivePreviewUrl]);

  // Fetch bulk data history
  const fetchBulkHistory = useCallback(async () => {
    if (subPanel !== 'bulk_data') return;
    try {
      const response = await axios.get(`${API}/bulk-data/history`, { headers: getAuthHeader() });
      setBulkHistory(response.data);
    } catch { /* silent */ }
  }, [subPanel]);

  // Detect which output format the currently-selected order sheet will use.
  // Called automatically whenever the user picks / changes the order sheet.
  useEffect(() => {
    if (!bulkOrderFile) { setBulkFormat(null); return; }
    let cancelled = false;
    (async () => {
      setBulkFormatLoading(true);
      try {
        const fd = new FormData();
        fd.append('order_sheet', bulkOrderFile);
        const r = await axios.post(`${API}/bulk-data/detect-format`, fd,
          { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }, timeout: 20000 });
        if (!cancelled) setBulkFormat(r.data);
      } catch (e) {
        if (!cancelled) setBulkFormat(null);
      } finally {
        if (!cancelled) setBulkFormatLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bulkOrderFile]);

  // Fetch companies for sender dropdown
  const fetchCompanies = useCallback(async () => {
    if (subPanel !== 'bulk_data') return;
    try {
      console.log('[Bulk Data] Fetching companies from API...');
      const response = await axios.get(`${API}/companies`, { headers: getAuthHeader() });
      const data = response.data || [];
      console.log(`[Bulk Data] Companies loaded: ${data.length}`, data.map(c => c.name));
      setBulkCompanies(data);
      // Auto-select if only one company exists
      if (data.length === 1 && !bulkSelectedCompany) {
        setBulkSelectedCompany(data[0].id || data[0].name);
        console.log(`[Bulk Data] Auto-selected: ${data[0].name}`);
      }
    } catch (err) {
      console.error('[Bulk Data] FAILED to fetch companies:', err?.response?.status, err?.message);
      setBulkCompanies([]);
    }
  }, [subPanel, bulkSelectedCompany]);

  useEffect(() => {
    fetchBulkHistory();
    fetchCompanies();
  }, [fetchBulkHistory, fetchCompanies]);

  // Fetch Pack Team Print history
  const fetchPtHistory = useCallback(async () => {
    if (subPanel !== 'pack_team_print') return;
    setPtHistoryLoading(true);
    try {
      const response = await axios.get(`${API}/pack-team/history`, { headers: getAuthHeader() });
      setPtHistory(response.data);
    } catch { /* silent */ } finally { setPtHistoryLoading(false); }
  }, [subPanel]);

  useEffect(() => { fetchPtHistory(); }, [fetchPtHistory]);

  // Fetch Bulk Label history
  const fetchBlHistory = useCallback(async () => {
    if (subPanel !== 'bulk_label') return;
    setBlHistoryLoading(true);
    try {
      const response = await axios.get(`${API}/bulk-label/history`, { headers: getAuthHeader() });
      setBlHistory(response.data);
    } catch { /* silent */ } finally { setBlHistoryLoading(false); }
  }, [subPanel]);

  // Fetch companies for bulk label
  const fetchBlCompanies = useCallback(async () => {
    if (subPanel !== 'bulk_label') return;
    try {
      const response = await axios.get(`${API}/companies`, { headers: getAuthHeader() });
      const data = response.data || [];
      setBlCompanies(data);
      if (data.length === 1 && !blSelectedCompany) {
        setBlSelectedCompany(data[0].id || data[0].name);
      }
    } catch { setBlCompanies([]); }
  }, [subPanel, blSelectedCompany]);

  useEffect(() => { fetchBlHistory(); fetchBlCompanies(); }, [fetchBlHistory, fetchBlCompanies]);

  // Load USD→INR rate when bulk_label panel opens (used for USA labels)
  useEffect(() => {
    if (subPanel !== 'bulk_label') return;
    axios.get(`${API}/settings/usd-inr-rate`, { headers: getAuthHeader() })
      .then(r => { if (r.data?.value) setBlUsdInrRate(r.data.value); })
      .catch(() => { /* silent — fallback stays at 95 */ });
  }, [subPanel]);

  // Access denied guard - must be after ALL hooks, before any conditional rendering
  if (!hasSubPanelAccess) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="access-denied-panel">
        <Card className="max-w-md w-full border-red-200 bg-red-50/50">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">Access Denied</h3>
            <p className="text-sm text-red-600">
              You do not have permission to view this module. Please contact your administrator to request access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============ MANAGE COMPANIES SUB-PANEL ============
  if (subPanel === 'manage_companies') {
    const fetchMcCompanies = async () => {
      setMcLoading(true);
      try {
        const res = await axios.get(`${API}/companies`, { headers: getAuthHeader() });
        setMcCompanies(res.data || []);
      } catch { setMcCompanies([]); }
      setMcLoading(false);
    };

    const openCreateDialog = () => {
      setMcEditingCompany(null);
      setMcForm({
        name: '', gstin: '', iec: '', address_line_1: '', address_line_2: '', address_line_3: '', pincode: '',
        city: '', state: '',
        name_hi: '', address_line_1_hi: '', address_line_2_hi: '', address_line_3_hi: '',
        city_hi: '', state_hi: '', pincode_hi: ''
      });
      setMcDialogOpen(true);
    };

    const openEditDialog = (c) => {
      setMcEditingCompany(c);
      setMcForm({
        name: c.name || '', gstin: c.gstin || '', iec: c.iec || '',
        address_line_1: c.address_line_1 || '', address_line_2: c.address_line_2 || '',
        address_line_3: c.address_line_3 || '', pincode: c.pincode || '',
        city: c.city || '', state: c.state || '',
        name_hi: c.name_hi || '',
        address_line_1_hi: c.address_line_1_hi || '',
        address_line_2_hi: c.address_line_2_hi || '',
        address_line_3_hi: c.address_line_3_hi || '',
        city_hi: c.city_hi || '', state_hi: c.state_hi || '', pincode_hi: c.pincode_hi || '',
        ...invoiceFieldsOf(c)
      });
      setMcDialogOpen(true);
    };

    const handleSaveCompany = async () => {
      if (!mcForm.name.trim()) { toast.error("Company name is required"); return; }
      if (!mcForm.gstin.trim()) { toast.error("GSTIN is required"); return; }
      try {
        if (mcEditingCompany) {
          await axios.put(`${API}/companies/${mcEditingCompany.id}`, mcForm, { headers: getAuthHeader() });
          toast.success("Company updated");
        } else {
          await axios.post(`${API}/companies`, mcForm, { headers: getAuthHeader() });
          toast.success("Company created");
        }
        setMcDialogOpen(false);
        fetchMcCompanies();
      } catch (e) { toast.error(e.response?.data?.detail || "Failed to save company"); }
    };

    const handleDeleteCompany = async (id, name) => {
      if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
      try {
        await axios.delete(`${API}/companies/${id}`, { headers: getAuthHeader() });
        toast.success("Company deleted");
        fetchMcCompanies();
      } catch { toast.error("Failed to delete company"); }
    };

    // Auto-fetch on mount
    if (mcCompanies.length === 0 && !mcLoading) fetchMcCompanies();

    return (
      <div className="space-y-6" data-testid="manage-companies-panel">
        <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Manage Companies</CardTitle>
                  <CardDescription>{mcCompanies.length} sender companies</CardDescription>
                </div>
              </div>
              <Dialog open={mcDialogOpen} onOpenChange={setMcDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openCreateDialog} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-company-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Company
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{mcEditingCompany ? 'Edit Company' : 'Add New Company'}</DialogTitle>
                    <DialogDescription>
                      {mcEditingCompany ? 'Update sender company details' : 'Add a new sender company for document generation'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3 mt-2">
                    <div>
                      <Label className="text-xs font-medium text-slate-600">Company Name *</Label>
                      <Input value={mcForm.name} onChange={e => setMcForm({...mcForm, name: e.target.value})} placeholder="e.g. INFINITY INC." data-testid="mc-name-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-medium text-slate-600">GSTIN *</Label>
                        <Input value={mcForm.gstin} onChange={e => setMcForm({...mcForm, gstin: e.target.value})} placeholder="e.g. 27ALNPT1672K1ZF" data-testid="mc-gstin-input" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-slate-600">IEC Code</Label>
                        <Input value={mcForm.iec} onChange={e => setMcForm({...mcForm, iec: e.target.value})} placeholder="e.g. AABCI1234A" data-testid="mc-iec-input" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-600">Address Line 1</Label>
                      <Input value={mcForm.address_line_1} onChange={e => setMcForm({...mcForm, address_line_1: e.target.value})} placeholder="Building, street" data-testid="mc-addr1-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-600">Address Line 2</Label>
                      <Input value={mcForm.address_line_2} onChange={e => setMcForm({...mcForm, address_line_2: e.target.value})} placeholder="Area, landmark" data-testid="mc-addr2-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-600">Address Line 3</Label>
                      <Input value={mcForm.address_line_3} onChange={e => setMcForm({...mcForm, address_line_3: e.target.value})} placeholder="Optional" data-testid="mc-addr3-input" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs font-medium text-slate-600">City</Label>
                        <Input value={mcForm.city} onChange={e => setMcForm({...mcForm, city: e.target.value})} placeholder="e.g. Mumbai" data-testid="mc-city-input" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-slate-600">State</Label>
                        <Input value={mcForm.state} onChange={e => setMcForm({...mcForm, state: e.target.value})} placeholder="e.g. Maharashtra" data-testid="mc-state-input" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-slate-600">Pincode</Label>
                        <Input value={mcForm.pincode} onChange={e => setMcForm({...mcForm, pincode: e.target.value})} placeholder="e.g. 440002" data-testid="mc-pincode-input" />
                      </div>
                    </div>
                    <CompanyInvoiceFields form={mcForm} setForm={setMcForm} />

                    {/* Hindi (Devanagari) version — optional, used by CN22 Bulk Label "(Hindi)" variant */}
                    <div className="pt-3 mt-2 border-t border-dashed border-slate-300">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <Label className="text-sm font-semibold text-slate-700">Hindi Version (हिंदी) <span className="text-xs font-normal text-slate-500">— optional</span></Label>
                          <p className="text-xs text-slate-500 mt-0.5">If filled, a "(Hindi)" variant will appear in the Bulk Label sender dropdown and print this FROM block in Devanagari.</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs font-medium text-slate-600">Company Name (हिंदी)</Label>
                          <Input lang="hi" value={mcForm.name_hi} onChange={e => setMcForm({...mcForm, name_hi: e.target.value})} placeholder="उदा. इन्फिनिटी इंक." data-testid="mc-name-hi-input" />
                        </div>
                        <div>
                          <Label className="text-xs font-medium text-slate-600">Address Line 1 (हिंदी)</Label>
                          <Input lang="hi" value={mcForm.address_line_1_hi} onChange={e => setMcForm({...mcForm, address_line_1_hi: e.target.value})} placeholder="इमारत, गली" data-testid="mc-addr1-hi-input" />
                        </div>
                        <div>
                          <Label className="text-xs font-medium text-slate-600">Address Line 2 (हिंदी)</Label>
                          <Input lang="hi" value={mcForm.address_line_2_hi} onChange={e => setMcForm({...mcForm, address_line_2_hi: e.target.value})} placeholder="क्षेत्र, लैंडमार्क" data-testid="mc-addr2-hi-input" />
                        </div>
                        <div>
                          <Label className="text-xs font-medium text-slate-600">Address Line 3 (हिंदी)</Label>
                          <Input lang="hi" value={mcForm.address_line_3_hi} onChange={e => setMcForm({...mcForm, address_line_3_hi: e.target.value})} placeholder="वैकल्पिक" data-testid="mc-addr3-hi-input" />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs font-medium text-slate-600">City (हिंदी)</Label>
                            <Input lang="hi" value={mcForm.city_hi} onChange={e => setMcForm({...mcForm, city_hi: e.target.value})} placeholder="मुंबई" data-testid="mc-city-hi-input" />
                          </div>
                          <div>
                            <Label className="text-xs font-medium text-slate-600">State (हिंदी)</Label>
                            <Input lang="hi" value={mcForm.state_hi} onChange={e => setMcForm({...mcForm, state_hi: e.target.value})} placeholder="महाराष्ट्र" data-testid="mc-state-hi-input" />
                          </div>
                          <div>
                            <Label className="text-xs font-medium text-slate-600">Pincode (हिंदी)</Label>
                            <Input lang="hi" value={mcForm.pincode_hi} onChange={e => setMcForm({...mcForm, pincode_hi: e.target.value})} placeholder="४४०००२ (optional)" data-testid="mc-pincode-hi-input" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button onClick={handleSaveCompany} className="bg-indigo-600 hover:bg-indigo-700 flex-1" data-testid="mc-save-btn">
                        {mcEditingCompany ? 'Update Company' : 'Add Company'}
                      </Button>
                      <Button variant="outline" onClick={() => setMcDialogOpen(false)}>Cancel</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {mcLoading ? (
              <div className="text-center py-12 text-slate-500">Loading companies...</div>
            ) : mcCompanies.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No companies yet. Add your first sender company.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="companies-table">
                  <thead>
                    <tr className="border-b bg-slate-50/80">
                      <th className="px-3 py-2.5 text-left font-semibold text-slate-600">Company Name</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-slate-600">GSTIN</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-slate-600">IEC</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-slate-600">Address</th>
                      <th className="px-3 py-2.5 text-center font-semibold text-slate-600">Pincode</th>
                      <th className="px-3 py-2.5 text-center font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mcCompanies.map(c => (
                      <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`company-row-${c.id}`}>
                        <td className="px-3 py-3 font-semibold text-slate-900">{c.name}</td>
                        <td className="px-3 py-3 text-xs font-mono text-slate-600">{c.gstin}</td>
                        <td className="px-3 py-3 text-xs text-slate-600">{c.iec || '-'}</td>
                        <td className="px-3 py-3 text-xs text-slate-600 max-w-xs truncate">
                          {[c.address_line_1, c.address_line_2, c.address_line_3].filter(Boolean).join(', ')}
                        </td>
                        <td className="px-3 py-3 text-center text-xs">{c.pincode || '-'}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50" onClick={() => openEditDialog(c)} data-testid={`edit-company-${c.id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteCompany(c.id, c.name)} data-testid={`delete-company-${c.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Bulk Data sub-panel
  if (subPanel === 'bulk_data') {
    // Split & Generate: for mixed-method order sheets. Backend returns a ZIP
    // containing both the Emirates 18-col and the Standard 47-col workbooks.
    const handleBulkSplitGenerate = async () => {
      if (!bulkOrderFile) { toast.error("Order Sheet is required"); return; }
      if (!bulkSelectedCompany) {
        toast.warning("No sender company selected. Sender fields may be blank in the output.", { duration: 4000 });
      }
      setBulkGenerating(true);
      setBulkWarnings([]);
      try {
        const formData = new FormData();
        formData.append('order_sheet', bulkOrderFile);
        if (bulkPackingSheetFile) formData.append('packing_sheet', bulkPackingSheetFile);
        if (bulkPackingSlipFile) formData.append('packing_slip', bulkPackingSlipFile);
        if (bulkSelectedCompany) formData.append('sender_company_id', bulkSelectedCompany);

        const response = await axios.post(
          `${API}/bulk-data/generate-split`, formData,
          { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
            responseType: 'blob', timeout: 90000 },
        );

        // Extract filename from Content-Disposition (falls back to a default)
        const cd = response.headers['content-disposition'] || '';
        const m = /filename="?([^";]+)"?/.exec(cd);
        const filename = m ? m[1] : `Bulk Data Split.zip`;
        const parts = response.headers['x-bulkdata-parts'] || '?';
        const rows = response.headers['x-bulkdata-rows'] || '?';

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url; link.setAttribute('download', filename);
        document.body.appendChild(link); link.click(); link.remove();
        window.URL.revokeObjectURL(url);

        toast.success(`Split ZIP generated — ${parts} workbook${parts === '1' ? '' : 's'}, ${rows} rows total.`);
        fetchBulkHistory();
      } catch (error) {
        const block = parseBannedBlockError(error);
        if (block.isBlocked) {
          setBulkBannedBlock(block);
          toast.error(`Split BLOCKED: ${block.totalMatches} banned product match(es).`, { duration: 8000 });
          return;
        }
        toast.error(error?.response?.data?.detail || error?.message || "Failed to split-generate");
      } finally {
        setBulkGenerating(false);
      }
    };

    const handleBulkGenerate = async (invoiceOverride = null, overrides = {}) => {
      // Iter 194 — invoice override from the Completed Invoices bar
      if (invoiceOverride && !invoiceOverride.invoice_no) invoiceOverride = null;
      // Iter 228 — dialog-supplied company/slip override the panel state
      const effCompany = overrides.companyId !== undefined ? overrides.companyId : bulkSelectedCompany;
      const effSlip = overrides.slipFile !== undefined ? overrides.slipFile : bulkPackingSlipFile;
      if (overrides.companyId) setBulkSelectedCompany(overrides.companyId);
      if (!invoiceOverride && !bulkOrderFile) {
        toast.error("Order Sheet is required");
        return;
      }
      if (!effCompany) {
        toast.warning("No sender company selected. Sender fields may be blank in the output.", { duration: 4000 });
      }      console.log('[Bulk Data Generate] Starting...', {
        orderFile: bulkOrderFile?.name,
        packingSheet: bulkPackingSheetFile?.name || 'none',
        packingSlip: effSlip?.name || 'none',
        selectedCompany: effCompany || 'none',
        companiesLoaded: bulkCompanies.length,
      });
      setBulkGenerating(true);
      setBulkWarnings([]);
      try {
        const formData = new FormData();
        if (invoiceOverride) {
          formData.append('invoice_no', invoiceOverride.invoice_no);
        } else {
          formData.append('order_sheet', bulkOrderFile);
        }
        if (bulkPackingSheetFile) formData.append('packing_sheet', bulkPackingSheetFile);
        if (effSlip) formData.append('packing_slip', effSlip);
        if (effCompany) formData.append('sender_company_id', effCompany);
        if (!invoiceOverride && bulkInvoiceOverride.trim()) formData.append('invoice_override', bulkInvoiceOverride.trim());
        console.log('[Bulk Data Generate] FormData entries:', [...formData.entries()].map(([k,v]) => `${k}=${typeof v === 'object' ? v.name : v}`));

        const response = await axios.post(
          invoiceOverride
            ? `${API}/bulk-data/generate-from-invoice`
            : `${API}/bulk-data/generate`, formData,
          { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }, timeout: 60000 }
        );

        const { id: entryId, filename, warnings, debug } = response.data;
        console.log('[Bulk Data Generate] Response:', JSON.stringify(response.data, null, 2));
        if (warnings?.length) setBulkWarnings(warnings);

        // Log debug info for troubleshooting
        if (debug) {
          console.log('[Bulk Data Debug] GSTIN:', debug.gstin_detected, '| Source:', debug.gstin_source, '| Company:', debug.matched_company);
          console.log('[Bulk Data Debug] Addr1:', debug.sender_addr1, '| Addr2:', debug.sender_addr2, '| Pin:', debug.sender_pincode);
          if (debug.matched_company) {
            toast.info(`Matched: ${debug.matched_company} (GSTIN: ${debug.gstin_detected || 'dropdown'})`, { duration: 5000 });
          } else {
            toast.warning(`No company matched. GSTIN: ${debug.gstin_detected || 'none'}`, { duration: 5000 });
          }
        }

        const dlResponse = await axios.get(
          `${API}/bulk-data/history/${entryId}/download`,
          { headers: getAuthHeader(), responseType: 'blob', timeout: 30000 }
        );
        const url = window.URL.createObjectURL(new Blob([dlResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        toast.success(`Bulk Data Sheet generated! ${response.data.total_rows} rows.`);

        // USA-only: surface missing product codes so ops can add them
        if ((response.data.missing_generics || []).length) {
          setMissingGen({ entryId: response.data.id, items: response.data.missing_generics });
          toast.warning(`${response.data.missing_generics.length} product(s) have no generic name — HS DESCRIPTION shows MEDICINE`);
        }
        const missing = response.data.missing_product_codes || [];
        if (response.data.format === 'usa' && missing.length > 0) {
          setBulkMissingCodes(missing);
        }

        // Iter 247 — quantity discrepancy notification
        const discs = response.data.qty_discrepancies || [];
        if (discs.length > 0) {
          setQtyDiscrepancies({ kind: 'bulk-data', items: discs });
          if (response.data.id) {
            setQtyMismatch({ kind: 'bulk', entryId: response.data.id, items: discs });
          }
          const over = discs.filter(d => d.direction === 'over').length;
          const under = discs.length - over;
          toast.warning(
            `Quantity mismatch — ${discs.length} line(s): ${under} less, ${over} more. See the yellow banner.`,
            { duration: 8000 });
        }

        fetchBulkHistory();
      } catch (error) {
        const block = parseBannedBlockError(error);
        if (block.isBlocked) {
          setBulkBannedBlock(block);
          toast.error(`Bulk Data BLOCKED: ${block.totalMatches} banned product match(es) in source file.`, { duration: 8000 });
          return;
        }
        let msg = "Failed to generate bulk data";
        if (error.response?.data?.detail) {
          msg = typeof error.response.data.detail === "string"
            ? error.response.data.detail : msg;
        }
        toast.error(msg);
      } finally {
        setBulkGenerating(false);
      }
    };

    const handleBulkDownloadHistory = async (entry) => {
      try {
        const response = await axios.get(`${API}/bulk-data/history/${entry.id}/download`,
          { headers: getAuthHeader(), responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', entry.filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch { toast.error("Download failed"); }
    };

    const handleBulkDeleteHistory = async (entry) => {
      if (!window.confirm(`Delete "${entry.filename}"?`)) return;
      try {
        await axios.delete(`${API}/bulk-data/history/${entry.id}`, { headers: getAuthHeader() });
        toast.success("Deleted");
        fetchBulkHistory();
      } catch { toast.error("Delete failed"); }
    };

    const UploadBox = ({ file, setFile, label, hint, icon, required, accept, testId }) => (
      <CompactUploadBox
        file={file} label={label} hint={hint} icon={icon}
        required={required} accept={accept}
        inputTestId={testId}
        onPick={(f) => setFile(f)}
        onRemove={() => setFile(null)} />
    );

    return (
      <div className="space-y-6">
        <GlobalDocSearch />
        <PendingParcelsStrip tool="bulk_data" />
        <CompletedInvoicesBar
          history={bulkHistory}
          onUse={(inv) => openUseDialog(inv, "bulk_data")}
          busy={bulkGenerating}
          hint="Order Sheet auto-filled — Packing Sheet/Slip stay optional"
          tool="bulk_data"
          testId="bulk-data" />
        <UseInvoiceDialog
          open={useDlg?.tool === "bulk_data"}
          invoice={useDlg?.tool === "bulk_data" ? useDlg.inv : null}
          tool="bulk_data"
          companies={bulkCompanies}
          defaultCompany={(() => { try { return localStorage.getItem("useInvoice:lastCompany:bulk_data") || ""; } catch { return ""; } })()}
          busy={bulkGenerating}
          onClose={() => setUseDlg(null)}
          onConfirm={async ({ slipFile, companyId }) => {
            const inv = useDlg?.inv;
            setUseDlg(null);
            try { localStorage.setItem("useInvoice:lastCompany:bulk_data", companyId); } catch {}
            setBulkPackingSlipFile(slipFile);
            if (inv) await handleBulkGenerate(inv, { slipFile, companyId });
            window.dispatchEvent(new Event("order-register:invoice-changed"));
          }}
        />
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">{config.icon} {config.title}</CardTitle>
            <CardDescription>Upload Order Sheet + optional Packing Sheet/Slip to generate Bulk Data Sheet</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <UploadBox file={bulkOrderFile} setFile={setBulkOrderFile} label="Order Sheet" hint="Excel (.xlsx) — Required" required
                icon={<FileSpreadsheet className="w-5 h-5 text-green-600" />} accept=".xlsx,.xls" testId="bulk-order-input" />
              <UploadBox file={bulkPackingSheetFile} setFile={setBulkPackingSheetFile} label="Packing Sheet" hint="Excel (.xlsx) — Optional"
                icon={<FileSpreadsheet className="w-5 h-5 text-blue-600" />} accept=".xlsx,.xls" testId="bulk-packing-sheet-input" />
              <UploadBox file={bulkPackingSlipFile} setFile={setBulkPackingSlipFile} label="Packing Slip" hint="PDF (.pdf) — Optional"
                icon={<FileText className="w-5 h-5 text-orange-600" />} accept=".pdf" testId="bulk-packing-slip-input" />
            </div>

            {/* Detected-format pill — appears after order sheet is picked */}
            {bulkOrderFile && (
              <div className="flex items-center gap-2 flex-wrap" data-testid="bulk-format-detection">
                {bulkFormatLoading && (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs bg-slate-100 text-slate-600 border border-slate-200">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Detecting format…
                  </div>
                )}
                {!bulkFormatLoading && bulkFormat && (
                  <>
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border ${
                      bulkFormat.format === 'emirates'
                        ? 'bg-purple-50 border-purple-300 text-purple-800'
                        : bulkFormat.format === 'usa'
                        ? 'bg-blue-50 border-blue-300 text-blue-800'
                        : 'bg-indigo-50 border-indigo-300 text-indigo-800'
                    }`}
                      data-testid={`bulk-format-pill-${bulkFormat.format}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        bulkFormat.format === 'emirates' ? 'bg-purple-500'
                          : bulkFormat.format === 'usa' ? 'bg-blue-500'
                          : 'bg-indigo-500'
                      }`} />
                      <span className="font-semibold">
                        {bulkFormat.format === 'emirates' ? 'Emirates format'
                          : bulkFormat.format === 'usa' ? 'USA format'
                          : 'Standard ITPS format'}
                      </span>
                      <span className="text-slate-500">·</span>
                      <span>{bulkFormat.customer_count} customer{bulkFormat.customer_count === 1 ? '' : 's'}</span>
                      {bulkFormat.methods_detected?.length > 0 && (
                        <>
                          <span className="text-slate-500">·</span>
                          <span className="text-[10px] uppercase tracking-wide">
                            {bulkFormat.methods_detected.join(', ')}
                          </span>
                        </>
                      )}
                    </div>
                    {bulkFormat.format === 'emirates' && (
                      <span className="text-[11px] text-slate-500">
                        Output will use the 18-column USPS-style layout.
                      </span>
                    )}
                    {bulkFormat.format === 'usa' && (
                      <span className="text-[11px] text-slate-500">
                        Output will use the 46-column India Post AMS layout.
                      </span>
                    )}
                    {bulkFormat.mixed_batch && (
                      <div className="w-full mt-1 flex flex-col gap-2 px-3 py-2 rounded-lg text-xs bg-amber-50 border border-amber-300 text-amber-800"
                           data-testid="bulk-format-mixed-warning">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="font-semibold">Mixed methods detected</div>
                            <div className="text-[11px] mt-0.5">
                              {bulkFormat.emirates_count} EMIRATES + {bulkFormat.other_count} other-method
                              customer{bulkFormat.other_count === 1 ? '' : 's'} in this sheet.
                              You can either split &amp; generate both formats as a ZIP, or split the source
                              sheet manually before generating.
                            </div>
                          </div>
                          <Button size="sm"
                                  onClick={handleBulkSplitGenerate}
                                  disabled={bulkGenerating || !bulkOrderFile || !bulkSelectedCompany}
                                  className="h-7 px-2 text-[11px] bg-amber-600 hover:bg-amber-700 text-white"
                                  data-testid="split-generate-bulk-data">
                            {bulkGenerating
                              ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Splitting…</>
                              : <><Download className="w-3 h-3 mr-1" /> Split &amp; Generate Both</>}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Sender Company Selection */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="sender-company-section">
              <label className="text-sm font-semibold text-slate-700 mb-2 block">
                Select Sender Company <span className="text-red-500">*</span>
                <span className={`ml-2 text-xs font-normal ${bulkCompanies.length > 0 ? 'text-green-600' : 'text-red-500'}`}>
                  ({bulkCompanies.length} companies loaded)
                </span>
              </label>
              {bulkCompanies.length === 0 ? (
                <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>No companies loaded. Go to <strong>Manage Companies</strong> to add sender companies, then refresh this page.</span>
                </div>
              ) : (
                <>
                  <select
                    value={bulkSelectedCompany}
                    onChange={(e) => setBulkSelectedCompany(e.target.value)}
                    className="w-full md:w-80 h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    data-testid="sender-company-dropdown"
                  >
                    <option value="">-- Choose sender company --</option>
                    {bulkCompanies.map(c => (
                      <option key={c.id || c.name} value={c.id || c.name}>
                        {c.name} — GSTIN: {c.gstin || 'N/A'}
                      </option>
                    ))}
                  </select>
                  {bulkSelectedCompany && (
                    <div className="mt-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1 inline-block" data-testid="selected-company-preview">
                      {(() => {
                        const sc = bulkCompanies.find(c => (c.id || c.name) === bulkSelectedCompany);
                        return sc ? (
                          <span>Selected: <strong>{sc.name}</strong> | {sc.address_line_1} | Pin: {sc.pincode}</span>
                        ) : null;
                      })()}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-center" title="Type an invoice number to force it, in case the slip is wrong">
              <Input value={bulkInvoiceOverride} onChange={e => setBulkInvoiceOverride(e.target.value)}
                     placeholder="Invoice # override (optional)" className="h-9 w-60 text-sm" data-testid="bulk-invoice-override" />
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button onClick={handleBulkGenerate} disabled={!bulkOrderFile || bulkGenerating} className="bg-indigo-600 hover:bg-indigo-700" data-testid="generate-bulk-data">
                {bulkGenerating ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : <><Download className="w-4 h-4 mr-2" /> Generate Bulk Data Sheet</>}
              </Button>
              {(bulkOrderFile || bulkPackingSheetFile || bulkPackingSlipFile) && (
                <Button variant="ghost" onClick={() => { setBulkOrderFile(null); setBulkPackingSheetFile(null); setBulkPackingSlipFile(null); setBulkSelectedCompany(""); setBulkWarnings([]); setBulkBannedBlock(null); setBulkFormat(null); }} data-testid="reset-bulk-data">
                  <RotateCcw className="w-4 h-4 mr-2" /> Reset
                </Button>
              )}
            </div>

            <BannedBlockBanner block={bulkBannedBlock} onClose={() => setBulkBannedBlock(null)} />

            {bulkWarnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <h4 className="text-sm font-semibold text-amber-700 flex items-center gap-1 mb-1"><AlertCircle className="w-4 h-4" /> Warnings ({bulkWarnings.length})</h4>
                <ul className="text-xs text-amber-600 space-y-0.5 max-h-32 overflow-y-auto">
                  {bulkWarnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            <QtyMismatchDialog mismatch={qtyMismatch} onClose={() => setQtyMismatch(null)}
              onRepaired={() => { fetchBulkHistory(); fetchPackingHistory(); }} />
            {qtyDiscrepancies && qtyDiscrepancies.items?.length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3" data-testid="qty-discrepancy-banner">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-amber-800 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    Quantity Mismatch — {qtyDiscrepancies.items.length} line{qtyDiscrepancies.items.length === 1 ? '' : 's'}
                    <span className="text-[11px] text-amber-700 font-normal ml-2">
                      ({qtyDiscrepancies.kind === 'bulk-data' ? 'Bulk Data' : 'Packing List'})
                      · pink cells in the SubPieces sheet show the differing quantities
                    </span>
                  </h4>
                  <Button variant="ghost" size="sm"
                          onClick={() => setQtyDiscrepancies(null)}
                          data-testid="qty-discrepancy-dismiss">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="max-h-40 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-amber-100 text-amber-900 text-[10px] uppercase">
                      <tr>
                        <th className="p-1.5 text-left">Customer</th>
                        <th className="p-1.5 text-left">Product</th>
                        <th className="p-1.5 text-right">Order</th>
                        <th className="p-1.5 text-right">Output</th>
                        <th className="p-1.5 text-right">Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {qtyDiscrepancies.items.slice(0, 50).map((d, i) => (
                        <tr key={i} className="border-t border-amber-200" data-testid={`qty-discrepancy-row-${i}`}>
                          <td className="p-1.5">{d.customer}</td>
                          <td className="p-1.5 font-semibold">{d.product}</td>
                          <td className="p-1.5 text-right">{d.source_qty}</td>
                          <td className="p-1.5 text-right">{d.output_qty}</td>
                          <td className={`p-1.5 text-right font-bold ${d.direction === 'over' ? 'text-rose-700' : 'text-amber-700'}`}>
                            {d.delta > 0 ? '+' : ''}{d.delta} {d.direction === 'over' ? 'more' : 'less'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {qtyDiscrepancies.items.length > 50 && (
                    <p className="text-[10px] text-amber-700 text-center py-1">
                      …{qtyDiscrepancies.items.length - 50} more (see highlighted cells in the Excel file)
                    </p>
                  )}
                </div>
              </div>
            )}

            {missingGen && (
              <MissingGenericsPanel key={missingGen.entryId} items={missingGen.items} entryId={missingGen.entryId}
                                    onClose={() => setMissingGen(null)} onSaved={() => fetchBulkHistory()} />
            )}
            {bulkMissingCodes.length > 0 && (
              <div className="bg-rose-50 border border-rose-300 rounded-lg p-3" data-testid="bulk-missing-codes">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-rose-700 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> Missing USA Product Codes ({bulkMissingCodes.length})
                  </h4>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline" size="sm"
                      className="h-7 text-xs border-rose-300 text-rose-700 hover:bg-rose-100"
                      onClick={() => downloadMissingCsv(bulkMissingCodes)}
                      data-testid="bulk-missing-csv"
                    >
                      <Download className="w-3 h-3 mr-1" /> Download
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      className="h-7 text-xs border-rose-300 text-rose-700 hover:bg-rose-100"
                      onClick={() => openMissingQuickAdd(bulkMissingCodes)}
                      data-testid="bulk-missing-quick-add"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add all
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setBulkMissingCodes([])} data-testid="bulk-missing-codes-dismiss">
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-rose-600 mb-2">
                  These brands have no entry in the USA Product Code Database. Add them so the next USA bulk data run auto-fills the PRODUCT CODE column.
                </p>
                <div className="max-h-48 overflow-y-auto rounded border border-rose-200 bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-rose-100 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1">Parcel #</th>
                        <th className="text-left px-2 py-1">Customer</th>
                        <th className="text-left px-2 py-1">Brand</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkMissingCodes.map((m, i) => (
                        <tr key={i} className="border-t border-rose-100" data-testid={`bulk-missing-row-${i}`}>
                          <td className="px-2 py-1 font-mono">{m.parcel_serial}</td>
                          <td className="px-2 py-1">{m.customer_name}</td>
                          <td className="px-2 py-1 font-semibold">{m.brand}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick-Add dialog for missing USA product codes */}
        <Dialog open={missingQuickAddOpen} onOpenChange={setMissingQuickAddOpen}>
          <DialogContent className="max-w-3xl" data-testid="bulk-missing-quickadd-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add Missing USA Product Codes
              </DialogTitle>
              <DialogDescription>
                Enter the customs product code for each brand. Rows left blank are skipped. Saved rows disappear from the missing report.
              </DialogDescription>
            </DialogHeader>
            {missingQuickAddRows.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-500">All done — nothing left to add.</div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5 w-56">Brand (HS Description)</th>
                      <th className="text-left px-2 py-1.5 w-40">Generic</th>
                      <th className="text-left px-2 py-1.5">Product Code</th>
                      <th className="text-left px-2 py-1.5 w-24">HS Code (6)</th>
                      <th className="text-left px-2 py-1.5 w-32">HTSUS (7–10)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {missingQuickAddRows.map((r, i) => (
                      <tr key={i} className="border-t" data-testid={`missing-quickadd-row-${i}`}>
                        <td className="px-2 py-1 font-medium">{r.brand}</td>
                        <td className="px-2 py-1 text-slate-600">{r.generic || '—'}</td>
                        <td className="px-2 py-1">
                          <Input
                            value={r.product_code}
                            onChange={(e) => setMissingQuickAddRows(prev => prev.map((x, j) => j === i ? { ...x, product_code: e.target.value } : x))}
                            placeholder="USA-1234 (optional)"
                            className="h-7 text-xs"
                            data-testid={`missing-quickadd-code-${i}`}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            value={r.hs_code}
                            maxLength={6}
                            onChange={(e) => setMissingQuickAddRows(prev => prev.map((x, j) => j === i ? { ...x, hs_code: e.target.value.replace(/\D/g, '').slice(0, 6) } : x))}
                            className="h-7 text-xs font-mono"
                            data-testid={`missing-quickadd-hs-${i}`}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            value={r.htsus_code || ''}
                            maxLength={10}
                            placeholder="3004909900"
                            onChange={(e) => setMissingQuickAddRows(prev => prev.map((x, j) => j === i ? { ...x, htsus_code: e.target.value.replace(/\D/g, '').slice(0, 10) } : x))}
                            className="h-7 text-xs font-mono"
                            data-testid={`missing-quickadd-htsus-${i}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <DialogFooter className="flex-wrap gap-2">
              <Button variant="ghost" onClick={() => setMissingQuickAddOpen(false)} disabled={missingQuickAddSaving}>Cancel</Button>
              <Button
                variant="outline"
                onClick={() => downloadMissingCsv(bulkMissingCodes)}
                disabled={missingQuickAddSaving || bulkMissingCodes.length === 0}
                data-testid="missing-quickadd-download"
              >
                <Download className="w-3 h-3 mr-1" /> Download Missing Products
              </Button>
              <Button
                variant="secondary"
                onClick={generateMissingWithoutSaving}
                disabled={missingQuickAddSaving || missingQuickAddRows.length === 0}
                data-testid="missing-quickadd-generate-without-saving"
                title="Seed all brands to USA Product Codes with blank Product Code. Codes Team can fill in later."
              >
                {missingQuickAddSaving
                  ? <><RefreshCw className="w-3 h-3 animate-spin mr-1" /> Working…</>
                  : <>Generate Without Saving</>}
              </Button>
              <Button
                onClick={saveMissingQuickAdd}
                disabled={missingQuickAddSaving || missingQuickAddRows.length === 0}
                data-testid="missing-quickadd-save-all"
              >
                {missingQuickAddSaving
                  ? <><RefreshCw className="w-3 h-3 animate-spin mr-1" /> Saving…</>
                  : <><CheckCircle2 className="w-3 h-3 mr-1" /> Save</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg" data-testid="bulk-merge-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Layers className="w-4 h-4 text-fuchsia-500" />
              Merge Data
              <span className="text-[11px] font-normal text-slate-500 ml-1">— combine today's / selected invoices into one Excel, continuous SR</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BulkDataMergePanel user={user} />
          </CardContent>
        </Card>

        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base"><Clock className="w-4 h-4 text-slate-500" /> Generation History</CardTitle>
            <div className="relative w-60 mt-2">
              <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-slate-400" />
              <Input value={bulkHistQ} onChange={(e) => setBulkHistQ(e.target.value)}
                     placeholder="Search invoice, client, code, tracking…"
                     className="h-7 pl-7 text-xs" data-testid="bulk-history-search" />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2"><OriginFilter value={originOf("bulk_data")} onChange={setOriginOf("bulk_data")} testId="bulk-data" /><SupersededToggle on={showSupBd} toggle={toggleSupBd} count={bulkHistory.filter(h => h.superseded_by).length} testId="bulk-data" /><WeightSortToggle mode={wtModeBd} setMode={setWtModeBd} st={wtSt} testId="bulk-data" /><CompanyMissingTools rows={bulkHistory.filter(h => showSupBd || !h.superseded_by)} on={bdNoCo} toggle={() => setBdNoCo(v => !v)} type="bulk_data" testId="bulk-data" onSaved={() => { fetchBulkHistory(); fetchPackingHistory(); }} /><CentralEditLog module="bulk_data" label="Bulk Data" /></div>
          </CardHeader>
          <CardContent>
            {bulkHistory.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400">No bulk data sheets generated yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <div className="mb-2"><DocPrintBar keys={greenKeys(bdRows.filter(h => (showSupBd || !h.superseded_by) && docSearchMatch(h, bulkHistQ) && originMatch(directCreated, "bulk_data", h, originOf("bulk_data")) && (!bdNoCo || companyMissing(h))))} sel={bdSel.sel} setSel={bdSel.setSel} label="Select all weighed" testid="wt-print-bd" /></div>
                <table className="w-full text-sm" data-testid="bulk-history-table">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-2 py-2 w-8" title="Tick fully weighed invoices to print"></th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Filename</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Invoice</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Rows</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Sender</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Generated At</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bdLatest.apply(bdRows.filter(h => (showSupBd || !h.superseded_by) && docSearchMatch(h, bulkHistQ) && originMatch(directCreated, "bulk_data", h, originOf("bulk_data")) && (!bdNoCo || companyMissing(h)))).map((entry) => { const isDirect = isDirectEntry(directCreated, "bulk_data", entry); return (
                      <tr key={entry.id} className={`border-b ${isDirect ? DIRECT_ROW_CLS : "hover:bg-slate-50"}`} data-testid={`bulk-history-row-${entry.id}`}>
                        <td className="px-2 py-2.5 text-center w-8">{isGreen(entry) ? <SelectBox k={invKeyOf(entry)} sel={bdSel.sel} toggle={bdSel.toggle} testid={`wt-print-select-bd-${entry.id}`} /> : null}</td>
                        <td className="px-3 py-2.5 font-medium text-slate-800"><span className={entry.superseded_by ? SUPERSEDED_ROW_CLS : ""}>{entry.filename}</span><CorrectedTag entry={entry} testId="bd" />{bdLatest.chip(entry, "bd")}{isDirect && <DirectBadge testId={`direct-badge-bulk-${entry.id}`} />}
                          {entry.missing_generics?.length > 0 && (
                            <button type="button" onClick={() => { setMissingGen({ entryId: entry.id, items: entry.missing_generics }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                                    className="ml-1 text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-1.5 hover:bg-amber-200"
                                    title="Products with no generic name (HS DESCRIPTION = MEDICINE) — click to fix"
                                    data-testid={`missing-generic-badge-${entry.id}`}>{new Set(entry.missing_generics.map(m => m.brand)).size} no generic</button>)}
                          <CancelFlagBadges entry={entry} flags={bdCancelFlags} testId="bd" onRegenerated={() => { fetchBulkHistory(); fetchPackingHistory(); reloadBdFlags(); reloadPlFlags(); }} /></td>
                        <td className="px-3 py-2.5 font-mono text-xs">{entry.invoice_no}<WeightChip s={wtSt[wkey(entry)]} testId={`wt-chip-bd-${entry.id}`} /></td>
                        <td className="px-3 py-2.5 text-center"><span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs font-medium">{entry.total_rows}</span></td>
                        <DocCompanyCell entry={entry} type="bulk_data" className="px-3 py-2.5 text-slate-600" onSaved={() => { fetchBulkHistory(); fetchPackingHistory(); }} />
                        <td className="px-3 py-2.5 text-slate-500 text-xs">{new Date(entry.created_at).toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {(entry.qty_discrepancies || []).length > 0 && (
                              <Button variant="ghost" size="sm" data-testid={`qty-mismatch-${entry.id}`}
                                onClick={() => setQtyMismatch({ kind: 'bulk', entryId: entry.id, items: entry.qty_discrepancies })}
                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                title={`${entry.qty_discrepancies.length} quantity mismatch(es) — click to repair`}>
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => openPreviewTab('bulk_data', entry.id)} className="h-7 w-7 p-0 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50" title="Preview" data-testid={`bulk-preview-${entry.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleBulkDownloadHistory(entry)} className="h-7 w-7 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50" title="Download Output"><Download className="w-3.5 h-3.5" /></Button>
                            {entry.source_files && entry.source_files.length > 0 ? (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => downloadSourceFile('bulk_data', entry.id, 0, entry.source_files[0]?.name || 'OrderSheet.xlsx')} className="h-auto px-1.5 py-1 text-amber-600 hover:text-amber-800 hover:bg-amber-50 text-[10px] leading-tight" title={`Download: ${entry.source_files[0]?.name || 'Order Sheet'}`} data-testid={`bulk-source-order-${entry.id}`}>
                                  <div className="flex flex-col items-center gap-0.5"><FileInput className="w-3 h-3" /><span>Order</span></div>
                                </Button>
                                {entry.source_files.length > 1 && (
                                  <Button variant="ghost" size="sm" onClick={() => downloadSourceFile('bulk_data', entry.id, 1, entry.source_files[1]?.name || 'PackingSheet.xlsx')} className="h-auto px-1.5 py-1 text-orange-600 hover:text-orange-800 hover:bg-orange-50 text-[10px] leading-tight" title={`Download: ${entry.source_files[1]?.name || 'Packing Sheet'}`} data-testid={`bulk-source-packing-${entry.id}`}>
                                    <div className="flex flex-col items-center gap-0.5"><FileInput className="w-3 h-3" /><span>Packing</span></div>
                                  </Button>
                                )}
                              </>
                            ) : (
                              <span className="text-[10px] text-slate-400">N/A</span>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleBulkDeleteHistory(entry)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Packing List sub-panel
  if (subPanel === 'packing_list') {
    const fileToBase64 = (file) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const handlePreview = async () => {
      if (!orderSheetFile || !packingSlipFile) {
        toast.error("Please upload both files first");
        return;
      }
      try {
        toast.info("Parsing files...", { duration: 2000 });
        const formData = new FormData();
        formData.append('order_sheet', orderSheetFile);
        formData.append('packing_slip', packingSlipFile);

        const response = await axios.post(
          `${API}/packing-list/preview`,
          formData,
          { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }, timeout: 60000 }
        );

        // Cache files as base64 for regeneration
        const ob64 = await fileToBase64(orderSheetFile);
        const sb64 = await fileToBase64(packingSlipFile);

        // Store data in sessionStorage for the new tab
        const editData = {
          parsed_products: response.data.parsed_products || [],
          header: response.data.header || {},
          customers_found: response.data.customers_found,
          products_in_slip: response.data.products_in_slip,
          order_b64: ob64,
          slip_b64: sb64,
          order_filename: orderSheetFile.name,
          slip_filename: packingSlipFile.name,
          sender_company_id: plSelectedCompany || '',
        };
        sessionStorage.setItem("packing_list_edit_data", JSON.stringify(editData));

        // Open in new browser tab
        window.open("/packing-list-edit", "_blank");

        toast.success(`Parsed ${response.data.customers_found} customers, ${response.data.products_in_slip} products — opened in new tab`);
      } catch (error) {
        toast.error("Preview failed - " + (error.response?.data?.detail || error.message));
      }
    };

    const handleGeneratePackingList = async (invoiceOverride = null, overrides = {}) => {
      // Iter 194 — invoice override comes from the Completed Invoices bar;
      // an onClick event would land here too, so validate the shape.
      if (invoiceOverride && !invoiceOverride.invoice_no) invoiceOverride = null;
      // Iter 228 — dialog-supplied company/slip override the panel state.
      const effCompany = overrides.companyId !== undefined ? overrides.companyId : plSelectedCompany;
      const effSlip = overrides.slipFile !== undefined ? overrides.slipFile : packingSlipFile;
      if (overrides.companyId) setPlSelectedCompany(overrides.companyId);
      if (!invoiceOverride && (!orderSheetFile || !packingSlipFile)) {
        toast.error("Please upload both Order Sheet and Packing Slip");
        return;
      }
      // Iter 228 — slip-less generation from an invoice is now allowed;
      // the team can add batch data later. Classic upload still needs both.

      // If in edit mode, generate from edited data
      if (!invoiceOverride && editMode && editedProducts.length > 0) {
        return handleGenerateFromEdited();
      }

      setGenerating(true);
      try {
        const formData = new FormData();
        if (invoiceOverride) {
          formData.append('invoice_no', invoiceOverride.invoice_no);
        } else {
          formData.append('order_sheet', orderSheetFile);
        }
        if (effSlip) formData.append('packing_slip', effSlip);
        if (effCompany) {
          formData.append('sender_company_id', effCompany);
        }
        if (!invoiceOverride && plInvoiceOverride.trim()) formData.append('invoice_no_hint', plInvoiceOverride.trim());

        const response = await axios.post(
          invoiceOverride
            ? `${API}/packing-list/generate-from-invoice`
            : `${API}/packing-list/generate`,
          formData,
          { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }, timeout: 60000 }
        );

        // Invoice-already-exists path — open the Replacement Confirmation
        // popup instead of downloading. The Packing List Excel is NOT
        // downloaded yet; DB A is untouched until the user clicks Replace.
        if (response.data && response.data.requires_replacement) {
          setReplacementPopup({
            open: true,
            entryId: response.data.id,
            invoiceNo: response.data.invoice_no,
            existingCount: response.data.existing_row_count,
            newCount: response.data.new_row_count,
            financialYear: response.data.financial_year || null,
            comparison: response.data.comparison || { rows: [], summary: {} },
            busy: false,
          });
          setGenerating(false);
          return;
        }

        const entryId = response.data.id;
        const filename = response.data.filename;

        const downloadResponse = await axios.get(
          `${API}/packing-list/history/${entryId}/download`,
          { headers: getAuthHeader(), responseType: 'blob', timeout: 30000 }
        );

        const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        toast.success("Packing List generated and downloaded!");
        // Iter 247 — quantity discrepancy notification
        const plDiscs = response.data.qty_discrepancies || [];
        if (plDiscs.length > 0) {
          setQtyDiscrepancies({ kind: 'packing-list', items: plDiscs });
          if (response.data.id) {
            setQtyMismatch({ kind: 'pl', entryId: response.data.id, items: plDiscs });
          }
          const over = plDiscs.filter(d => d.direction === 'over').length;
          const under = plDiscs.length - over;
          toast.warning(
            `Quantity mismatch — ${plDiscs.length} line(s): ${under} less, ${over} more. See the yellow banner.`,
            { duration: 8000 });
        }
        fetchPackingHistory();
      } catch (error) {
        const block = parseBannedBlockError(error);
        if (block.isBlocked) {
          setPackingBannedBlock(block);
          toast.error(`Packing List BLOCKED: ${block.totalMatches} banned product match(es) in source file.`, { duration: 8000 });
          return;
        }
        let errorMsg = "Failed to generate packing list";
        if (error.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            errorMsg = JSON.parse(text).detail || errorMsg;
          } catch { /* ignore */ }
        } else if (error.response?.data?.detail) {
          errorMsg = typeof error.response.data.detail === "string"
            ? error.response.data.detail : errorMsg;
        }
        toast.error(errorMsg);
      } finally {
        setGenerating(false);
      }
    };

    const handleGenerateFromEdited = async () => {
      setGenerating(true);
      try {
        const ob64 = orderSheetB64 || await fileToBase64(orderSheetFile);
        const sb64 = slipB64 || (packingSlipFile ? await fileToBase64(packingSlipFile) : '');

        const cleanProducts = editedProducts.map(({ _key, ...rest }) => rest);

        const response = await axios.post(
          `${API}/packing-list/generate-from-edited`,
          {
            order_sheet_b64: ob64,
            edited_products: cleanProducts,
            header: previewData?.header || {},
            order_filename: orderSheetFile?.name || 'OrderSheet.xlsx',
            slip_b64: sb64,
            slip_filename: packingSlipFile?.name || 'PackingSlip.pdf',
            sender_company_id: plSelectedCompany || undefined,
          },
          { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, timeout: 60000 }
        );

        const entryId = response.data.id;
        const filename = response.data.filename;

        const downloadResponse = await axios.get(
          `${API}/packing-list/history/${entryId}/download`,
          { headers: getAuthHeader(), responseType: 'blob', timeout: 30000 }
        );

        const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        toast.success("Corrected Packing List generated!");
        fetchPackingHistory();
      } catch (error) {
        const block = parseBannedBlockError(error);
        if (block.isBlocked) {
          setPackingBannedBlock(block);
          toast.error(`Packing List BLOCKED: ${block.totalMatches} banned product match(es).`, { duration: 8000 });
          return;
        }
        const detail = error.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : (error.message || "unknown");
        toast.error("Generate failed: " + msg);
      } finally {
        setGenerating(false);
      }
    };

    const handleRegenerate = async (entry) => {
      setRegeneratingId(entry.id);
      try {
        const res = await axios.get(
          `${API}/packing-list/history/${entry.id}/parsed-data`,
          { headers: getAuthHeader(), timeout: 30000 }
        );
        const data = res.data;
        if (!data.parsed_products || data.parsed_products.length === 0) {
          toast.error("No parsed data available for this entry. Generate a new one with Preview first.");
          return;
        }
        // Store data in sessionStorage and open in new tab
        const editData = {
          parsed_products: data.parsed_products,
          header: data.header,
          customers_found: data.customers_count,
          products_in_slip: data.products_count,
          order_b64: data.order_b64 || "",
          slip_b64: data.slip_b64 || "",
          order_filename: data.order_filename || "OrderSheet.xlsx",
          slip_filename: data.slip_filename || "PackingSlip.pdf",
          sender_company_id: plSelectedCompany || '',
        };
        sessionStorage.setItem("packing_list_edit_data", JSON.stringify(editData));
        window.open("/packing-list-edit", "_blank");
        toast.success("Loaded parsed data — opened in new tab for editing!");
      } catch (error) {
        toast.error("Failed to load parsed data: " + (error.response?.data?.detail || error.message));
      } finally {
        setRegeneratingId(null);
      }
    };

    const handleReset = () => {
      setOrderSheetFile(null);
      setPackingSlipFile(null);
      setPreviewData(null);
      setEditedProducts([]);
      setEditMode(false);
      setOrderSheetB64('');
      setSlipB64('');
    };

    const updateProduct = (key, field, value) => {
      setEditedProducts(prev => prev.map(p => p._key === key ? { ...p, [field]: value } : p));
    };

    const deleteProduct = (key) => {
      setEditedProducts(prev => prev.filter(p => p._key !== key));
    };

    const addProduct = () => {
      const newKey = editedProducts.length > 0 ? Math.max(...editedProducts.map(p => p._key)) + 1 : 0;
      setEditedProducts(prev => [...prev, {
        _key: newKey, sno: prev.length + 1, product_name: '', generic_name: '',
        packing: '', mfr: '', batch: '', mfd: '', exp: '', qty: 0, method: '',
      }]);
    };

    const downloadWeightSheet = async (entry) => {
      try {
        const response = await axios.get(
          `${API}/packing-list/history/${entry.id}/weight-sheet`,
          { headers: getAuthHeader(), responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${(entry.invoice_no || 'packing')}_WEIGHT_SHEET.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch {
        toast.error('Weight sheet download failed');
      }
    };

    const handleDownloadHistory = async (entry) => {
      try {
        const response = await axios.get(
          `${API}/packing-list/history/${entry.id}/download`,
          { headers: getAuthHeader(), responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', entry.filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch {
        toast.error("Download failed");
      }
    };

    // Emirates-only "Packing USD" sheet — Sr No · Generic Name · Product ·
    // Qty · Batch · Amt in USD · Rate per Unit. Derived at request time
    // from parsed_products stored with the packing-list history entry.
    const handleDownloadPackingUsd = async (entry) => {
      try {
        const invoice = entry.invoice_no || 'packing';
        const res = await axios.get(
          `${API}/packing-list/history/${entry.id}/packing-usd-excel?service=EMIRATES`,
          { headers: getAuthHeader(), responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${invoice}_PACKING_USD_EMIRATES.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`Downloaded Packing USD sheet for ${invoice}`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Packing USD export failed');
      }
    };

    const handleDeleteHistory = async (entry) => {
      if (!window.confirm(`Delete "${entry.filename}"?`)) return;
      try {
        await axios.delete(`${API}/packing-list/history/${entry.id}`, { headers: getAuthHeader() });
        toast.success("Deleted");
        fetchPackingHistory();
      } catch {
        toast.error("Delete failed");
      }
    };

    // Iter 173 — Batch-wise Stock Withdrawal List for the packing-list
    // history table. Handler MUST live in this sub-panel scope (the
    // DocumentsPanel branches on subPanel and returns different JSX for
    // packing_list vs pack_team_print — a handler defined in the other
    // branch is not reachable from here).
    const handlePlWithdrawalDownload = async (entry, fmt) => {
      try {
        const res = await axios.get(
          `${API}/packing-list/history/${entry.id}/stock-withdrawal?format=${fmt}`,
          { headers: getAuthHeader(), responseType: 'blob' }
        );
        const ext = fmt === 'excel' ? 'xlsx' : 'pdf';
        const base = `STOCK-WITHDRAWAL-${entry.invoice_no || 'INVOICE'}-${entry.invoice_date || ''}`;
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${base}.${ext}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`Stock withdrawal ${fmt.toUpperCase()} downloaded`);
      } catch (e) {
        toast.error(e?.response?.data?.detail
          || "Stock withdrawal unavailable for this entry (no product data)");
      }
    };

    return (
      <div className="space-y-6">
        <GlobalDocSearch />
        <QtyMismatchDialog mismatch={qtyMismatch} onClose={() => setQtyMismatch(null)}
          onRepaired={() => { fetchBulkHistory(); fetchPackingHistory(); }} />
        <ScanWeightsDialog entry={scanEntry} onClose={() => setScanEntry(null)}
          onSaved={fetchPackingHistory} />
        <PendingParcelsStrip tool="packing_list" />
        <CompletedInvoicesBar
          history={packingHistory}
          onUse={(inv) => openUseDialog(inv, "packing_list")}
          busy={generating}
          hint="Order Sheet auto-filled — upload only the Packing Slip"
          tool="packing_list"
          testId="packing-list" />
        <UseInvoiceDialog
          open={useDlg?.tool === "packing_list"}
          invoice={useDlg?.tool === "packing_list" ? useDlg.inv : null}
          tool="packing_list"
          companies={plCompanies}
          defaultCompany={(() => { try { return localStorage.getItem("useInvoice:lastCompany:packing_list") || ""; } catch { return ""; } })()}
          busy={generating}
          onClose={() => setUseDlg(null)}
          onConfirm={async ({ slipFile, companyId }) => {
            const inv = useDlg?.inv;
            setUseDlg(null);
            try { localStorage.setItem("useInvoice:lastCompany:packing_list", companyId); } catch {}
            setPackingSlipFile(slipFile);
            if (inv) await handleGeneratePackingList(inv, { slipFile, companyId });
            window.dispatchEvent(new Event("order-register:invoice-changed"));
          }}
        />
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {config.icon}
              {config.title}
            </CardTitle>
            <CardDescription>Upload Order Sheet + Packing Slip to generate ITPS Packing List</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Upload Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Order Sheet Upload — Iter 229 compact card */}
              <CompactUploadBox
                file={orderSheetFile}
                label="Order Sheet"
                hint="Excel file (.xlsx, .xls)"
                icon={<FileSpreadsheet className="w-4 h-4" />}
                accent="green"
                accept=".xlsx,.xls"
                inputTestId="order-sheet-input"
                removeTestId="remove-order-sheet"
                onPick={(f) => { setOrderSheetFile(f); setEditMode(false); setPreviewData(null); }}
                onRemove={() => { setOrderSheetFile(null); setEditMode(false); setPreviewData(null); setEditedProducts([]); }} />

              {/* Packing Slip Upload — Iter 229 compact card */}
              <CompactUploadBox
                file={packingSlipFile}
                label="Packing Slip"
                hint="PDF file (.pdf)"
                icon={<FileText className="w-4 h-4" />}
                accent="orange"
                accept=".pdf"
                inputTestId="packing-slip-input"
                removeTestId="remove-packing-slip"
                onPick={(f) => { setPackingSlipFile(f); setEditMode(false); setPreviewData(null); }}
                onRemove={() => { setPackingSlipFile(null); setEditMode(false); setPreviewData(null); setEditedProducts([]); }} />
            </div>

            {/* Sender Company Selector */}
            <div className="bg-slate-50 rounded-lg p-4 border" data-testid="pl-company-selector">
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                Sender Company
                <span className={`ml-2 text-xs font-normal ${plCompanies.length > 0 ? 'text-green-600' : 'text-red-500'}`}>
                  ({plCompanies.length} companies loaded)
                </span>
              </Label>
              {plCompanies.length === 0 ? (
                <p className="text-xs text-red-500">No companies found. Add companies in Manage Companies panel.</p>
              ) : (
                <>
                  <select
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={plSelectedCompany}
                    onChange={(e) => setPlSelectedCompany(e.target.value)}
                    data-testid="pl-company-select"
                  >
                    <option value="">-- Select sender company --</option>
                    {plCompanies.map(c => (
                      <option key={c.id || c.name} value={c.id || c.name}>{c.name}</option>
                    ))}
                  </select>
                  {plSelectedCompany && (
                    <div className="mt-2 text-xs text-slate-500 flex gap-4">
                      {(() => {
                        const sc = plCompanies.find(c => (c.id || c.name) === plSelectedCompany);
                        return sc ? (
                          <>
                            <span>IEC: <strong className="text-slate-700">{sc.iec || 'N/A'}</strong></span>
                            <span>GSTIN: <strong className="text-slate-700">{sc.gstin || 'N/A'}</strong></span>
                          </>
                        ) : null;
                      })()}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-center" title="Type an invoice number to force it, in case the slip is wrong">
              <Input value={plInvoiceOverride} onChange={e => setPlInvoiceOverride(e.target.value)}
                     placeholder="Invoice # override (optional)" className="h-9 w-60 text-sm" data-testid="pl-invoice-override" />
            </div>
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 justify-center pt-2">
              <Button onClick={handlePreview} variant="outline" disabled={!orderSheetFile || !packingSlipFile}
                data-testid="preview-packing-list">
                <Eye className="w-4 h-4 mr-2" /> Preview & Parse Data
              </Button>
              <Button onClick={handleGeneratePackingList} disabled={!orderSheetFile || !packingSlipFile || generating}
                className="bg-blue-600 hover:bg-blue-700" data-testid="generate-packing-list">
                {generating ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" /> Generate Packing Sheet</>
                )}
              </Button>
              {(orderSheetFile || packingSlipFile) && (
                <Button variant="ghost" onClick={() => { handleReset(); setPackingBannedBlock(null); }} data-testid="reset-packing-list">
                  <RotateCcw className="w-4 h-4 mr-2" /> Reset
                </Button>
              )}
            </div>

            <BannedBlockBanner block={packingBannedBlock} onClose={() => setPackingBannedBlock(null)} />

            {/* Customer Preview (read-only) */}
            {previewData && previewData.customers && !editMode && (
              <PackingListPreviewWithBannedCheck previewData={previewData} />
            )}
          </CardContent>
        </Card>

        {/* History Section */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="w-4 h-4 text-slate-500" />
                Generation History
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline"
                        className="h-7 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                        title="Ping the Bulk Labels team for every packing list still missing weights"
                        onClick={async () => {
                          try {
                            const r = await axios.post(`${API}/packing-list/weight-reminders/sweep`, {}, { headers: getAuthHeader() });
                            toast.success(`Reminder sweep done — ${r.data.pinged} invoice(s) pinged, ${r.data.skipped} skipped`);
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Reminder sweep failed");
                          }
                        }}
                        data-testid="packing-weight-reminder-sweep">
                  <Bell className="w-3.5 h-3.5 mr-1" /> Ping Missing Weights
                </Button>
                <div className="relative w-60">
                  <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-slate-400" />
                  <Input value={packingHistQ} onChange={(e) => setPackingHistQ(e.target.value)}
                         placeholder="Search invoice, client, code, tracking…"
                         className="h-7 pl-7 text-xs" data-testid="packing-history-search" />
                </div>
                <OriginFilter value={originOf("packing_list")} onChange={setOriginOf("packing_list")} testId="packing-list" />
                <CentralEditLog module="packing_list" label="Packing List" />
                <SupersededToggle on={showSupPl} toggle={toggleSupPl} count={packingHistory.filter(h => h.superseded_by).length} testId="packing-list" />
                <NameLinksReviewButton />
                <WeightSortToggle mode={wtModePl} setMode={setWtModePl} st={wtSt} testId="packing-list" />
                <CompanyMissingTools rows={packingHistory.filter(h => showSupPl || !h.superseded_by)} on={plNoCo} toggle={() => setPlNoCo(v => !v)} type="packing_list" testId="packing-list" onSaved={() => { fetchPackingHistory(); fetchBulkHistory(); }} />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : packingHistory.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400">
                No packing lists generated yet. Upload files above to get started.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="mb-2"><DocPrintBar keys={greenKeys(plRows.filter(h => (showSupPl || !h.superseded_by) && docSearchMatch(h, packingHistQ) && originMatch(directCreated, "packing_list", h, originOf("packing_list")) && (!plNoCo || companyMissing(h))))} sel={plSel.sel} setSel={plSel.setSel} label="Select all weighed" testid="wt-print-pl" /></div>
                <table className="w-full text-sm" data-testid="packing-history-table">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-2 py-2 w-8" title="Tick fully weighed invoices to print"></th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Filename</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Invoice</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Company</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Date</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Customers</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Products</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Generated At</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plLatest.apply(plRows.filter(h => (showSupPl || !h.superseded_by) && docSearchMatch(h, packingHistQ) && originMatch(directCreated, "packing_list", h, originOf("packing_list")) && (!plNoCo || companyMissing(h)))).map((entry) => { const isDirect = isDirectEntry(directCreated, "packing_list", entry); return (
                      <tr key={entry.id} className={`border-b ${isDirect ? DIRECT_ROW_CLS : "hover:bg-slate-50"}`} data-testid={`history-row-${entry.id}`}>
                        <td className="px-2 py-2.5 text-center w-8">{isGreen(entry) ? <SelectBox k={invKeyOf(entry)} sel={plSel.sel} toggle={plSel.toggle} testid={`wt-print-select-pl-${entry.id}`} /> : null}</td>
                        <td className={`px-3 py-2.5 font-medium text-slate-800 ${plCancelFlags[entry.id] || entry.superseded_by ? "max-w-[340px]" : "max-w-[320px] truncate"}`} title={entry.filename}>
                          {plLatest.chip(entry, "pl")}{isDirect && <DirectBadge testId={`direct-badge-pl-${entry.id}`} />}{isDirect && " "}<span className={entry.superseded_by ? SUPERSEDED_ROW_CLS : ""}>{entry.filename}</span><CorrectedTag entry={entry} testId="pl" />                          <CancelFlagBadges entry={entry} flags={plCancelFlags} testId="pl" onRegenerated={() => { fetchPackingHistory(); fetchBulkHistory(); reloadPlFlags(); reloadBdFlags(); }} />
                          {entry.regenerated && <span className="ml-1 text-[9px] bg-amber-100 text-amber-700 px-1 rounded">edited</span>}
                          {(entry.missing_weight_count || 0) > 0 && (
                            <span
                              className="ml-1 text-[9px] bg-rose-100 text-rose-700 px-1 py-0.5 rounded font-semibold"
                              title={`${entry.missing_weight_count} of ${entry.total_parcels || "?"} parcels missing weight — bulk labels would print 0 g`}
                              data-testid={`history-missing-weight-${entry.id}`}>
                              ● {entry.missing_weight_count} no-wt
                            </span>
                          )}
                          {entry.emi_generated_count > 0 && (
                            <span
                              className="ml-1 text-[9px] bg-sky-100 text-sky-700 px-1 py-0.5 rounded font-semibold"
                              title={
                                (entry.emi_generated_trackings || []).length
                                  ? `Auto-generated EMI trackings for Emirates Post parcels:\n${entry.emi_generated_trackings.join(', ')}`
                                  : `${entry.emi_generated_count} Emirates trackings were auto-generated for this batch.`
                              }
                              data-testid={`history-emi-count-${entry.id}`}>
                              +{entry.emi_generated_count} EMI
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 font-mono text-xs">{entry.invoice_no}<WeightChip s={wtSt[wkey(entry)]} testId={`wt-chip-pl-${entry.id}`} /></td>
                        <DocCompanyCell entry={entry} type="packing_list" className="px-3 py-2.5 text-slate-600 text-xs" onSaved={() => { fetchPackingHistory(); fetchBulkHistory(); }} />
                        <td className="px-3 py-2.5 text-slate-600">{entry.invoice_date}</td>
                        <td className="px-3 py-2.5 text-center">
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">{entry.customers_count}</span>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs font-medium">{entry.products_count}</span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs">
                          {new Date(entry.created_at).toLocaleString()}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {(entry.qty_discrepancies || []).length > 0 && (
                              <Button variant="ghost" size="sm" data-testid={`pl-qty-mismatch-${entry.id}`}
                                onClick={() => setQtyMismatch({ kind: 'pl', entryId: entry.id, items: entry.qty_discrepancies })}
                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                title={`${entry.qty_discrepancies.length} quantity mismatch(es) — click to repair`}>
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => openPreviewTab('packing_list', entry.id)}
                              data-testid={`preview-history-${entry.id}`}
                              className="h-7 w-7 p-0 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50" title="Preview">
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDownloadHistory(entry)}
                              data-testid={`download-history-${entry.id}`}
                              className="h-7 w-7 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50" title="Download Output">
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => downloadWeightSheet(entry)}
                              data-testid={`pl-weight-sheet-${entry.id}`}
                              className="h-7 w-7 p-0 text-violet-600 hover:text-violet-800 hover:bg-violet-50"
                              title="Manual weight input sheet (print for pack team)">
                              <Scale className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setScanEntry(entry)}
                              data-testid={`pl-scan-weights-${entry.id}`}
                              className="h-7 w-7 p-0 text-violet-600 hover:text-violet-800 hover:bg-violet-50"
                              title="Scan-back weights — upload the filled weight sheet, AI reads it">
                              <ScanLine className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm"
                              onClick={() => handleDownloadPackingUsd(entry)}
                              data-testid={`packing-usd-${entry.id}`}
                              className="h-auto px-1.5 py-1 text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 text-[10px] leading-tight font-bold"
                              title="Download Emirates Packing USD sheet (Sr.No · Generic · Product · Qty · Batch · Amt USD · Rate/Unit)">
                              <div className="flex flex-col items-center gap-0.5">
                                <span className="text-base leading-none">$</span>
                                <span>USD</span>
                              </div>
                            </Button>
                            {/* Iter 173 — Batch-wise Stock Withdrawal List (store keeper) */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost" size="sm"
                                  data-testid={`pl-withdrawal-${entry.id}`}
                                  className="h-7 w-7 p-0 text-purple-600 hover:text-purple-800 hover:bg-purple-50"
                                  title="Stock Withdrawal List — batch-wise with expiry (PDF / Excel)"
                                >
                                  <PackageMinus className="w-3.5 h-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" data-testid={`pl-withdrawal-menu-${entry.id}`}>
                                <DropdownMenuItem
                                  onClick={() => handlePlWithdrawalDownload(entry, 'pdf')}
                                  data-testid={`pl-withdrawal-pdf-${entry.id}`}
                                >
                                  <FileText className="w-3.5 h-3.5 mr-2 text-red-500" /> Withdrawal PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handlePlWithdrawalDownload(entry, 'excel')}
                                  data-testid={`pl-withdrawal-excel-${entry.id}`}
                                >
                                  <FileSpreadsheet className="w-3.5 h-3.5 mr-2 text-green-600" /> Withdrawal Excel
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <Button variant="ghost" size="sm"
                              onClick={() => handleRegenerate(entry)}
                              disabled={regeneratingId === entry.id}
                              data-testid={`regenerate-history-${entry.id}`}
                              className="h-auto px-1.5 py-1 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 text-[10px] leading-tight"
                              title="Edit & Regenerate">
                              <div className="flex flex-col items-center gap-0.5">
                                {regeneratingId === entry.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Pencil className="w-3 h-3" />}
                                <span>Regen</span>
                              </div>
                            </Button>
                            {entry.source_files && entry.source_files.length > 0 ? (
                              <>
                                <Button variant="ghost" size="sm"
                                  onClick={() => downloadSourceFile('packing_list', entry.id, 0, entry.source_files[0]?.name || 'OrderSheet.xlsx')}
                                  data-testid={`source-order-${entry.id}`}
                                  className="h-auto px-1.5 py-1 text-amber-600 hover:text-amber-800 hover:bg-amber-50 text-[10px] leading-tight"
                                  title={`Download: ${entry.source_files[0]?.name || 'Order Sheet'}`}>
                                  <div className="flex flex-col items-center gap-0.5"><FileInput className="w-3 h-3" /><span>Order</span></div>
                                </Button>
                                {entry.source_files.length > 1 && (
                                  <Button variant="ghost" size="sm"
                                    onClick={() => downloadSourceFile('packing_list', entry.id, 1, entry.source_files[1]?.name || 'PackingSlip.pdf')}
                                    data-testid={`source-slip-${entry.id}`}
                                    className="h-auto px-1.5 py-1 text-orange-600 hover:text-orange-800 hover:bg-orange-50 text-[10px] leading-tight"
                                    title={`Download: ${entry.source_files[1]?.name || 'Packing Slip'}`}>
                                    <div className="flex flex-col items-center gap-0.5"><FileInput className="w-3 h-3" /><span>Slip</span></div>
                                  </Button>
                                )}
                              </>
                            ) : (
                              <span className="text-[10px] text-slate-400">N/A</span>
                            )}
                            {!entry.superseded_by && (
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteHistory(entry)}
                              data-testid={`delete-history-${entry.id}`}
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" title="Delete">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>)}
                          </div>
                        </td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invoice Replacement Popup — opens when a re-uploaded invoice
            already exists in Database A within the same FY. Shows a
            row-by-row comparison; operator picks Replace / Cancel /
            Download. */}
        <Dialog open={replacementPopup.open} onOpenChange={(o) => !o && !replacementPopup.busy && closeReplacementPopup()}>
          <DialogContent className="max-w-5xl max-h-[86vh] overflow-hidden flex flex-col" data-testid="invoice-replacement-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-900" data-testid="invoice-replacement-title">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Invoice Already Exists
              </DialogTitle>
              <div className="text-[12px] text-slate-600 leading-relaxed">
                <strong className="text-slate-900">Invoice {replacementPopup.invoiceNo}</strong> already
                exists in Database A within the current financial year.
                A new Packing List contains changes — review below before replacing.
                {replacementPopup.financialYear && (
                  <span className="ml-1 text-[11px] text-slate-500">
                    (FY {String(replacementPopup.financialYear.start || '').slice(0, 10)} → {String(replacementPopup.financialYear.end || '').slice(0, 10)})
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-[11px] mt-1">
                <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                  Existing: <strong>{replacementPopup.existingCount}</strong> rows
                </span>
                <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                  New: <strong>{replacementPopup.newCount}</strong> rows
                </span>
                {(() => {
                  const s = replacementPopup.comparison?.summary || {};
                  return (
                    <>
                      {(s.changed || 0) > 0 && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800">Changed: <strong>{s.changed}</strong></span>}
                      {(s.added || 0) > 0   && <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">Added: <strong>{s.added}</strong></span>}
                      {(s.removed || 0) > 0 && <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-800">Removed: <strong>{s.removed}</strong></span>}
                      {(s.unchanged || 0) > 0 && <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600">Same: <strong>{s.unchanged}</strong></span>}
                    </>
                  );
                })()}
              </div>
            </DialogHeader>

            <div className="flex-1 overflow-auto border rounded" data-testid="invoice-replacement-table">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-slate-100 text-slate-600">
                  <tr>
                    <th className="text-left px-2 py-1">Field</th>
                    <th className="text-left px-2 py-1">Existing</th>
                    <th className="text-left px-2 py-1">New</th>
                  </tr>
                </thead>
                <tbody>
                  {(replacementPopup.comparison?.rows || []).map((r, i) => {
                    const changed = new Set(r.changed_fields || []);
                    const isAdded = changed.has('added');
                    const isRemoved = changed.has('removed');
                    const cellCls = (field) => {
                      if (isAdded) return 'bg-emerald-50';
                      if (isRemoved) return 'bg-rose-50';
                      return changed.has(field) ? 'bg-orange-100 text-orange-900 font-medium' : '';
                    };
                    const old = r.existing || {};
                    const nw = r.new || {};
                    // Only show rows with changes/add/remove or non-empty pair
                    return (
                      <Fragment key={i}>
                        <tr className="border-t border-slate-200 align-top">
                          <td className="px-2 py-1 font-mono text-[10px] text-slate-500" rowSpan={6}>
                            #{i + 1}
                            {isAdded && <div className="text-[9px] text-emerald-700 mt-0.5">ADDED</div>}
                            {isRemoved && <div className="text-[9px] text-rose-700 mt-0.5">REMOVED</div>}
                            {!isAdded && !isRemoved && changed.size > 0 && (
                              <div className="text-[9px] text-amber-700 mt-0.5">CHANGED</div>
                            )}
                          </td>
                        </tr>
                        <tr className="border-t border-slate-100">
                          <td className="px-2 py-0.5 text-slate-500 w-24">Invoice No.</td>
                          <td className="px-2 py-0.5">{old.invoice_no || '—'}</td>
                          <td className="px-2 py-0.5">{nw.invoice_no || '—'}</td>
                        </tr>
                        <tr>
                          <td className="px-2 py-0.5 text-slate-500">Packing Date</td>
                          <td className={`px-2 py-0.5 ${cellCls('packing_date')}`} data-testid={changed.has('packing_date') ? `cmp-changed-date-${i}` : undefined}>{old.parcel_date || '—'}</td>
                          <td className={`px-2 py-0.5 ${cellCls('packing_date')}`}>{nw.parcel_date || '—'}</td>
                        </tr>
                        <tr>
                          <td className="px-2 py-0.5 text-slate-500">Tracking</td>
                          <td className={`px-2 py-0.5 font-mono ${cellCls('tracking')}`}>{old.track_no || '—'}</td>
                          <td className={`px-2 py-0.5 font-mono ${cellCls('tracking')}`}>{nw.track_no || '—'}</td>
                        </tr>
                        <tr>
                          <td className="px-2 py-0.5 text-slate-500">Product</td>
                          <td className={`px-2 py-0.5 ${cellCls('product')}`} data-testid={changed.has('product') ? `cmp-changed-product-${i}` : undefined}>{old.product || '—'}</td>
                          <td className={`px-2 py-0.5 ${cellCls('product')}`}>{nw.product || '—'}</td>
                        </tr>
                        <tr>
                          <td className="px-2 py-0.5 text-slate-500">Quantity</td>
                          <td className={`px-2 py-0.5 ${cellCls('qty')}`}>{old.qty || '—'}</td>
                          <td className={`px-2 py-0.5 ${cellCls('qty')}`}>{nw.qty || '—'}</td>
                        </tr>
                        <tr className="border-b-2 border-slate-200">
                          <td className="px-2 py-0.5 text-slate-500">Client</td>
                          <td className={`px-2 py-0.5 ${cellCls('customer')}`}>{old.customer_name || '—'}</td>
                          <td className={`px-2 py-0.5 ${cellCls('customer')}`}>{nw.customer_name || '—'}</td>
                        </tr>
                      </Fragment>
                    );
                  })}
                  {(replacementPopup.comparison?.rows || []).length === 0 && (
                    <tr><td colSpan={3} className="p-4 text-center text-slate-500">No rows to compare</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <DialogFooter className="flex flex-wrap gap-2 justify-between pt-2 border-t">
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => handleReplaceDownloadExcel('changes')}
                  disabled={replacementPopup.busy}
                  data-testid="invoice-replacement-download-changes"
                >
                  <Download className="w-3.5 h-3.5 mr-1" />
                  Changes Excel
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={() => handleReplaceDownloadExcel('full')}
                  disabled={replacementPopup.busy}
                  data-testid="invoice-replacement-download-full"
                >
                  <Download className="w-3.5 h-3.5 mr-1" />
                  Full Excel
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm"
                  onClick={handleReplaceCancel}
                  disabled={replacementPopup.busy}
                  data-testid="invoice-replacement-cancel"
                >
                  Cancel
                </Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  size="sm"
                  onClick={handleReplaceConfirm}
                  disabled={replacementPopup.busy}
                  data-testid="invoice-replacement-confirm"
                >
                  {replacementPopup.busy ? 'Working…' : 'Replace Existing'}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  if (subPanel === 'pack_team_print') {
    const handlePtGenerate = async (invoiceOverride = null, overrides = {}) => {
      // Iter 194 — invoice override from the Completed Invoices bar
      if (invoiceOverride && !invoiceOverride.invoice_no) invoiceOverride = null;
      // Iter 228 — dialog-supplied slip overrides the panel state.
      const effSlip = overrides.slipFile !== undefined ? overrides.slipFile : ptSlipFile;
      if (!invoiceOverride && !ptOrderFile) {
        toast.error("Please upload an Order Sheet");
        return;
      }
      setPtGenerating(true);
      try {
        const formData = new FormData();
        if (invoiceOverride) {
          formData.append('invoice_no', invoiceOverride.invoice_no);
        } else {
          formData.append('order_sheet', ptOrderFile);
        }
        if (effSlip) formData.append('packing_slip', effSlip);
        if (!invoiceOverride && ptInvoiceOverride.trim()) formData.append('invoice_override', ptInvoiceOverride.trim());
        formData.append('mode', 'check');  // detect duplicates first
        const response = await axios.post(
          invoiceOverride
            ? `${API}/pack-team/generate-from-invoice`
            : `${API}/pack-team/generate`, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        });

        // Duplicate detection path — open the Replace / Keep Both / Cancel popup
        if (response.data && response.data.requires_action) {
          setPtDupePopup({
            open: true,
            token: response.data.pending_token,
            invoiceNo: response.data.invoice_no,
            existingEntries: response.data.existing_entry_count,
            existingParcels: response.data.existing_parcels,
            newParcels: response.data.new_parcels,
            comparison: response.data.comparison || { rows: [], summary: {} },
            busy: false,
          });
          setPtGenerating(false);
          return;
        }

        const { id: entryId, filename } = response.data;
        // Download the generated PDF
        const dlRes = await axios.get(`${API}/pack-team/history/${entryId}/download`, {
          headers: getAuthHeader(), responseType: 'blob', timeout: 30000,
        });
        const url = window.URL.createObjectURL(new Blob([dlRes.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`Generated ${response.data.customers_count} slips on ${response.data.pages_count} page(s)`);
        if (response.data.withdrawal_available) {
          toast.success("Stock Withdrawal List saved — use the purple icon in Print History to download PDF/Excel", { duration: 5000 });
        }
        fetchPtHistory();
      } catch (error) {
        console.error("Pack Team generation error:", error);
        let msg = "Failed to generate Pack Team PDF";
        if (error.response?.data?.detail) msg = error.response.data.detail;
        toast.error(msg);
      } finally {
        setPtGenerating(false);
      }
    };

    const handlePtDupeConfirm = async (mode) => {
      if (!ptDupePopup.token) return;
      setPtDupePopup(p => ({ ...p, busy: true }));
      try {
        const fd = new FormData();
        fd.append('token', ptDupePopup.token);
        fd.append('mode', mode);
        const r = await axios.post(`${API}/pack-team/confirm`, fd, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
          timeout: 20000,
        });

        if (mode === 'cancel') {
          toast.info('Cancelled — nothing was saved');
        } else {
          // Download the PDF that was just materialised
          try {
            const dl = await axios.get(`${API}/pack-team/history/${r.data.entry_id}/download`, {
              headers: getAuthHeader(), responseType: 'blob', timeout: 30000,
            });
            const url = window.URL.createObjectURL(new Blob([dl.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', r.data.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          } catch { /* best-effort */ }

          if (mode === 'replace') {
            toast.success(`Replaced ${r.data.removed_entries} existing entry(ies) · ${r.data.customers_count} slips generated`);
          } else if (mode === 'keep_both') {
            toast.success(`Kept both versions · ${r.data.customers_count} slips generated`);
          }
        }
        setPtDupePopup({ open: false, token: null, invoiceNo: '',
          existingEntries: 0, existingParcels: 0, newParcels: 0,
          comparison: { rows: [], summary: {} }, busy: false });
        fetchPtHistory();
      } catch (e) {
        toast.error(e?.response?.data?.detail || `${mode} failed`);
        setPtDupePopup(p => ({ ...p, busy: false }));
      }
    };

    const handlePtPreview = async () => {
      if (!ptOrderFile) { toast.error("Please upload an Order Sheet first"); return; }
      try {
        const formData = new FormData();
        formData.append('order_sheet', ptOrderFile);
        if (ptSlipFile) formData.append('packing_slip', ptSlipFile);
        const response = await axios.post(`${API}/pack-team/preview`, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
          timeout: 30000,
        });
        setPtPreviewData(response.data);
        toast.success(`Found ${response.data.customers_count} customers (${response.data.pages_count} page(s))`);
      } catch {
        toast.error("Preview failed");
      }
    };

    const handlePtDownloadHistory = async (entry) => {
      try {
        const res = await axios.get(`${API}/pack-team/history/${entry.id}/download`, {
          headers: getAuthHeader(), responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', entry.filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch { toast.error("Download failed"); }
    };

    // Iter 173 — Stock Withdrawal List downloads (store-keeper pull list)
    const handlePtWithdrawalDownload = async (entry, fmt) => {
      try {
        const res = await axios.get(`${API}/pack-team/history/${entry.id}/withdrawal?format=${fmt}`, {
          headers: getAuthHeader(), responseType: 'blob',
        });
        const ext = fmt === 'excel' ? 'xlsx' : 'pdf';
        const base = entry.withdrawal_base || `STOCK-WITHDRAWAL-${entry.invoice_no || 'PACKTEAM'}`;
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${base}.${ext}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`Stock withdrawal ${fmt.toUpperCase()} downloaded`);
      } catch {
        toast.error("No stock withdrawal list stored for this entry — regenerate the pack team print");
      }
    };

    const handlePtPrintHistory = async (entry) => {
      try {
        const res = await axios.get(`${API}/pack-team/history/${entry.id}/download`, {
          headers: getAuthHeader(), responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const win = window.open(url, '_blank');
        if (win) {
          win.addEventListener('load', () => { win.print(); });
        }
      } catch { toast.error("Print failed"); }
    };

    const handlePtDeleteHistory = async (entry) => {
      if (!window.confirm(`Delete "${entry.filename}"?`)) return;
      try {
        await axios.delete(`${API}/pack-team/history/${entry.id}`, { headers: getAuthHeader() });
        toast.success("Deleted");
        fetchPtHistory();
      } catch { toast.error("Delete failed"); }
    };

    return (
      <div className="space-y-6" data-testid="pack-team-print-panel">
        <GlobalDocSearch />
        <PendingParcelsStrip tool="pack_team" />
        <CompletedInvoicesBar
          history={ptHistory}
          onUse={(inv) => openUseDialog(inv, "pack_team")}
          busy={ptGenerating}
          hint="Order Sheet auto-filled — Packing Slip stays optional"
          tool="pack_team"
          testId="pack-team" />
        <UseInvoiceDialog
          open={useDlg?.tool === "pack_team"}
          invoice={useDlg?.tool === "pack_team" ? useDlg.inv : null}
          tool="pack_team"
          companies={[]}
          defaultCompany={(() => { try { return localStorage.getItem("useInvoice:lastCompany:pack_team") || ""; } catch { return ""; } })()}
          busy={ptGenerating}
          onClose={() => setUseDlg(null)}
          onConfirm={async ({ slipFile, companyId }) => {
            const inv = useDlg?.inv;
            setUseDlg(null);
            try { localStorage.setItem("useInvoice:lastCompany:pack_team", companyId); } catch {}
            setPtSlipFile(slipFile);
            if (inv) await handlePtGenerate(inv, { slipFile, companyId });
            window.dispatchEvent(new Event("order-register:invoice-changed"));
          }}
        />
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" data-testid="pack-team-title">
              {config.icon}
              {config.title}
            </CardTitle>
            <CardDescription>Upload Order Sheet to generate packing slips — 5 per A4 page, horizontal layout for easy cutting</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Upload Section */}
            <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Order Sheet Upload — Iter 229 compact card */}
              <CompactUploadBox
                file={ptOrderFile}
                label="Order Sheet"
                hint="Excel file (.xlsx, .xls)"
                icon={<FileSpreadsheet className="w-4 h-4" />}
                accent="teal"
                accept=".xlsx,.xls"
                inputTestId="pt-order-input"
                removeTestId="pt-remove-order"
                onPick={(f) => setPtOrderFile(f)}
                onRemove={() => { setPtOrderFile(null); setPtPreviewData(null); }} />

              {/* Packing Slip Upload — Iter 229 compact card */}
              <CompactUploadBox
                file={ptSlipFile}
                label="Packing Slip / Invoice"
                hint="PDF for batch numbers (optional)"
                icon={<FileText className="w-4 h-4" />}
                accent="orange"
                accept=".pdf"
                inputTestId="pt-slip-input"
                removeTestId="pt-remove-slip"
                onPick={(f) => setPtSlipFile(f)}
                onRemove={() => { setPtSlipFile(null); setPtPreviewData(null); }} />
            </div>

            <div className="flex justify-center" title="Type an invoice number to force it, in case the slip is wrong">
              <Input value={ptInvoiceOverride} onChange={e => setPtInvoiceOverride(e.target.value)}
                     placeholder="Invoice # override (optional)" className="h-9 w-60 text-sm" data-testid="pt-invoice-override" />
            </div>
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 justify-center pt-2">
              <Button
                onClick={handlePtPreview}
                variant="outline"
                disabled={!ptOrderFile}
                data-testid="pt-preview-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview Data
              </Button>
              <Button
                onClick={handlePtGenerate}
                disabled={!ptOrderFile || ptGenerating}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="pt-generate-btn"
              >
                {ptGenerating ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><Printer className="w-4 h-4 mr-2" /> Generate Print PDF</>
                )}
              </Button>
              {ptOrderFile && (
                <Button variant="ghost" onClick={() => { setPtOrderFile(null); setPtSlipFile(null); setPtPreviewData(null); }} data-testid="pt-reset-btn">
                  <RotateCcw className="w-4 h-4 mr-2" /> Reset
                </Button>
              )}
            </div>

            {/* Preview Section */}
            {ptPreviewData && (
              <PackTeamPreviewWithBannedCheck ptPreviewData={ptPreviewData} />
            )}
          </CardContent>
        </Card>

        {/* History Section */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="w-4 h-4 text-slate-500" />
                Print History
              </CardTitle>
              <OriginFilter value={originOf("pack_team")} onChange={setOriginOf("pack_team")} testId="pack-team" />
              <select value={ptCompany} onChange={(e) => setPtCompany(e.target.value)}
                      className="h-7 rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid="pt-history-company-filter">
                <option value="">All companies</option>
                {[...new Set(ptHistory.map(e => (e.sender_company || "").trim()).filter(Boolean))].sort().map(c => <option key={c} value={c}>{c}</option>)}
                <option value="__none__">No company</option>
              </select>
              <div className="relative w-60">
                <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-slate-400" />
                <Input value={ptHistQ} onChange={(e) => setPtHistQ(e.target.value)}
                       placeholder="Search invoice, client, code, tracking…"
                       className="h-7 pl-7 text-xs" data-testid="pt-history-search" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {ptHistoryLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : ptHistory.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400">
                No pack team PDFs generated yet. Upload an order sheet above to get started.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="pt-history-table">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Filename</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Company</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Source</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Customers</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Pages</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Generated By</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Generated At</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ptLatest.apply(ptHistory.filter(e2 => docSearchMatch(e2, ptHistQ) && originMatch(directCreated, "pack_team", e2, originOf("pack_team")) && (!ptCompany || (ptCompany === "__none__" ? !(e2.sender_company || "").trim() : (e2.sender_company || "").trim() === ptCompany)))).map((entry) => { const isDirect = isDirectEntry(directCreated, "pack_team", entry); return (
                      <tr key={entry.id} className={`border-b ${isDirect ? DIRECT_ROW_CLS : "hover:bg-slate-50"}`} data-testid={`pt-history-row-${entry.id}`}>
                        <td className="px-3 py-2.5 font-medium text-slate-800 max-w-[200px] truncate">{isDirect && <DirectBadge testId={`direct-badge-pt-${entry.id}`} />}{isDirect && " "}{ptLatest.chip(entry, "pt")}{entry.special_care_trackings?.length > 0 && <span className="mr-1 px-1.5 py-0.5 bg-red-600 text-white rounded text-[10px] font-bold" title={`Special Care: ${entry.special_care_trackings.join(", ")}`} data-testid={`pt-special-care-${entry.id}`}>{entry.special_care_trackings.length}</span>}{entry.filename}</td>
                        <td className="px-3 py-2.5 text-slate-600 text-xs" data-testid={`pt-history-company-${entry.id}`}>{entry.sender_company || '—'}</td>
                        <td className="px-3 py-2.5 text-slate-600 text-xs max-w-[150px] truncate">{entry.source_file}</td>
                        <td className="px-3 py-2.5 text-center">
                          <span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded text-xs font-medium">{entry.customers_count}</span>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">{entry.pages_count}</span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-600">{entry.user_name}</td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs">
                          {new Date(entry.created_at).toLocaleString()}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => openPreviewTab('pack_team', entry.id)}
                              data-testid={`pt-preview-${entry.id}`}
                              className="h-7 w-7 p-0 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                              title="Preview"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => handlePtDownloadHistory(entry)}
                              data-testid={`pt-download-${entry.id}`}
                              className="h-7 w-7 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              title="Download"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                            {entry.source_files && entry.source_files.length > 0 ? (
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => downloadSourceFile('pack_team', entry.id, 0, entry.source_files[0]?.name || entry.source_file || 'OrderSheet.xlsx')}
                                data-testid={`pt-source-${entry.id}`}
                                className="h-auto px-1.5 py-1 text-amber-600 hover:text-amber-800 hover:bg-amber-50 text-[10px] leading-tight"
                                title={`Download: ${entry.source_files[0]?.name || 'Order Sheet'}`}
                              >
                                <div className="flex flex-col items-center gap-0.5"><FileInput className="w-3 h-3" /><span>Order</span></div>
                              </Button>
                            ) : (
                              <span className="text-[10px] text-slate-400" title="Source file not stored for this entry">N/A</span>
                            )}
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => handlePtPrintHistory(entry)}
                              data-testid={`pt-print-${entry.id}`}
                              className="h-7 w-7 p-0 text-teal-600 hover:text-teal-800 hover:bg-teal-50"
                              title="Print"
                            >
                              <Printer className="w-3.5 h-3.5" />
                            </Button>
                            {/* Iter 173 — Stock Withdrawal List (store keeper pull list) */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost" size="sm"
                                  data-testid={`pt-withdrawal-${entry.id}`}
                                  className="h-7 w-7 p-0 text-purple-600 hover:text-purple-800 hover:bg-purple-50"
                                  title="Stock Withdrawal List (PDF / Excel)"
                                >
                                  <PackageMinus className="w-3.5 h-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" data-testid={`pt-withdrawal-menu-${entry.id}`}>
                                <DropdownMenuItem
                                  onClick={() => handlePtWithdrawalDownload(entry, 'pdf')}
                                  data-testid={`pt-withdrawal-pdf-${entry.id}`}
                                >
                                  <FileText className="w-3.5 h-3.5 mr-2 text-red-500" /> Withdrawal PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handlePtWithdrawalDownload(entry, 'excel')}
                                  data-testid={`pt-withdrawal-excel-${entry.id}`}
                                >
                                  <FileSpreadsheet className="w-3.5 h-3.5 mr-2 text-green-600" /> Withdrawal Excel
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => handlePtDeleteHistory(entry)}
                              data-testid={`pt-delete-${entry.id}`}
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pack Team duplicate popup — Replace / Keep Both / Cancel */}
        <Dialog open={ptDupePopup.open} onOpenChange={(o) => !o && !ptDupePopup.busy && setPtDupePopup(p => ({ ...p, open: false }))}>
          <DialogContent className="max-w-3xl max-h-[86vh] overflow-hidden flex flex-col" data-testid="pt-dupe-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-900" data-testid="pt-dupe-title">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Invoice Already Exists
              </DialogTitle>
              <div className="text-[12px] text-slate-600 leading-relaxed">
                Pack Team Print already has <strong className="text-slate-900">{ptDupePopup.existingEntries}</strong>{' '}
                entry(ies) for invoice{' '}
                <strong className="text-slate-900">{ptDupePopup.invoiceNo}</strong>{' '}
                covering <strong>{ptDupePopup.existingParcels}</strong> parcel(s).
                New upload has <strong>{ptDupePopup.newParcels}</strong> parcel(s).
              </div>
              <div className="flex flex-wrap items-center gap-2 text-[11px] mt-1">
                <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                  Existing entries: <strong>{ptDupePopup.existingEntries}</strong>
                </span>
                <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                  Existing parcels: <strong>{ptDupePopup.existingParcels}</strong>
                </span>
                <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                  New parcels: <strong>{ptDupePopup.newParcels}</strong>
                </span>
                {(ptDupePopup.comparison?.summary?.parcels_affected || 0) > 0 && (
                  <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                    Parcels affected: <strong>{ptDupePopup.comparison.summary.parcels_affected}</strong>
                  </span>
                )}
              </div>
            </DialogHeader>

            <div className="flex-1 overflow-auto border rounded" data-testid="pt-dupe-table">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-slate-100 text-slate-600">
                  <tr>
                    <th className="text-left px-2 py-1">Source</th>
                    <th className="text-left px-2 py-1">Client / Entry</th>
                    <th className="text-left px-2 py-1">Tracking</th>
                    <th className="text-left px-2 py-1">Products</th>
                    <th className="text-left px-2 py-1">Packing Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(ptDupePopup.comparison?.rows || []).map((r, i) => {
                    const isExisting = r.source === 'existing';
                    return (
                      <tr key={i} className={`border-t border-slate-200 ${isExisting ? 'bg-slate-50' : 'bg-blue-50'}`}>
                        <td className="px-2 py-1">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${isExisting ? 'bg-slate-200 text-slate-700' : 'bg-blue-200 text-blue-800'}`}>
                            {isExisting ? 'Existing' : 'New'}
                          </span>
                        </td>
                        <td className="px-2 py-1">{r.customer_name || '—'}</td>
                        <td className="px-2 py-1 font-mono">{r.track_no || '—'}</td>
                        <td className="px-2 py-1">
                          {(r.products || []).map((p, pi) => (
                            <div key={pi}>
                              {p.name} <span className="text-slate-400">×{p.qty}</span>
                            </div>
                          ))}
                          {(!r.products || r.products.length === 0) && <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-2 py-1">{r.packing_date || '—'}</td>
                      </tr>
                    );
                  })}
                  {(ptDupePopup.comparison?.rows || []).length === 0 && (
                    <tr><td colSpan={5} className="p-4 text-center text-slate-500">No rows to compare</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <DialogFooter className="flex flex-wrap gap-2 justify-end pt-2 border-t">
              <Button
                variant="outline" size="sm"
                onClick={() => handlePtDupeConfirm('cancel')}
                disabled={ptDupePopup.busy}
                data-testid="pt-dupe-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() => handlePtDupeConfirm('keep_both')}
                disabled={ptDupePopup.busy}
                data-testid="pt-dupe-keep-both"
              >
                {ptDupePopup.busy ? 'Working…' : 'Keep Both'}
              </Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white"
                size="sm"
                onClick={() => handlePtDupeConfirm('replace')}
                disabled={ptDupePopup.busy}
                data-testid="pt-dupe-replace"
              >
                {ptDupePopup.busy ? 'Working…' : 'Replace Existing'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ============ PDF TO WORD SUB-PANEL ============
  if (subPanel === 'pdf_to_word') {
    const handleP2wDrop = (e) => {
      e.preventDefault();
      setP2wDragActive(false);
      const file = e.dataTransfer?.files?.[0];
      if (file && file.type === 'application/pdf') {
        setP2wFile(file);
        setP2wResult(null);
        setP2wClassification(null);
      } else {
        toast.error("Please drop a PDF file");
      }
    };

    const handleP2wFileSelect = (e) => {
      const file = e.target.files?.[0];
      if (file) {
        setP2wFile(file);
        setP2wResult(null);
        setP2wClassification(null);
      }
    };

    const handleP2wConvert = async () => {
      if (!p2wFile) { toast.error("Please upload a PDF file first"); return; }
      setP2wConverting(true);
      setP2wResult(null);
      setP2wClassification(null);

      try {
        // Stage 1: Analyzing
        setP2wStage("Analyzing PDF...");
        setP2wProgress(15);
        const classifyForm = new FormData();
        classifyForm.append('pdf_file', p2wFile);
        const classifyRes = await axios.post(`${API}/pdf-to-word/classify`, classifyForm, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        });
        setP2wClassification(classifyRes.data);
        setP2wProgress(30);

        // Stage 2: Extracting
        const pdfType = classifyRes.data.type;
        if (pdfType === 'scanned') {
          setP2wStage("Running OCR extraction...");
        } else if (pdfType === 'hybrid') {
          setP2wStage("Extracting text + OCR...");
        } else {
          setP2wStage("Extracting text & layout...");
        }
        setP2wProgress(50);

        // Stage 3: Converting
        await new Promise(r => setTimeout(r, 300));
        setP2wStage(p2wMode === 'ai' ? "AI reconstructing layout..." : "Building Word document...");
        setP2wProgress(70);

        const convertForm = new FormData();
        convertForm.append('pdf_file', p2wFile);
        convertForm.append('mode', p2wMode);
        const convertRes = await axios.post(`${API}/pdf-to-word/convert`, convertForm, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });

        setP2wProgress(95);
        setP2wStage("Complete!");
        setP2wResult(convertRes.data);

        // Add to local history
        setP2wHistory(prev => [{
          filename: convertRes.data.filename,
          info: convertRes.data.info,
          timestamp: new Date().toISOString(),
          file_data: convertRes.data.file_data,
        }, ...prev]);

        setP2wProgress(100);
        toast.success(`Converted successfully: ${convertRes.data.filename}`);
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || "Conversion failed";
        toast.error(msg);
        setP2wStage("");
        setP2wProgress(0);
      } finally {
        setP2wConverting(false);
      }
    };

    const handleP2wDownload = (result) => {
      if (!result?.file_data) return;
      const byteChars = atob(result.file_data);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename || 'converted.docx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    };

    const handleP2wReset = () => {
      setP2wFile(null);
      setP2wResult(null);
      setP2wClassification(null);
      setP2wStage("");
      setP2wProgress(0);
      setP2wConverting(false);
    };

    return (
      <div className="space-y-6" data-testid="pdf-to-word-panel">
        {/* Header */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileText className="w-5 h-5 text-rose-600" />
                  PDF to Word Converter
                </CardTitle>
                <CardDescription>Convert PDFs to editable Word documents with layout preservation</CardDescription>
              </div>
              {p2wFile && (
                <Button variant="outline" size="sm" onClick={handleP2wReset} data-testid="p2w-reset-btn">
                  <RotateCcw className="w-4 h-4 mr-1" /> Reset
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Upload Area */}
            <div
              className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                p2wDragActive ? 'border-rose-400 bg-rose-50' :
                p2wFile ? 'border-green-300 bg-green-50' :
                'border-slate-200 hover:border-rose-300 bg-slate-50'
              }`}
              onDragOver={(e) => { e.preventDefault(); setP2wDragActive(true); }}
              onDragLeave={() => setP2wDragActive(false)}
              onDrop={handleP2wDrop}
              onClick={() => document.getElementById('p2w-file-input')?.click()}
              data-testid="p2w-upload-area"
            >
              <input
                id="p2w-file-input"
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={handleP2wFileSelect}
                data-testid="p2w-file-input"
              />
              {p2wFile ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-green-600" />
                  <div className="text-left">
                    <p className="font-medium text-green-800 truncate max-w-[300px]">{p2wFile.name}</p>
                    <p className="text-xs text-green-600">{(p2wFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <Button variant="ghost" size="sm" className="ml-2" onClick={(e) => { e.stopPropagation(); handleP2wReset(); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div>
                  <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-600">
                    {p2wDragActive ? 'Drop your PDF here' : 'Click or drag & drop a PDF file'}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">Supports text-based, scanned, and hybrid PDFs</p>
                </div>
              )}
            </div>

            {/* Mode Selector */}
            <div className="flex items-center gap-4" data-testid="p2w-mode-selector">
              <Label className="text-sm font-medium text-slate-700 min-w-[100px]">Conversion Mode:</Label>
              <div className="flex gap-2">
                <Button
                  variant={p2wMode === 'fast' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setP2wMode('fast')}
                  data-testid="p2w-mode-fast"
                  className={p2wMode === 'fast' ? 'bg-slate-800 hover:bg-slate-900' : ''}
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Fast
                </Button>
                <Button
                  variant={p2wMode === 'ai' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setP2wMode('ai')}
                  data-testid="p2w-mode-ai"
                  className={p2wMode === 'ai' ? 'bg-rose-600 hover:bg-rose-700' : ''}
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> AI Enhanced
                </Button>
              </div>
              <span className="text-xs text-slate-400">
                {p2wMode === 'fast' ? 'Local processing — fast & accurate for text PDFs' : 'AI-powered OCR correction & layout reconstruction'}
              </span>
            </div>

            {/* Convert Button */}
            <Button
              onClick={handleP2wConvert}
              disabled={!p2wFile || p2wConverting}
              data-testid="p2w-convert-btn"
              className="w-full bg-rose-600 hover:bg-rose-700 text-white h-11"
            >
              {p2wConverting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Converting...
                </>
              ) : (
                <>
                  <FileInput className="w-4 h-4 mr-2" />
                  Convert to Word
                </>
              )}
            </Button>

            {/* Progress / Status */}
            {p2wConverting && (
              <div className="space-y-2" data-testid="p2w-progress">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-700">{p2wStage}</span>
                  <span className="text-slate-500">{p2wProgress}%</span>
                </div>
                <Progress value={p2wProgress} className="h-2" />
              </div>
            )}

            {/* Classification Result */}
            {p2wClassification && !p2wConverting && (
              <div className="bg-slate-50 rounded-lg p-4 border" data-testid="p2w-classification">
                <p className="text-sm font-medium text-slate-700 mb-2">PDF Analysis</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="text-center p-2 bg-white rounded border">
                    <p className="text-xs text-slate-500">Type</p>
                    <Badge variant={p2wClassification.type === 'text' ? 'default' : p2wClassification.type === 'scanned' ? 'destructive' : 'secondary'} className="mt-1">
                      {p2wClassification.type}
                    </Badge>
                  </div>
                  <div className="text-center p-2 bg-white rounded border">
                    <p className="text-xs text-slate-500">Pages</p>
                    <p className="font-semibold text-slate-800 mt-1">{p2wClassification.pages}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded border">
                    <p className="text-xs text-slate-500">Accuracy</p>
                    <p className="font-semibold text-green-700 mt-1">{p2wClassification.estimated_accuracy}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded border">
                    <p className="text-xs text-slate-500">Mode</p>
                    <Badge variant="outline" className="mt-1">{p2wMode === 'ai' ? 'AI Enhanced' : 'Fast'}</Badge>
                  </div>
                </div>
                {p2wClassification.type !== 'text' && p2wClassification.ocr_available === false && (
                  <p className="mt-2 text-xs rounded border border-amber-300 bg-amber-50 text-amber-800 px-2 py-1.5"
                     data-testid="p2w-ocr-unavailable">
                    Scanned PDF needs OCR — not available on this server. Upload a text-based (digital) PDF, or ask an admin to install the tesseract OCR engine.
                  </p>
                )}
              </div>
            )}

            {/* Conversion Result */}
            {p2wResult && !p2wConverting && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-5" data-testid="p2w-result">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-green-800 text-base">Conversion Complete</p>
                    <p className="text-sm text-green-700 mt-1 truncate">{p2wResult.filename}</p>
                    <div className="flex flex-wrap gap-2 mt-2 text-xs text-green-600">
                      <span>Type: {p2wResult.info?.pdf_type}</span>
                      <span className="text-green-300">|</span>
                      <span>Pages: {p2wResult.info?.pages}</span>
                      <span className="text-green-300">|</span>
                      <span>Elements: {p2wResult.info?.elements_extracted}</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleP2wDownload(p2wResult)}
                    data-testid="p2w-download-btn"
                    className="bg-green-700 hover:bg-green-800 text-white"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-1" /> Download .docx
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Conversion History */}
        {p2wHistory.length > 0 && (
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" />
                Recent Conversions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="p2w-history-table">
                  <thead>
                    <tr className="border-b text-left text-slate-500">
                      <th className="py-2 pr-4 font-medium">Filename</th>
                      <th className="py-2 pr-4 font-medium">Type</th>
                      <th className="py-2 pr-4 font-medium">Pages</th>
                      <th className="py-2 pr-4 font-medium">Mode</th>
                      <th className="py-2 pr-4 font-medium">Time</th>
                      <th className="py-2 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p2wHistory.map((entry, idx) => (
                      <tr key={idx} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-2.5 pr-4 font-medium text-slate-800 max-w-[200px] truncate">{entry.filename}</td>
                        <td className="py-2.5 pr-4">
                          <Badge variant="outline" className="text-xs">{entry.info?.pdf_type || '-'}</Badge>
                        </td>
                        <td className="py-2.5 pr-4 text-slate-600">{entry.info?.pages || '-'}</td>
                        <td className="py-2.5 pr-4">
                          <Badge variant={entry.info?.mode === 'ai' ? 'destructive' : 'secondary'} className="text-xs">
                            {entry.info?.mode === 'ai' ? 'AI' : 'Fast'}
                          </Badge>
                        </td>
                        <td className="py-2.5 pr-4 text-slate-500 text-xs">{formatDateTimeDetailed(entry.timestamp)}</td>
                        <td className="py-2.5 text-right">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleP2wDownload(entry)}
                            data-testid={`p2w-history-download-${idx}`}
                            className="h-7 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          >
                            <Download className="w-3.5 h-3.5 mr-1" /> Download
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ============ BULK LABEL GENERATOR SUB-PANEL ============
  if (subPanel === 'bulk_label') {
    const handleBlGenerate = async (invoiceOverride = null, overrides = {}) => {
      // Iter 194 — invoice override from the Completed Invoices bar;
      // backend reuses the invoice's latest Bulk Data file as the label source.

      if (invoiceOverride && !invoiceOverride.invoice_no) invoiceOverride = null;
      // Iter 228 — dialog-supplied company/list override the panel state.
      const effCompany = overrides.companyId !== undefined ? overrides.companyId : blSelectedCompany;
      const effList = overrides.slipFile !== undefined ? overrides.slipFile : blPackingList;
      if (overrides.companyId) setBlSelectedCompany(overrides.companyId);
      if (!invoiceOverride && !blFile) { toast.error("Please upload a Label Excel file"); return; }
      if (!effCompany) { toast.error("Please select a sender company"); return; }
      setBlGenerating(true);
      try {
        const formData = new FormData();
        if (invoiceOverride) formData.append('invoice_no', invoiceOverride.invoice_no);
        else formData.append('label_file', blFile);
        formData.append('sender_company_id', effCompany);
        if (blMailingDate) formData.append('mailing_date', blMailingDate);
        formData.append('layout', blLayout);
        formData.append('labels_per_page', blLabelsPerPage);
        if (effList) formData.append('packing_list', effList);
        formData.append('convert_usd_to_inr', blConvertUsdToInr ? 'true' : 'false');
        formData.append('personal_note', blPersonalNote ? 'true' : 'false');
        if (!invoiceOverride && blInvoiceOverride.trim()) formData.append('invoice_override', blInvoiceOverride.trim());

        const response = await axios.post(
          invoiceOverride
            ? `${API}/bulk-label/generate-from-invoice`
            : `${API}/bulk-label/generate`, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }, timeout: 60000
        });

        const { id: entryId, filename, total_labels, total_pages,
                zero_weight_count, zero_weight_tracks, trimmed_parcels } = response.data;

        // Iter 147 — USA multi-product parcels print only 2 names + "N OTHER PRODUCTS".
        if (trimmed_parcels?.length) {
          toast.info(
            `${trimmed_parcels.length} USA parcel(s) show 2 of N products on the label: ` +
            trimmed_parcels.slice(0, 3).map(p => `${p.name} (2 of ${p.total})`).join(", ") +
            (trimmed_parcels.length > 3 ? "…" : ""),
            { duration: 10000 }
          );
        }

        // Iter 271 — warn when parcels still have no weight so ops can
        // fix them (packing list / bulk data weight editors) before the
        // labels are actually used.
        if (zero_weight_count > 0) {
          toast.warning(
            `${zero_weight_count} parcel(s) have ZERO weight — labels will show 0 g. ` +
            `Fix via Packing List / Bulk Data weight editing.` +
            (zero_weight_tracks?.length ? ` (${zero_weight_tracks.slice(0, 3).join(", ")}${zero_weight_count > 3 ? "…" : ""})` : ""),
            { duration: 10000 }
          );
        }

        // Auto-download
        const dlRes = await axios.get(`${API}/bulk-label/history/${entryId}/download`, {
          headers: getAuthHeader(), responseType: 'blob', timeout: 30000
        });
        const url = window.URL.createObjectURL(new Blob([dlRes.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        toast.success(`Generated ${total_labels} labels on ${total_pages} page(s)!`);
        fetchBlHistory();
      } catch (error) {
        const msg = error.response?.data?.detail || "Failed to generate labels";
        toast.error(msg);
      } finally { setBlGenerating(false); }
    };

    // Preview-only run: same inputs as Generate, but the backend returns
    // the PDF inline (no history row). Opens in a new browser tab so ops
    // can review label content before committing the batch to history.
    const handleBlPreview = async () => {
      if (!blFile) { toast.error("Please upload a Label Excel file"); return; }
      if (!blSelectedCompany) { toast.error("Please select a sender company"); return; }
      setBlPreviewing(true);
      try {
        const formData = new FormData();
        formData.append('label_file', blFile);
        formData.append('sender_company_id', blSelectedCompany);
        if (blMailingDate) formData.append('mailing_date', blMailingDate);
        formData.append('layout', blLayout);
        formData.append('labels_per_page', blLabelsPerPage);
        if (blPackingList) formData.append('packing_list', blPackingList);
        formData.append('save_to_history', 'false');
        formData.append('convert_usd_to_inr', blConvertUsdToInr ? 'true' : 'false');
        formData.append('personal_note', blPersonalNote ? 'true' : 'false');

        const res = await axios.post(`${API}/bulk-label/generate`, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
          responseType: 'blob', timeout: 60000,
        });
        const zw = parseInt(res.headers?.['x-zero-weight-count'] || '0', 10);
        if (zw > 0) {
          toast.warning(`${zw} parcel(s) have ZERO weight — labels will show 0 g.`,
            { id: 'bl-zero-wt', duration: 8000 });
        }
        const trimmed = parseInt(res.headers?.['x-trimmed-count'] || '0', 10);
        if (trimmed > 0) {
          toast.info(`${trimmed} USA parcel(s) show only 2 of N products — first 2 brand names on the label`,
            { id: 'bl-trimmed', duration: 8000 });
        }
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        // Open the blob URL in a fresh tab. It stays alive until the tab is closed.
        const w = window.open(url, '_blank', 'noopener,noreferrer');
        if (!w) toast.error('Preview blocked by pop-up settings — please allow pop-ups');
        else toast.success('Preview opened in new tab — nothing saved to history');
      } catch (error) {
        // axios blob error: response body is a Blob, try to read it as text
        let msg = "Preview failed";
        try {
          const t = await error.response?.data?.text?.();
          if (t) { try { msg = JSON.parse(t).detail || msg; } catch { msg = t; } }
        } catch { /* ignore */ }
        toast.error(msg);
      } finally { setBlPreviewing(false); }
    };

    // Iter 160 — Live inline preview handled via top-level effect (see below).

    const handleBlDownload = async (entry) => {
      try {
        const res = await axios.get(`${API}/bulk-label/history/${entry.id}/download`, {
          headers: getAuthHeader(), responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', entry.filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch { toast.error("Download failed"); }
    };

    const handleBlPrint = async (entry) => {
      try {
        const res = await axios.get(`${API}/bulk-label/history/${entry.id}/download`, {
          headers: getAuthHeader(), responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        // Use iframe for reliable cross-browser printing
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = url;
        document.body.appendChild(iframe);
        iframe.onload = () => {
          try {
            iframe.contentWindow.print();
          } catch {
            // Fallback: open in new tab
            window.open(url, '_blank');
          }
          setTimeout(() => {
            document.body.removeChild(iframe);
            window.URL.revokeObjectURL(url);
          }, 5000);
        };
      } catch { toast.error("Print failed"); }
    };

    const handleBlDelete = async (entry) => {
      if (!window.confirm(`Delete "${entry.filename}"?`)) return;
      try {
        await axios.delete(`${API}/bulk-label/history/${entry.id}`, { headers: getAuthHeader() });
        toast.success("Deleted");
        fetchBlHistory();
      } catch { toast.error("Delete failed"); }
    };

    const handleBlDownloadSource = async (entry) => {
      try {
        const res = await axios.get(`${API}/bulk-label/history/${entry.id}/download-source`, {
          headers: getAuthHeader(), responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', entry.source_file?.name || 'original_file.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`Downloaded: ${entry.source_file?.name || 'original file'}`);
      } catch { toast.error("Original file not available"); }
    };

    const handleBlRegenerate = async (entryId) => {
      setBlRegenLoading(true);
      try {
        const res = await axios.post(`${API}/bulk-label/history/${entryId}/regenerate`, {
          layout: blRegenLayout,
          labels_per_page: parseInt(blRegenLpp)
        }, { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `CN22_Labels_${blRegenLayout}_${blRegenLpp}pp.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`Generated ${blRegenLayout} / ${blRegenLpp} per page`);
        setBlRegenId(null);
        fetchBlHistory();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Regeneration failed");
      } finally { setBlRegenLoading(false); }
    };

    const blSelectedIsHindi = typeof blSelectedCompany === 'string' && blSelectedCompany.endsWith(':hi');
    const blSelectedBaseId = blSelectedIsHindi ? blSelectedCompany.slice(0, -3) : blSelectedCompany;
    const selectedComp = blCompanies.find(c => c.id === blSelectedBaseId || c.name === blSelectedBaseId);

    return (
      <div className="space-y-6" data-testid="bulk-label-panel">
        <GlobalDocSearch />
        <PendingParcelsStrip tool="bulk_label" />
        <CompletedInvoicesBar
          history={blHistory}
          onUse={(inv) => openUseDialog(inv, "bulk_label")}
          busy={blGenerating}
          hint="Uses the invoice's latest Bulk Data file — no label Excel needed"
          tool="bulk_label"
          testId="bulk-label" />
        <UseInvoiceDialog
          open={useDlg?.tool === "bulk_label"}
          invoice={useDlg?.tool === "bulk_label" ? useDlg.inv : null}
          tool="bulk_label"
          companies={blCompanies}
          defaultCompany={(() => { try { return localStorage.getItem("useInvoice:lastCompany:bulk_label") || ""; } catch { return ""; } })()}
          busy={blGenerating}
          onClose={() => setUseDlg(null)}
          onConfirm={async ({ slipFile, companyId }) => {
            const inv = useDlg?.inv;
            setUseDlg(null);
            try { localStorage.setItem("useInvoice:lastCompany:bulk_label", companyId); } catch {}
            setBlPackingList(slipFile);
            if (inv) await handleBlGenerate(inv, { slipFile, companyId });
            window.dispatchEvent(new Event("order-register:invoice-changed"));
          }}
        />
        <BulkLabelReadyTable companies={blCompanies} layout={blLayout} labelsPerPage={blLabelsPerPage}
          mailingDate={blMailingDate} convertUsdToInr={blConvertUsdToInr} personalNote={blPersonalNote} onGenerated={fetchBlHistory} />
        <SelectedLabelsDialog entryId={blSelectId} onClose={() => setBlSelectId(null)} />
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" data-testid="bulk-label-title">
              <Tag className="w-5 h-5 text-violet-600" /> Bulk Label Generator
            </CardTitle>
            <CardDescription>Upload Excel data to generate CN22 Customs Declaration labels in exact reference format</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Company Selection */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="bl-company-section">
              <label className="text-sm font-semibold text-slate-700 mb-2 block">
                Select Sender Company <span className="text-red-500">*</span>
                <span className={`ml-2 text-xs font-normal ${blCompanies.length > 0 ? 'text-green-600' : 'text-red-500'}`}>
                  ({blCompanies.length} companies loaded)
                </span>
              </label>
              {blCompanies.length === 0 ? (
                <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  No companies found. Go to "Manage Companies" to add one.
                </div>
              ) : (
                <Select value={blSelectedCompany} onValueChange={setBlSelectedCompany} data-testid="bl-company-select">
                  <SelectTrigger className="bg-white" data-testid="bl-company-trigger">
                    <SelectValue placeholder="Choose a company..." />
                  </SelectTrigger>
                  <SelectContent>
                    {blCompanies.flatMap(comp => {
                      const baseId = comp.id || comp.name;
                      const items = [
                        <SelectItem key={baseId} value={baseId} data-testid={`bl-company-${comp.name}`}>
                          {comp.name} {comp.gstin ? `(${comp.gstin})` : ''}
                        </SelectItem>
                      ];
                      if (comp.name_hi && comp.name_hi.trim()) {
                        items.push(
                          <SelectItem key={`${baseId}:hi`} value={`${baseId}:hi`} data-testid={`bl-company-${comp.name}-hi`}>
                            {comp.name_hi}  —  {comp.name} (Hindi)
                          </SelectItem>
                        );
                      }
                      return items;
                    })}
                  </SelectContent>
                </Select>
              )}
              {selectedComp && (
                <div className="mt-2 p-2 bg-violet-50 border border-violet-200 rounded-lg text-xs text-violet-800" data-testid="bl-company-preview">
                  {blSelectedIsHindi ? (
                    <>
                      <span className="font-semibold">{selectedComp.name_hi || selectedComp.name}</span>
                      {selectedComp.address_line_1_hi && <span> — {selectedComp.address_line_1_hi}</span>}
                      {selectedComp.pincode_hi && <span>, {selectedComp.pincode_hi}</span>}
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-violet-200 text-violet-900 text-[10px] font-semibold">हिंदी</span>
                    </>
                  ) : (
                    <>
                      <span className="font-semibold">{selectedComp.name}</span>
                      {selectedComp.address_line_1 && <span> — {selectedComp.address_line_1}</span>}
                      {selectedComp.pincode && <span>, {selectedComp.pincode}</span>}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Iter 230 — Label Excel + Packing List sit side-by-side
                (half width each) to keep the panel compact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
            {/* File Upload — Iter 229 compact card */}
            <CompactUploadBox
              file={blFile}
              label="Label Data Excel"
              required
              hint="Bulk Data format (.xlsx) with tracking numbers and receiver addresses"
              icon={<FileSpreadsheet className="w-4 h-4" />}
              accent="violet"
              accept=".xlsx,.xls"
              inputTestId="bl-file-input"
              removeTestId="bl-remove-file"
              onPick={(f) => setBlFile(f)}
              onRemove={() => setBlFile(null)} />

            {/* Optional: Packing List for USA product-name enrichment */}
            <div className={`border-2 border-dashed rounded-lg px-3 py-2.5 transition-all ${
              blPackingList ? 'border-blue-300 bg-blue-50' : 'border-slate-200 hover:border-blue-300 bg-slate-50'
            }`} data-testid="bl-packing-list-section">
              <div className="flex items-center justify-center gap-2 mb-1">
                <FileSpreadsheet className="w-4 h-4 text-blue-600" />
                <h3 className="font-semibold text-sm">Packing List (Order Sheet) <span className="text-slate-500 text-xs font-normal">— optional, USA labels only</span></h3>
              </div>
              <p className="text-xs text-slate-500 mb-2 flex items-center justify-center gap-1.5 flex-wrap">
                <span>Enables real product names & USD→INR value on USA labels. Rate today:</span>
                {blRateEditing ? (
                  <span className="inline-flex items-center gap-1" data-testid="bl-rate-editor">
                    <span>₹</span>
                    <Input
                      type="number" step="0.5" min="0.1" max="1000"
                      value={blRateDraft}
                      onChange={(e) => setBlRateDraft(e.target.value)}
                      className="h-6 w-20 text-xs px-1.5 py-0"
                      autoFocus
                      data-testid="bl-rate-input"
                    />
                    <span>/USD</span>
                    <Input
                      type="text" maxLength={300}
                      placeholder="Reason (optional)"
                      value={blRateReason}
                      onChange={(e) => setBlRateReason(e.target.value)}
                      className="h-6 w-40 text-xs px-1.5 py-0"
                      data-testid="bl-rate-reason"
                    />
                    <Button
                      size="sm" variant="outline"
                      className="h-6 px-2 text-[11px]"
                      disabled={blRateSaving}
                      onClick={async () => {
                        const v = parseFloat(blRateDraft);
                        if (!v || v <= 0 || v > 1000) { toast.error("Enter a rate between 0 and 1000"); return; }
                        setBlRateSaving(true);
                        try {
                          const r = await axios.put(`${API}/settings/usd-inr-rate`,
                            { value: v, reason: blRateReason },
                            { headers: getAuthHeader() });
                          setBlUsdInrRate(r.data.value);
                          setBlRateEditing(false);
                          setBlRateReason("");
                          toast.success(`Rate updated to ₹${r.data.value}/USD`);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Failed to update rate");
                        } finally { setBlRateSaving(false); }
                      }}
                      data-testid="bl-rate-save"
                    >
                      {blRateSaving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      className="h-6 px-2 text-[11px]"
                      disabled={blRateSaving}
                      onClick={() => { setBlRateEditing(false); setBlRateDraft(""); setBlRateReason(""); }}
                      data-testid="bl-rate-cancel"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </span>
                ) : (
                  <>
                    <span className="font-semibold text-slate-700" data-testid="bl-usd-inr-rate">₹{blUsdInrRate}/USD</span>
                    {user?.role === 'admin' && (
                      <>
                        <button
                          type="button"
                          onClick={() => { setBlRateDraft(String(blUsdInrRate)); setBlRateEditing(true); }}
                          className="text-[11px] text-violet-600 hover:text-violet-800 hover:underline font-medium inline-flex items-center gap-0.5"
                          data-testid="bl-rate-edit"
                        >
                          <Edit className="w-3 h-3" /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            setBlRateHistoryOpen(true);
                            setBlRateHistoryLoading(true);
                            try {
                              const r = await axios.get(`${API}/settings/usd-inr-rate/history`, { headers: getAuthHeader() });
                              setBlRateHistory(r.data.entries || []);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Failed to load history");
                            } finally { setBlRateHistoryLoading(false); }
                          }}
                          className="text-[11px] text-slate-500 hover:text-slate-800 hover:underline font-medium inline-flex items-center gap-0.5"
                          data-testid="bl-rate-history-btn"
                        >
                          <Clock className="w-3 h-3" /> History
                        </button>
                      </>
                    )}
                  </>
                )}
              </p>
              {blPackingList ? (
                <div className="space-y-1">
                  <div className="flex items-center justify-center gap-1 text-blue-700 text-sm font-medium">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="truncate max-w-[240px]">{blPackingList.name}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setBlPackingList(null)} data-testid="bl-remove-packing-list">
                    <X className="w-3 h-3 mr-1" /> Remove
                  </Button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <input type="file" accept=".xlsx,.xls" className="hidden" data-testid="bl-packing-list-input"
                    onChange={(e) => { if (e.target.files[0]) setBlPackingList(e.target.files[0]); }} />
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-50">
                    <Upload className="w-3 h-3" /> Upload Order Sheet
                  </span>
                </label>
              )}
            </div>
            </div>{/* /half-width pair */}

            {/* Layout & Labels per Page */}
            <div className="grid grid-cols-2 gap-4" data-testid="bl-layout-section">
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Page Layout</label>
                <Select value={blLayout} onValueChange={setBlLayout}>
                  <SelectTrigger className="bg-white" data-testid="bl-layout-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="horizontal">Horizontal (Landscape)</SelectItem>
                    <SelectItem value="vertical">Vertical (Portrait)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">{blLayout === 'horizontal' ? 'Labels placed side by side' : 'Labels stacked vertically'}</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Labels per Page</label>
                <Select value={blLabelsPerPage} onValueChange={setBlLabelsPerPage}>
                  <SelectTrigger className="bg-white" data-testid="bl-lpp-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Label (Large Parcel)</SelectItem>
                    <SelectItem value="2">2 Labels (Standard)</SelectItem>
                    <SelectItem value="4">4 Labels (Small Parcel)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">{blLabelsPerPage === '1' ? 'Full page per label' : blLabelsPerPage === '2' ? 'Default — matches reference format' : '4 per page for small items'}</p>
              </div>
            </div>

            {/* Optional: Mailing Date */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-slate-700 whitespace-nowrap">Mailing Date (optional):</label>
              <Input
                type="date"
                value={blMailingDate}
                onChange={(e) => setBlMailingDate(e.target.value)}
                className="w-44"
                data-testid="bl-mailing-date"
              />
              {blMailingDate && (
                <Button variant="ghost" size="sm" onClick={() => setBlMailingDate("")}>
                  <X className="w-3 h-3" />
                </Button>
              )}
              <span className="text-xs text-slate-500">Defaults to today if empty</span>
            </div>

            {/* Iter 170 — USD→INR conversion toggle (USA labels). Default OFF
                because packing lists already export INR-denominated values. */}
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-sm font-medium text-slate-700 whitespace-nowrap">USA Value in INR — Convert from USD:</label>
              <Button
                type="button"
                size="sm"
                onClick={() => setBlConvertUsdToInr(v => !v)}
                variant={blConvertUsdToInr ? "default" : "outline"}
                className={blConvertUsdToInr
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "border-slate-300 text-slate-700 hover:bg-slate-50"}
                data-testid="bl-convert-usd-inr-toggle"
                title={blConvertUsdToInr
                  ? `ON — multiplies each USA value by ${blUsdInrRate} (USD→INR)`
                  : "OFF — VALUE column uses the packing-list amount as-is (assumed INR)"}
              >
                {blConvertUsdToInr
                  ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Conversion: ON</>
                  : <><X className="w-3.5 h-3.5 mr-1" /> Conversion: OFF</>}
              </Button>
              <span className="text-xs text-slate-500">
                {blConvertUsdToInr
                  ? `Multiplier: ${blUsdInrRate}× (from settings)`
                  : "Default OFF — packing list already provides INR values"}
              </span>
              {blConvertUsdToInr && (
                <div className="flex items-center gap-1.5 ml-1 px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200"
                     data-testid="bl-rate-preview">
                  <span className="text-xs text-emerald-700 font-medium">Spot-check:</span>
                  <span className="text-xs text-slate-600">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={blRatePreviewUsd}
                    onChange={(e) => setBlRatePreviewUsd(e.target.value)}
                    className="w-20 h-7 text-xs"
                    data-testid="bl-rate-preview-input"
                  />
                  <span className="text-xs text-slate-500">→</span>
                  <span className="text-xs font-semibold text-emerald-800"
                        data-testid="bl-rate-preview-result">
                    ₹{(() => {
                      const usd = parseFloat(blRatePreviewUsd);
                      const rate = parseFloat(blUsdInrRate) || 0;
                      if (!isFinite(usd) || !isFinite(rate)) return "—";
                      const v = usd * rate;
                      return v.toLocaleString('en-IN', { maximumFractionDigits: 2 });
                    })()}
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-sm font-medium text-slate-700 whitespace-nowrap">USA "For Personal Use Only" note:</label>
              <Button type="button" size="sm" onClick={() => setBlPersonalNote(v => !v)}
                variant={blPersonalNote ? "default" : "outline"}
                className={blPersonalNote ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "border-slate-300 text-slate-700 hover:bg-slate-50"}
                title="Tariff paid / Postage paid / For personal use only — printed in the weight box of USA labels (never on Emirates parcels)"
                data-testid="bl-personal-note-toggle">
                {blPersonalNote ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Note: ON</> : <><X className="w-3.5 h-3.5 mr-1" /> Note: OFF</>}
              </Button>
              <span className="text-xs text-slate-500">USA labels only · Emirates parcels always skipped · saved with the batch</span>
            </div>

            {/* Actions */}
            <div className="flex gap-3 items-center flex-wrap">
              <Input value={blInvoiceOverride} onChange={e => setBlInvoiceOverride(e.target.value)}
                     placeholder="Invoice # override (optional)" className="h-9 w-60 text-sm"
                     title="Type an invoice number to force it, in case the slip is wrong"
                     data-testid="bl-invoice-override" />
              <Button
                onClick={handleBlGenerate}
                disabled={!blFile || !blSelectedCompany || blGenerating || blPreviewing}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="generate-bulk-label"
              >
                {blGenerating ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : <><Tag className="w-4 h-4 mr-2" /> Generate Labels</>}
              </Button>
              <Button
                onClick={handleBlPreview}
                disabled={!blFile || !blSelectedCompany || blGenerating || blPreviewing}
                variant="outline"
                className="border-sky-500 text-sky-700 hover:bg-sky-50"
                title="Review the PDF in a new tab without saving it to history"
                data-testid="preview-bulk-label"
              >
                {blPreviewing ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Rendering…</> : <><Eye className="w-4 h-4 mr-2" /> Preview (no save)</>}
              </Button>
              <Button
                onClick={() => setBlLivePreviewOn(v => !v)}
                variant="outline"
                disabled={!blFile || !blSelectedCompany}
                className={blLivePreviewOn
                  ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-50"}
                title="Auto-render an inline preview whenever layout / labels-per-page / packing list changes"
                data-testid="bl-live-preview-toggle"
              >
                {blLivePreviewLoading
                  ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Live…</>
                  : blLivePreviewOn
                    ? <><CheckCircle2 className="w-4 h-4 mr-2" /> Live Preview: On</>
                    : <><Eye className="w-4 h-4 mr-2" /> Live Preview: Off</>}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { setBlFile(null); setBlPackingList(null); setBlSelectedCompany(""); setBlMailingDate(""); setBlLayout("horizontal"); setBlLabelsPerPage("4"); setBlConvertUsdToInr(false); setBlPersonalNote(true); }}
                data-testid="reset-bulk-label"
              >
                <RotateCcw className="w-3 h-3 mr-1" /> Reset
              </Button>
            </div>

            {/* Iter 160 — Inline live preview iframe */}
            {blLivePreviewOn && (
              <div className="border rounded-md bg-slate-50 p-2" data-testid="bl-live-preview">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 mb-1">
                  <Eye className="w-3 h-3" />
                  Inline Preview
                  {blLivePreviewLoading && (
                    <RefreshCw className="w-3 h-3 animate-spin text-emerald-600" />
                  )}
                  {blNoteCount !== null && !blLivePreviewLoading && (
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${blPersonalNote ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}
                      data-testid="bl-personal-note-badge">
                      Personal-use note {blPersonalNote ? `ON · ${blNoteCount} USA label${blNoteCount === 1 ? "" : "s"}` : "OFF"}
                    </span>)}
                  <span className="ml-auto text-slate-500 font-normal">
                    Debounced 700 ms — no save
                  </span>
                </div>
                {blLivePreviewUrl ? (
                  <iframe
                    key={blLivePreviewUrl}
                    src={blLivePreviewUrl}
                    title="Bulk Label live preview"
                    className="w-full h-[520px] rounded border border-slate-200 bg-white"
                    data-testid="bl-live-preview-frame"
                  />
                ) : (
                  <div className="h-[520px] flex items-center justify-center text-xs text-slate-400"
                       data-testid="bl-live-preview-empty">
                    Upload a label file and pick a company — the preview will render here automatically.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* USD→INR rate history dialog (admin-only) */}
        <Dialog open={blRateHistoryOpen} onOpenChange={setBlRateHistoryOpen}>
          <DialogContent className="max-w-2xl" data-testid="bl-rate-history-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Clock className="w-4 h-4" /> USD → INR Rate History
              </DialogTitle>
              <DialogDescription>
                Every change to the label conversion rate. Newest first.
              </DialogDescription>
            </DialogHeader>
            {blRateHistoryLoading ? (
              <div className="py-8 text-center text-sm text-slate-500">
                <RefreshCw className="w-4 h-4 animate-spin inline mr-2" /> Loading history…
              </div>
            ) : blRateHistory.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500" data-testid="bl-rate-history-empty">
                No changes recorded yet.
              </div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto" data-testid="bl-rate-history-list">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-50 border-b">
                    <tr className="text-left text-slate-500">
                      <th className="py-2 pl-3 pr-2 font-medium">When (UTC)</th>
                      <th className="py-2 px-2 font-medium">Who</th>
                      <th className="py-2 px-2 font-medium">Change</th>
                      <th className="py-2 pl-2 pr-3 font-medium">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blRateHistory.map((h) => {
                      const when = (h.changed_at || '').replace('T', ' ').replace(/\..*$/, '');
                      const dir = h.value > h.previous_value ? '↑' : (h.value < h.previous_value ? '↓' : '=');
                      const dirClass = h.value > h.previous_value ? 'text-red-600' : (h.value < h.previous_value ? 'text-emerald-600' : 'text-slate-500');
                      return (
                        <tr key={h.id} className="border-b hover:bg-slate-50" data-testid={`bl-rate-history-row-${h.id}`}>
                          <td className="py-2 pl-3 pr-2 whitespace-nowrap font-mono text-[11px]">{when}</td>
                          <td className="py-2 px-2 whitespace-nowrap">{h.changed_by_name || h.changed_by_email || '—'}</td>
                          <td className="py-2 px-2 whitespace-nowrap">
                            <span className="text-slate-500">₹{h.previous_value}</span>
                            <span className={`mx-1 font-semibold ${dirClass}`}>{dir}</span>
                            <span className="font-semibold text-slate-800">₹{h.value}</span>
                            <span className="text-slate-500">/USD</span>
                          </td>
                          <td className="py-2 pl-2 pr-3 text-slate-600">{h.reason || <span className="text-slate-400 italic">—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" size="sm" onClick={() => setBlRateHistoryOpen(false)} data-testid="bl-rate-history-close">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* History */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-violet-500" /> Generation History
            </CardTitle>
            <div className="relative w-60 mt-2">
              <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-slate-400" />
              <Input value={blHistQ} onChange={(e) => setBlHistQ(e.target.value)}
                     placeholder="Search invoice, client, code, tracking…"
                     className="h-7 pl-7 text-xs" data-testid="bl-history-search" />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2"><OriginFilter value={originOf("bulk_label")} onChange={setOriginOf("bulk_label")} testId="bulk-label" /><WeightSortToggle mode={wtModeBl} setMode={setWtModeBl} st={wtSt} testId="bulk-label" /></div>
          </CardHeader>
          <CardContent>
            {blHistoryLoading ? (
              <div className="flex justify-center p-6">
                <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : blHistory.length === 0 ? (
              <p className="text-center text-slate-500 text-sm py-6">No labels generated yet</p>
            ) : (
              <div className="overflow-x-auto">
                <div className="mb-2 flex flex-wrap items-center gap-2"><SupersededToggle on={showSupBl} toggle={toggleSupBl} count={blHistory.filter(h => h.superseded_by).length} testId="bulk-label" /><DocPrintBar keys={greenKeys(blRows.filter(e2 => !e2.superseded_by && docSearchMatch(e2, blHistQ) && originMatch(directCreated, "bulk_label", e2, originOf("bulk_label"))))} sel={blSel.sel} setSel={blSel.setSel} label="Select all weighed" testid="wt-print-bl" /></div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-slate-500 text-xs">
                      <th className="pb-2 px-2 w-8" title="Tick fully weighed invoices to print"></th>
                      <th className="pb-2 pr-3">Invoice No</th>
                      <th className="pb-2 pr-3">Filename</th>
                      <th className="pb-2 pr-3">Company</th>
                      <th className="pb-2 pr-3">Labels</th>
                      <th className="pb-2 pr-3">Layout</th>
                      <th className="pb-2 pr-3">Date Generated</th>
                      <th className="pb-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blLatest.apply(blRows.filter(e2 => (showSupBl || !e2.superseded_by) && docSearchMatch(e2, blHistQ) && originMatch(directCreated, "bulk_label", e2, originOf("bulk_label")))).map(entry => { const isDirect = isDirectEntry(directCreated, "bulk_label", entry); return (
                      <tr key={entry.id} className={`border-b last:border-0 ${isDirect ? DIRECT_ROW_CLS : "hover:bg-slate-50"}`} data-testid={`bl-history-row-${entry.id}`}>
                        <td className="py-2 px-2 text-center w-8">{isGreen(entry) ? <SelectBox k={invKeyOf(entry)} sel={blSel.sel} toggle={blSel.toggle} testid={`wt-print-select-bl-${entry.id}`} /> : null}</td>
                        <td className="py-2 pr-3 pl-2 text-xs font-semibold text-indigo-700" data-testid={`bl-invoice-${entry.id}`}>
                          {entry.matched_invoice_no || entry.invoice_no || '-'}{blLatest.chip(entry, "bl")}<WeightChip s={wtSt[wkey(entry)]} testId={`wt-chip-bl-${entry.id}`} />{isDirect && <DirectBadge testId={`direct-badge-bl-${entry.id}`} />}
                        </td>
                        <td className="py-2 pr-3 font-medium text-xs truncate max-w-[180px]" data-testid={`bl-history-file-${entry.id}`}>
                          <span className={entry.superseded_by ? SUPERSEDED_ROW_CLS : ""}>{entry.filename}</span><CorrectedTag entry={entry} testId="bl" />
                        </td>
                        <td className="py-2 pr-3 text-xs">{entry.company_name}</td>
                        <td className="py-2 pr-3 text-xs">
                          {entry.total_labels}
                          <TrimmedProductsBadge parcels={entry.trimmed_parcels} testId={`bl-trimmed-${entry.id}`} />
                          {entry.personal_note === false && (
                            <span className="ml-1 inline-flex items-center rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 whitespace-nowrap"
                              title="USA 'For Personal Use Only' note was switched OFF for this batch" data-testid={`bl-note-off-${entry.id}`}>Note OFF</span>)}
                        </td>
                        <td className="py-2 pr-3 text-xs capitalize">{entry.layout || '-'} / {entry.labels_per_page || '-'}pp</td>
                        <td className="py-2 pr-3 text-xs text-slate-500">
                          {entry.created_at ? new Date(entry.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                        <td className="py-2 text-right">
                          <div className="flex justify-end gap-1 items-center">
                            <div className="relative">
                              <Button variant="ghost" size="sm" onClick={() => setBlRegenId(blRegenId === entry.id ? null : entry.id)} className="h-7 w-7 p-0 text-emerald-600" title="Regenerate with different size" data-testid={`bl-regen-${entry.id}`}>
                                <RefreshCw className="w-3.5 h-3.5" />
                              </Button>
                              {blRegenId === entry.id && (
                                <div className="absolute right-0 top-8 z-50 bg-white border border-slate-200 rounded-lg shadow-xl p-3 w-56" data-testid="bl-regen-dropdown">
                                  <p className="text-xs font-semibold text-slate-700 mb-2">Regenerate Labels</p>
                                  <div className="space-y-2">
                                    <div>
                                      <label className="text-[10px] text-slate-500 font-medium">Layout</label>
                                      <select value={blRegenLayout} onChange={e => setBlRegenLayout(e.target.value)} className="w-full text-xs border border-slate-200 rounded px-2 py-1 mt-0.5" data-testid="bl-regen-layout">
                                        <option value="horizontal">Horizontal</option>
                                        <option value="vertical">Vertical</option>
                                      </select>
                                    </div>
                                    <div>
                                      <label className="text-[10px] text-slate-500 font-medium">Labels / Page</label>
                                      <select value={blRegenLpp} onChange={e => setBlRegenLpp(e.target.value)} className="w-full text-xs border border-slate-200 rounded px-2 py-1 mt-0.5" data-testid="bl-regen-lpp">
                                        <option value="1">1 per page</option>
                                        <option value="2">2 per page</option>
                                        <option value="4">4 per page</option>
                                      </select>
                                    </div>
                                    <Button size="sm" onClick={() => handleBlRegenerate(entry.id)} disabled={blRegenLoading} className="w-full h-7 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid="bl-regen-go">
                                      {blRegenLoading ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Printer className="w-3 h-3 mr-1" />}
                                      {blRegenLoading ? 'Generating...' : 'Generate & Download'}
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => handleBlDownload(entry)} className="h-7 w-7 p-0 text-violet-600" title="Download PDF" data-testid={`bl-download-${entry.id}`}>
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const tok = localStorage.getItem('token') || '';
                                const url = `${API}/bulk-label/history/${entry.id}/preview?token=${encodeURIComponent(tok)}`;
                                window.open(url, '_blank', 'noopener,noreferrer');
                              }}
                              className="h-7 w-7 p-0 text-sky-600"
                              title="Preview PDF in a new tab"
                              data-testid={`bl-preview-${entry.id}`}
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(`/bulk-label-excel?entry=${entry.id}`, '_blank', 'noopener')}
                              className="h-7 w-7 p-0 text-emerald-600"
                              title="View Excel in new tab (double-click cells to edit)"
                              data-testid={`bl-view-excel-${entry.id}`}
                            >
                              <FileSpreadsheet className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleBlDownloadSource(entry)} className="h-7 w-7 p-0 text-amber-600" title="Download Original File" data-testid={`bl-download-source-${entry.id}`}>
                              <FileInput className="w-3.5 h-3.5" />
                            </Button>
                            <SelectLabelsButton onClick={() => setBlSelectId(entry.id)} testId={`bl-select-${entry.id}`} />
                            <Button variant="ghost" size="sm" onClick={() => handleBlPrint(entry)} className="h-7 w-7 p-0 text-blue-600" title="Print" data-testid={`bl-print-${entry.id}`}>
                              <Printer className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleBlDelete(entry)} className="h-7 w-7 p-0 text-red-500" title="Delete" data-testid={`bl-delete-${entry.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <DocAcksBanner />
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                {config.icon}
                {config.title}
              </CardTitle>
              <CardDescription>{config.description}</CardDescription>
            </div>
            <Button onClick={() => setUploadDialogOpen(true)} className="bg-purple-600 hover:bg-purple-700">
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="documents" className="flex items-center gap-2">
                <FolderOpen className="w-4 h-4" />
                Documents ({filteredDocuments.length})
              </TabsTrigger>
              {subPanel === 'pdf_edits' && (
                <TabsTrigger value="library" className="flex items-center gap-2">
                  <Library className="w-4 h-4" />
                  Library ({libraryDocs.length})
                </TabsTrigger>
              )}
              <TabsTrigger value="recycle-bin" className="flex items-center gap-2">
                <Archive className="w-4 h-4" />
                Recycle Bin ({recycleBin.filter(d => subPanel === 'excel_transpose' ? d.file_type === 'excel' : d.file_type === 'pdf').length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="documents">
              {/* Search and Filters */}
              <div className="mb-4 space-y-3">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by file name or uploader..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Button
                    variant={showFilters ? "default" : "outline"}
                    onClick={() => setShowFilters(!showFilters)}
                    className={showFilters ? "bg-purple-600 hover:bg-purple-700" : ""}
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    Filters
                    {hasActiveFilters && <Badge className="ml-2 bg-purple-100 text-purple-700">Active</Badge>}
                  </Button>
                  {hasActiveFilters && (
                    <Button variant="ghost" onClick={resetFilters}>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Reset
                    </Button>
                  )}
                  <Button variant="outline" onClick={fetchData}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>

                {showFilters && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">File Type</Label>
                      <Select value={filters.file_type} onValueChange={(val) => setFilters({...filters, file_type: val})}>
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="excel">Excel</SelectItem>
                          <SelectItem value="pdf">PDF</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Conversion</Label>
                      <Select value={filters.conversion_type} onValueChange={(val) => setFilters({...filters, conversion_type: val})}>
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Conversions</SelectItem>
                          <SelectItem value="transpose">CARE EXIM</SelectItem>
                          <SelectItem value="pdf_to_excel">PDF → Excel</SelectItem>
                          <SelectItem value="pdf_edit">PDF Edit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Status</Label>
                      <Select value={filters.status} onValueChange={(val) => setFilters({...filters, status: val})}>
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="ready">Ready</SelectItem>
                          <SelectItem value="processing">Processing</SelectItem>
                          <SelectItem value="failed">Failed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Uploaded By</Label>
                      <Select value={filters.uploaded_by} onValueChange={(val) => setFilters({...filters, uploaded_by: val})}>
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Users</SelectItem>
                          {users.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {hasActiveFilters && (
                  <div className="text-sm text-slate-500">
                    Showing {filteredDocuments.length} of {documents.length} documents
                  </div>
                )}
              </div>

              {/* Documents Table */}
              {filteredDocuments.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">{documents.length === 0 ? "No documents yet" : "No documents match your filters"}</p>
                  <p className="text-sm">{documents.length === 0 ? "Upload your first document to get started" : "Try adjusting your filters"}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">#</TableHead>
                        <TableHead>File Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Conversion</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Upload Date</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead>Uploaded By</TableHead>
                        <TableHead className="text-center">Original</TableHead>
                        <TableHead className="text-center">Converted</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredDocuments.map((doc, index) => (
                        <TableRow key={doc.id}>
                          <TableCell className="text-slate-500">{index + 1}</TableCell>
                          <TableCell className="font-medium max-w-[260px]" title={doc.file_name}>
                            <div className="flex items-center gap-2 min-w-0">
                              {subPanel === 'pdf_edits' && doc.file_type === 'pdf' && doc.status === 'ready' && isImageSourced(doc) && (
                                <DocThumb docId={doc.id} alt={doc.file_name} />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="truncate">{doc.file_name}</div>
                                {subPanel === 'pdf_edits' && editorPrefs[doc.id] && (
                                  <span
                                    className={`mt-0.5 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                                      editorPrefs[doc.id] === 'text'
                                        ? 'bg-emerald-100 text-emerald-700'
                                        : 'bg-blue-100 text-blue-700'
                                    }`}
                                    data-testid={`editor-pref-badge-${doc.id}`}
                                    title="This document will open in your preferred editor. Use the row menu to reset."
                                  >
                                    {editorPrefs[doc.id] === 'text' ? '📝 Text Editor' : '🖼️ Image Editor'} preferred
                                  </span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{getTypeBadge(doc.file_type)}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{getConversionLabel(doc.conversion_type)}</Badge>
                          </TableCell>
                          <TableCell>{getStatusBadge(doc.status)}</TableCell>
                          <TableCell>{formatDateDDMMYYYY(doc.created_at)}</TableCell>
                          <TableCell>{formatFileSize(doc.file_size)}</TableCell>
                          <TableCell>{doc.uploaded_by_name}</TableCell>
                          <TableCell className="text-center">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDownload(doc.id, 'original')}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </TableCell>
                          <TableCell className="text-center">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDownload(doc.id, 'converted')}
                              disabled={doc.status !== 'ready'}
                              className={doc.status === 'ready' ? 'text-green-600 hover:text-green-700' : ''}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {/* View on Screen option for all converted documents */}
                                {doc.status === 'ready' && (
                                  <>
                                    <DropdownMenuItem onClick={() => openPreview(doc)} data-testid={`view-${doc.id}`}>
                                      <Eye className="w-4 h-4 mr-2" />
                                      View on Screen
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                {doc.file_type === 'pdf' && (
                                  <>
                                    <DropdownMenuItem onClick={() => openSmartEditor(doc)} data-testid={`smart-edit-${doc.id}`} className="font-semibold text-indigo-700">
                                      <Pencil className="w-4 h-4 mr-2" />
                                      Edit (Smart — auto-detects scan vs vector)
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openCanvasEditor(doc)} data-testid={`canvas-edit-${doc.id}`}>
                                      <Square className="w-4 h-4 mr-2" />
                                      Image Edit (for scans / photos)
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openPdfEditor(doc)}>
                                      <Type className="w-4 h-4 mr-2" />
                                      Text Edit (for vector PDFs — replace text inline)
                                    </DropdownMenuItem>
                                    {editorPrefs[doc.id] && (
                                      <DropdownMenuItem
                                        onClick={() => clearEditorPref(doc.id)}
                                        data-testid={`reset-editor-pref-${doc.id}`}
                                        className="text-amber-700"
                                      >
                                        <RotateCcw className="w-4 h-4 mr-2" />
                                        Reset preferred editor (auto-detect)
                                      </DropdownMenuItem>
                                    )}
                                    {doc.conversion_type === 'pdf_edit' && (
                                      <DropdownMenuItem onClick={() => openPdfOperations(doc)}>
                                        <Edit className="w-4 h-4 mr-2" />
                                        PDF Operations
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                <DropdownMenuItem onClick={() => handleSoftDelete(doc.id)} className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Move to Recycle Bin
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="library">
              {/* Library Search */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search library by name or tags..."
                    value={librarySearch}
                    onChange={(e) => setLibrarySearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {libraryDocs.filter(doc => 
                !librarySearch || 
                (doc.library_name || doc.file_name || '').toLowerCase().includes(librarySearch.toLowerCase()) ||
                (doc.library_tags || []).some(tag => tag.toLowerCase().includes(librarySearch.toLowerCase()))
              ).length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Document Library is empty</p>
                  <p className="text-sm">Save edited documents to the library from the PDF Editor</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {libraryDocs.filter(doc => 
                    !librarySearch || 
                    (doc.library_name || doc.file_name || '').toLowerCase().includes(librarySearch.toLowerCase()) ||
                    (doc.library_tags || []).some(tag => tag.toLowerCase().includes(librarySearch.toLowerCase()))
                  ).map((doc) => (
                    <Card key={doc.id} className="hover:shadow-md transition-shadow border-l-4 border-l-purple-500">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            {doc.file_type === 'pdf' ? (
                              <FileText className="w-6 h-6 text-purple-600" />
                            ) : (
                              <FileSpreadsheet className="w-6 h-6 text-green-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium truncate" title={doc.library_name || doc.file_name}>
                              {doc.library_name || doc.file_name}
                            </h4>
                            {/* File metadata: Type, Size, Date */}
                            <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500">
                              <Badge variant="outline" className={`text-xs ${doc.file_type === 'pdf' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-green-50 text-green-600 border-green-200'}`}>
                                {doc.file_type?.toUpperCase() || 'FILE'}
                              </Badge>
                              <span className="flex items-center gap-1">
                                <span className="font-medium">{formatFileSize(doc.file_size || doc.size)}</span>
                              </span>
                            </div>
                            {/* Saved date/time - prominent display */}
                            <div className="flex items-center gap-1 mt-2 text-xs text-slate-600 bg-slate-50 px-2 py-1 rounded">
                              <Clock className="w-3 h-3" />
                              <span>Saved: </span>
                              <span className="font-medium">
                                {doc.library_saved_at || doc.updated_at 
                                  ? formatDateTimeDetailed(doc.library_saved_at || doc.updated_at)
                                  : formatDateTimeDetailed(doc.created_at)}
                              </span>
                            </div>
                            {doc.library_tags && doc.library_tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {doc.library_tags.map((tag, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    <Tag className="w-3 h-3 mr-1" />{tag}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                          {doc.file_type === 'pdf' && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="flex-1 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                              onClick={() => openSmartEditor(doc)}
                              data-testid={`library-edit-${doc.id}`}
                              title="Auto-detects scan vs vector and opens the right editor"
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Smart Edit
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDownload(doc.id, 'converted')}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600"
                            onClick={() => handleRemoveFromLibrary(doc.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="recycle-bin">
              {recycleBin.filter(d => subPanel === 'excel_transpose' ? d.file_type === 'excel' : d.file_type === 'pdf').length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Archive className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Recycle bin is empty</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">#</TableHead>
                        <TableHead>File Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Deleted Date</TableHead>
                        <TableHead>Uploaded By</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recycleBin.filter(d => subPanel === 'excel_transpose' ? d.file_type === 'excel' : d.file_type === 'pdf').map((doc, index) => (
                        <TableRow key={doc.id} className="bg-slate-50">
                          <TableCell className="text-slate-500">{index + 1}</TableCell>
                          <TableCell className="font-medium">{doc.file_name}</TableCell>
                          <TableCell>{getTypeBadge(doc.file_type)}</TableCell>
                          <TableCell>{doc.deleted_at ? formatDateDDMMYYYY(doc.deleted_at) : '-'}</TableCell>
                          <TableCell>{doc.uploaded_by_name}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleRestore(doc.id)}
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Restore
                              </Button>
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => handlePermanentDelete(doc.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete Forever
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Upload a file for conversion or editing
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Drag & Drop Zone */}
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive 
                  ? 'border-purple-500 bg-purple-50' 
                  : 'border-slate-300 hover:border-purple-400'
              }`}
            >
              <Upload className="w-10 h-10 mx-auto mb-3 text-slate-400" />
              <p className="text-sm text-slate-600 mb-2">
                Drag and drop your file here, or
              </p>
              <label className="cursor-pointer">
                <span className="text-purple-600 hover:text-purple-700 font-medium">browse files</span>
                <input
                  type="file"
                  className="hidden"
                  accept={config.uploadAccept || ".xlsx,.xls,.pdf"}
                  onChange={handleFileSelect}
                />
              </label>
              <p className="text-xs text-slate-400 mt-2">
                Supported: {config.uploadHint || "Excel (.xlsx, .xls), PDF (.pdf)"}
              </p>
            </div>

            {uploadFile && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                {uploadFile.name.toLowerCase().endsWith('.pdf') ? (
                  <FileText className="w-8 h-8 text-orange-500" />
                ) : /\.(jpg|jpeg|png|webp|bmp|tif|tiff)$/i.test(uploadFile.name) ? (
                  <FileImage className="w-8 h-8 text-purple-500" />
                ) : (
                  <FileSpreadsheet className="w-8 h-8 text-emerald-500" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{uploadFile.name}</p>
                  <p className="text-xs text-slate-500">{formatFileSize(uploadFile.size)}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setUploadFile(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}

            <div>
              <Label>Conversion Type *</Label>
              <Select value={conversionType} onValueChange={setConversionType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select conversion type" />
                </SelectTrigger>
                <SelectContent>
                  {subPanel === 'excel_transpose' && (
                    <SelectItem value="transpose">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="w-4 h-4" />
                        Convert to CARE EXIM Format
                      </div>
                    </SelectItem>
                  )}
                  {subPanel === 'pdf_edits' && (
                    <>
                      <SelectItem value="pdf_to_excel">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          PDF to Excel
                        </div>
                      </SelectItem>
                      <SelectItem value="pdf_edit">
                        <div className="flex items-center gap-2">
                          <Edit className="w-4 h-4" />
                          PDF Edit (Advanced)
                        </div>
                      </SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {uploading && (
              <div className="space-y-2">
                <Progress value={uploadProgress} />
                <p className="text-xs text-center text-slate-500">{uploadProgress}% uploaded</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={!uploadFile || !conversionType || uploading} className="bg-purple-600 hover:bg-purple-700">
              {uploading ? "Uploading..." : "Upload & Convert"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Operations Dialog */}
      <Dialog open={pdfOperationDialogOpen} onOpenChange={setPdfOperationDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>PDF Operations</DialogTitle>
            <DialogDescription>
              {selectedDocument?.file_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Operation</Label>
              <Select value={pdfOperation} onValueChange={setPdfOperation}>
                <SelectTrigger>
                  <SelectValue placeholder="Select operation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rotate">Rotate Pages</SelectItem>
                  <SelectItem value="split">Split / Extract Pages</SelectItem>
                  <SelectItem value="merge">Merge PDFs</SelectItem>
                  <SelectItem value="add_watermark">Add Watermark</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {pdfOperation === 'rotate' && (
              <>
                <div>
                  <Label>Pages (e.g., 1,2,3 or 1-5 or leave empty for all)</Label>
                  <Input
                    value={pdfPages}
                    onChange={(e) => setPdfPages(e.target.value)}
                    placeholder="Leave empty to rotate all pages"
                  />
                </div>
                <div>
                  <Label>Rotation Angle</Label>
                  <Select value={pdfRotation} onValueChange={setPdfRotation}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="90">90° Clockwise</SelectItem>
                      <SelectItem value="180">180°</SelectItem>
                      <SelectItem value="270">90° Counter-clockwise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {pdfOperation === 'split' && (
              <div>
                <Label>Pages to Extract (e.g., 1,2,3 or 1-5) *</Label>
                <Input
                  value={pdfPages}
                  onChange={(e) => setPdfPages(e.target.value)}
                  placeholder="1,3,5 or 1-10"
                  required
                />
              </div>
            )}

            {pdfOperation === 'merge' && (
              <div>
                <Label>Additional PDFs to Merge</Label>
                <Input
                  type="file"
                  accept=".pdf"
                  multiple
                  onChange={(e) => setMergeFiles(Array.from(e.target.files || []))}
                />
                {mergeFiles.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    {mergeFiles.length} file(s) selected
                  </p>
                )}
              </div>
            )}

            {pdfOperation === 'add_watermark' && (
              <div>
                <Label>Watermark Text *</Label>
                <Input
                  value={watermarkText}
                  onChange={(e) => setWatermarkText(e.target.value)}
                  placeholder="Enter watermark text"
                  required
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setPdfOperationDialogOpen(false); resetPdfForm(); }}>
              Cancel
            </Button>
            <Button onClick={handlePdfOperation} disabled={!pdfOperation} className="bg-purple-600 hover:bg-purple-700">
              Apply Operation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Editor */}
      {pdfEditorOpen && editingDocument && (
        <PDFEditor
          document={editingDocument}
          onClose={handlePdfEditorClose}
          onSave={handlePdfEditorSave}
          onSwitchToCanvasEditor={() => {
            // Remember the user's choice for this document so next open goes straight there
            if (editingDocument?.id) setEditorPref(editingDocument.id, 'image');
            setPdfEditorOpen(false);
            setCanvasEditorOpen(true);
          }}
        />
      )}

      {/* Canvas PDF Editor */}
      {canvasEditorOpen && editingDocument && (
        <CanvasPDFEditor
          document={editingDocument}
          onClose={handleCanvasEditorClose}
          onSave={handleCanvasEditorSave}
          onSwitchToTextEditor={() => {
            if (editingDocument?.id) setEditorPref(editingDocument.id, 'text');
            setCanvasEditorOpen(false);
            setPdfEditorOpen(true);
          }}
        />
      )}

      {/* Document Preview */}
      {previewOpen && previewDocument && (
        <DocumentPreview
          document={previewDocument}
          onClose={handlePreviewClose}
        />
      )}
    </div>
  );
};

export default DocumentsPanel;
